/**
 * A run: a loop that spends a budget on moves.
 *
 * What it is NOT, and this is the design rather than a caveat: it is not a search
 * for an answer. There is no stopping condition the model's own RATINGS can
 * satisfy — no threshold on novelty, no "insight found", no score that ends it.
 * A run ends when its budget is spent, when the list of ends it will work runs
 * out, when the passes stop coming back, or when you stop it. Everything it says
 * afterwards is a count of what it did.
 *
 * The one early exit is `until`, and it sharpens the rule rather than bending it:
 * a condition the USER set, counted over the grounding record — source-check
 * readings whose citations were validated against what the search returned, panel
 * splits counted mechanically across three blind seats, objections raised by a
 * model from another company. What can end a run early is evidence, never the
 * model's enthusiasm for its own work — and it can only ever end one SOONER,
 * because every ceiling above it still binds.
 *
 * Three structural decisions worth knowing before editing:
 *
 *  - **It calls the existing verbs, one at a time, awaited, and releases `busy`
 *    between passes.** Holding the lock for nine minutes would disable every
 *    keybinding, opening another exploration, and the busy→idle transition the
 *    autosave uses as its forced flush. The gap between passes is not a cost, it
 *    is the steering surface: it is where "Not there" and Stop take effect.
 *    Undo is the one door held anyway, by its own guard in `core/store.ts`
 *    rather than by this lock: this loop's counters and closed sets do not
 *    rewind with the graph, so a step back under a live run would leave it
 *    working a document that no longer matches.
 *  - **The loop owns its mutable state.** Maps of Sets never enter the store; what
 *    the store holds is a flat projection the pod renders.
 *  - **It never indexes `session.ops`.** Four paths out of `runOp` return before
 *    `beginOp` — the busy guard being the live one, since a user pressing a verb
 *    key in a gap takes the lock — so the op id arrives through `onStart`, which
 *    either fires or does not. When it does not, `unstarted` decides whether
 *    asking again can go any differently.
 */

import type {
  Audit,
  DeriveOp,
  LogicalOpKind,
  ModelStamp,
  ReasonNode,
  Run,
  RunBudget,
  RunMove,
  ReasonedEndingCounts,
  RunStop,
  Session,
  SourceReading,
  UntilCondition,
  BridgeRung,
  Programme,
  ProgrammeStep,
  RunVerb,
  MoveReason,
} from '../core/types'
import { auditableBy } from '../core/types'
import { STOP_RULE, bridgeStanding } from '../core/rung'
import {
  READING_LABEL,
  RUN_STOP_HEAD,
  checksSkippedLine,
  checksUnreadLine,
  failedPassesFor,
  modelPassesFor,
  verificationLive,
} from '../core/types'
import { ModelError, type WaitCause } from './providers/contract'
import { pauseCause, unreachableWhy } from '../core/failure'
import { reasonedEndingOf } from '../core/endings'
import { askJudge, JUDGE_QUESTIONS_MAX } from './judge'
import { checkability, checkabilityQuestions } from './judgeQuestions'
import { foldText, textHash } from '../core/similarity'
import { PANEL_SEATS, panelSplit } from '../core/panel'
import {
  DEFAULT_EXECUTION,
  checkCanRead,
  entryFor,
  providerFor,
  resolveFor,
  resolveJudge,
  runAuditor,
  useStore,
  type RouteView,
  type RunDetail,
  type RunExecution,
  type RunLive,
  type RunMode,
  type RunTune,
  type RoutingState,
} from '../core/store'
import {
  CACHE_WRITE_CLASS,
  estimateSpendUsd,
  estimateUsd,
  fmtUsd,
  judgeStampFor,
  resolveEffort,
  stampOf,
  type Effort,
  type ModelEntry,
  type Role,
  type ModelPrice,
  type CacheWriteClass,
  type ProviderId,
  type ResolvedJudge,
  type ResolvedModel,
} from '../core/models'
import { drawnFigure, type DrawClass } from '../core/passDraw'
import { resolvePassEffort, type PassClass } from './effort'
import {
  VOICE_FRAMING_TOKENS,
  VOICE_INPUT_TOKENS,
  VOICE_MAX_TOKENS,
  voiceOn,
  type VoiceShape,
} from '../core/voice'
import { uid } from '../core/id'
import { beginAgendaAttempt, patchAgendaAttempt, pickAgendaStep } from '../core/agenda'
import type { AgendaAttempt, AgendaStep } from '../core/agenda'
import {
  DEFAULT_EVIDENCE_PROFILE,
  evidenceProfileStartIssue,
  normalizeEvidenceProfile,
  type EpistemicCompilation,
  type EvidenceRuleProfile,
} from '../core/epistemic'
import { compileSessionResearch, coverageTracesByOpId } from '../core/researchProjection'
import { onRenamed } from '../core/rename'
import { track } from '../core/analytics'
import { loadSession, writeDoc, type StoredSession } from '../core/persist'
import { searchlessCheckSentence } from '../core/corpus'
import {
  abortInFlight,
  auditorFor,
  AUDIT_MAX_CLAIMS,
  auditTargets,
  runAudit,
  runConjecture,
  runOp,
  runPanel,
  runPortfolio,
  runPriorArt,
  runVerify,
  scanForBridges,
  scanRequestText,
  SCAN_MAX_TOKENS,
  SEARCHES_PER_CHECK,
  SEARCHES_PER_PRIOR_ART,
  SEARCHES_PER_SWEEP,
  LINK_CONJUNCTION_BUDGET,
  LINK_GATHER_BUDGET,
  type PortfolioResult,
} from './reason'
import {
  auditCandidate,
  branchCap,
  checkableCandidates,
  chooseMove,
  conjectureCandidate,
  countOpen,
  freshRunState,
  nextRestingCheck,
  panelCandidate,
  planPortfolio,
  underClosed,
  underFocus,
  verifyCandidates,
  type RunState,
  conesOf,
} from './runPolicy'
import type { Move } from './runPolicy'

/**
 * Output tokens one move is assumed to draw.
 *
 * The ceiling is this times the move count, so a run whose passes draw more than
 * expected stops early rather than spending past what the button said it would.
 * Conservative in the safe direction: too low ends a run before its moves are
 * spent, which is visible and says so; too high is a bill nobody agreed to.
 */
export const TOKENS_PER_MOVE = 12_000

/**
 * The most nodes one run may add.
 *
 * A bound on the run's own contribution, not on the graph. It was the size the
 * graph reached before a run stopped, on the argument that one canvas and one
 * reading can hold about this much — and once a document could hold sixteen
 * thousand nodes (D73), that reading ended a run on any exploration past it
 * before its first move. The graph grows by hand without a ceiling; what one run
 * may add to it is bounded here, counted off what the run produced, so a run on a
 * large exploration works as one on a seed does (D74). The number is unchanged,
 * so a run from a seed behaves as it always did, and the ends it did not take are
 * counted rather than hidden.
 */
const MAX_NODES = 260

/** What each budget allows. The three rows differ in kind and not only in size: a
 *  look around, an afternoon's worth, and everything you would sit through. */
export const RUN_BUDGETS: Record<number, RunBudget> = {
  12: { moves: 12, outputTokens: 12 * TOKENS_PER_MOVE, nodes: MAX_NODES, checks: 2 },
  24: { moves: 24, outputTokens: 24 * TOKENS_PER_MOVE, nodes: MAX_NODES, checks: 3 },
  48: { moves: 48, outputTokens: 48 * TOKENS_PER_MOVE, nodes: MAX_NODES, checks: 4 },
}

/* ── money ────────────────────────────────────────────────────────────────
   A run can be given a ceiling in dollars. Three properties make that honest,
   and all three are load-bearing:

    - the estimate comes from `estimateUsd`, which leans ABOVE the bill by
      construction — see the header of `models.ts` for which way each term errs;
    - the loop stops before the pass that could cross the ceiling rather than
      after the one that did, by holding back a reserve that is a real bound on one
      pass — `worstPassUsd`, raised further if a pass ever exceeds it. So a ceiling
      is a ceiling and not a tripwire noticed a pass late. An ordinary run holds
      that reserve per role (`ceilingFloors`): a source check is held to its own,
      larger bound and skipped, out loud, when the ceiling cannot cover it;
    - a model this app has no list price for cannot be run under a ceiling at
      all. It refuses at the start and says so, because the alternative is a
      number the user set being silently ignored while their money is spent.

   What this costs: a run under a ceiling stops roughly one pass short of it. That
   is the price of the guarantee, and every surface says so rather than leaving
   someone to wonder why $5 stopped at $4.30. */

/**
 * What one move is expected to draw before any pass like it has run here — the
 * ALLOWANCE every figure before a press falls back to.
 *
 * `TOKENS_PER_MOVE` for input as well as output, which is generous on the input side
 * — a pass sends a brief about part of the graph, not twelve thousand tokens of it.
 * A TYPICAL-CASE-LEANING-HIGH figure, and since D311 the fallback rather than the
 * figure: once passes of a kind have run on a model here, what they drew is what
 * a press is quoted at (`drawnPassUsd`). It is deliberately NOT the reserve; see
 * `worstPassUsd` for why the two cannot be the same number.
 */
export const projectMoveUsd = (p: ModelPrice): number =>
  estimateUsd(
    {
      inputTokens: TOKENS_PER_MOVE,
      outputTokens: TOKENS_PER_MOVE,
      searches: 0,
      // Every priced provider this registry ships can bill an ordinary cold
      // write at 1.25×. A typical projection cannot know whether the prefix is
      // warm, so declaring the whole input as that same-rate write is what keeps
      // the displayed figure on the conservative side of the pass it describes.
      detail: { cacheWrite5m: TOKENS_PER_MOVE, cacheWrite30m: 0, cacheWrite1h: 0 },
    },
    p,
  )

/* ── Figures drawn from this browser's passes (D311) ──────────────────────
   Every per-pass figure printed before a press starts here: what passes of the
   kind drew on this model at the effort the next one would ask for
   (`core/passDraw.ts`), and — until enough of those have run — the allowance it
   always was. So a figure follows the model each role is routed to and the
   level each kind of work is set to (D313), and it tightens as passes land.
   None of this reaches the reserve a ceiling holds back, which stays a hard
   bound. */

/** The pass class each figure's passes were sent as, and so whose level they
 *  think at. */
const EFFORT_ROW: Readonly<Record<DrawClass, PassClass>> = {
  move: 'derive',
  packet: 'derive',
  verify: 'verify',
  priorArt: 'priorArt',
  sweep: 'sweep',
  panel: 'panel',
  audit: 'audit',
  voice: 'chat',
  wrapUp: 'chat',
}

/** The level the next pass of a kind would be sent at: the level its kind of
 *  work is set to, lowered to what the model accepts — the level its receipt
 *  records, and so the level its draws were filed under. */
function nextEffort(cls: DrawClass, entry: ModelEntry): Effort {
  const want = resolvePassEffort({
    pass: EFFORT_ROW[cls],
    levels: useStore.getState().effortLevels,
  })
  return resolveEffort(entry, want)
}

/**
 * What passes of this kind drew on this model here, at today's list — or null
 * until `DRAW_AFTER` of them have, and the caller prints its allowance. Null too
 * for a model with no published rate.
 */
export function drawnPassUsd(cls: DrawClass, entry: ModelEntry): number | null {
  if (!entry.price) return null
  return drawnFigure(cls, entry, nextEffort(cls, entry), entry.price)?.usd ?? null
}

/** How many passes a drawn figure comes from, or 0 where the allowance stands —
 *  for the sentence under a figure that says which it is. */
export function drawnPassCount(cls: DrawClass, entry: ModelEntry): number {
  if (!entry.price) return 0
  return drawnFigure(cls, entry, nextEffort(cls, entry), entry.price)?.passes ?? 0
}

/** One reasoning move on this model: drawn, or the allowance. Null where the
 *  model publishes no rate. An opening pass is one. */
export const expectedMoveUsd = (entry: ModelEntry): number | null =>
  entry.price ? (drawnPassUsd('move', entry) ?? projectMoveUsd(entry.price)) : null

/**
 * One reasoning move of a RUN: an angle of a packet when the run packs them, a
 * move otherwise. A packet's angle falls back to a single move's draw before the
 * allowance — one pass per angle is the dearer shape, so it can stand in for a
 * packet's and never the other way round, and a run whose packing is unknown is
 * priced as though it did not pack.
 */
const runMoveUsd = (entry: ModelEntry, price: ModelPrice, execution?: RunExecution): number =>
  (execution === 'portfolio' ? drawnPassUsd('packet', entry) : null) ??
  drawnPassUsd('move', entry) ??
  projectMoveUsd(price)

/** One seat of a panel: a third of a drawn panel press, or a move's allowance. */
const panelSeatUsd = (entry: ModelEntry, price: ModelPrice): number => {
  const panel = drawnPassUsd('panel', entry)
  return panel !== null ? panel / PANEL_SEATS : projectMoveUsd(price)
}

/**
 * One check slot, its live requests INCLUDED: a drawn check already carries the
 * searches it ran, and the allowance adds the slot's whole allowance of them.
 * Where a slot can be a prior-art search the dearer of the two figures, as
 * `checkSlotSearches` takes the larger allowance.
 */
const checkSlotUsd = (entry: ModelEntry, price: ModelPrice, priorArt: boolean): number => {
  const verify =
    drawnPassUsd('verify', entry) ??
    projectMoveUsd(price) + (SEARCHES_PER_CHECK * price.searchPerK) / 1e3
  if (!priorArt) return verify
  const prior =
    drawnPassUsd('priorArt', entry) ??
    projectMoveUsd(price) + (checkSlotSearches(true) * price.searchPerK) / 1e3
  return Math.max(verify, prior)
}

/** One claim of a cross-examination, on the auditor's house. */
const auditClaimUsd = (entry: ModelEntry, price: ModelPrice): number =>
  drawnPassUsd('audit', entry) ?? projectMoveUsd(price)

/* ── The voice ─────────────────────────────────────────────────────────────
   When the voice is on, a press buys the pass AND the one call that says what
   it came to — the narration is a disclosed follower of that press, and a
   run's wrap-up of the run press — so each has the two figures every pass here
   has, with the same two jobs (D295): a typical-leaning-high one for the figure
   printed before the press, and a real bound for a ceiling, which a typical
   figure does not hold. D51 had left the call out of the figure, and a figure
   that omits a call the press always makes leans LOW by exactly that call. */

/**
 * What one narration is expected to draw, for the figure printed before the
 * press: `VOICE_INPUT_TOKENS` written cold, the output at its cap for the shape.
 * `projectMoveUsd`'s kind of figure — typical, leaning high — and never a
 * ceiling's: nothing caps the premise chain a narration's contract carries, so
 * a ceiling is checked against `voiceBoundUsd` over the actual request instead.
 */
export const projectVoiceUsd = (p: ModelPrice, shape: VoiceShape): number =>
  estimateUsd(
    {
      inputTokens: VOICE_INPUT_TOKENS,
      outputTokens: VOICE_MAX_TOKENS[shape],
      searches: 0,
      detail: { cacheWrite5m: VOICE_INPUT_TOKENS, cacheWrite30m: 0, cacheWrite1h: 0 },
    },
    p,
  )

/**
 * A hard upper bound on ONE narration, from the request actually about to be
 * sent — `worstPassUsd`'s job for the voice. Every term is a real cap: no
 * tokenizer emits a token for less than a byte, so the request's UTF-8 length
 * bounds its input whatever language the graph is written in; the framing
 * allowance covers what the provider wraps around it; the whole input is
 * priced as a cold write in the provider's own class, the dearest way input
 * bills; and the output is at its cap.
 */
export const voiceBoundUsd = (
  requestBytes: number,
  p: ModelPrice,
  writeClass: CacheWriteClass,
  shape: VoiceShape,
): number => {
  const inputTokens = requestBytes + VOICE_FRAMING_TOKENS
  return estimateUsd(
    {
      inputTokens,
      outputTokens: VOICE_MAX_TOKENS[shape],
      searches: 0,
      // `passCeiling`'s mapping, so the voice's bound and a pass's describe one
      // write policy: no class, no premium to charge.
      detail:
        writeClass === '5m'
          ? { cacheWrite5m: inputTokens, cacheWrite30m: 0, cacheWrite1h: 0 }
          : writeClass === '30m'
            ? { cacheWrite5m: 0, cacheWrite30m: inputTokens, cacheWrite1h: 0 }
            : undefined,
    },
    p,
  )
}

/**
 * What the voice adds to a press, on the route the Asking role runs on: zero
 * when the voice is off, and null when it is on and its model publishes no
 * rate — a figure that quietly omitted a call the press will make is worse
 * than none, `projectRunUsd`'s own rule.
 *
 * It does not ask whether the Asking role has a key. Without one the voice
 * stays quiet (invariant 4), and the figure then leans high by the call that
 * did not happen, which is the direction a figure here may lean.
 */
export function voicePressUsd(s: RouteView, shape: VoiceShape): number | null {
  if (!voiceOn()) return 0
  const entry = entryFor(s, 'chat')
  if (!entry.price) return null
  // What the voice drew here in this shape, once it has spoken often enough on
  // this model to say (D311); its allowance until then.
  return (
    drawnPassUsd(shape === 'run' ? 'wrapUp' : 'voice', entry) ?? projectVoiceUsd(entry.price, shape)
  )
}

/**
 * What one press of a verb is expected to draw, for the price shown ON the
 * button before the click.
 *
 * The same per-move figure the run projection is built from, priced at the model
 * the verb actually routes to — so the hover on Deepen and the pre-flight quote
 * on a 24-move run can never learn different arithmetic. `panel` is three seats,
 * three passes; `verify` adds a check's whole allowance of live requests, charged
 * apart from tokens because that is how they bill, and `priorArt` and `sweep`
 * add their own allowances the same way, on the same check role. Null when the routed
 * model has no list rate: no figure, rather than a wrong one.
 *
 * A scan is not one of these verbs. Its input is its own prompt, not a move's,
 * so it is priced from that prompt wherever it is priced — the Overview's press
 * (`projectScanUsd`) and a run's closing scan (`closingScanUsd`) alike — and the
 * move's figure, which was low for a large or an across scan, is not on offer
 * here to be reached for.
 */
export function projectVerbUsd(
  s: RouteView,
  verb: 'move' | 'verify' | 'priorArt' | 'sweep' | 'panel' | 'audit',
  /** The model an audit would run on. Its price is a different house's and cannot
   *  be derived from the routing view. */
  auditor?: ModelEntry | null,
  opts: {
    /** `false` where no narration follows the press: a move a run is waiting
     *  to make at its gate, since a run is quiet until it ends, and a Lab
     *  analysis, which the voice does not narrate (`passLanded`). */
    voice?: boolean
    /** Passes of the verb under this ONE press — an audit reviews each claim of
     *  its batch separately. Counted here so the voice's line is counted once. */
    count?: number
  } = {},
): number | null {
  const pass = passVerbUsd(s, verb, auditor)
  // And the voice's line after it, when the voice is on (D295): a press a person
  // makes outside a run is narrated once when it lands anything, and one that
  // lands nothing is not, so the figure leans high by it.
  const voice = opts.voice === false ? 0 : voicePressUsd(s, 'operation')
  return pass === null || voice === null ? null : pass * Math.max(1, opts.count ?? 1) + voice
}

/** The verbs a press is priced by. */
type PricedVerb = 'move' | 'verify' | 'priorArt' | 'sweep' | 'panel' | 'audit'

/** The model a verb other than a cross-examination routes to: a search to the
 *  check role, a panel to its own, everything else to reasoning. */
const verbRoute = (s: RouteView, verb: Exclude<PricedVerb, 'audit'>): ModelEntry =>
  entryFor(
    s,
    verb === 'verify' || verb === 'priorArt' || verb === 'sweep'
      ? 'check'
      : verb === 'panel'
        ? 'panel'
        : 'reason',
  )

/**
 * What a press's figure is drawn from, for the sentence beside it (D311): how
 * many passes like it this browser has seen on the model it routes to, or 0
 * where the figure is the allowance. Null where the model publishes no rate.
 */
export function pressBasis(
  s: RouteView,
  verb: PricedVerb,
  auditor?: ModelEntry | null,
): { passes: number; model: string } | null {
  const entry = verb === 'audit' ? (auditor ?? null) : verbRoute(s, verb)
  if (!entry?.price) return null
  return { passes: drawnPassCount(verb, entry), model: entry.name }
}

/**
 * The pass alone, at the model its verb routes to: what passes of the verb drew
 * on that model here, or the allowance until enough have (D311). A drawn
 * searching pass already carries the searches it ran; the allowance adds the
 * pass's whole allowance of them, charged apart from tokens as they bill.
 */
function passVerbUsd(s: RouteView, verb: PricedVerb, auditor?: ModelEntry | null): number | null {
  if (verb === 'audit') {
    return auditor?.price ? auditClaimUsd(auditor, auditor.price) : null
  }
  const entry = verbRoute(s, verb)
  const price = entry.price
  if (!price) return null
  const searching = (searches: number) =>
    projectMoveUsd(price) + (searches * price.searchPerK) / 1e3
  if (verb === 'verify') return drawnPassUsd('verify', entry) ?? searching(SEARCHES_PER_CHECK)
  if (verb === 'priorArt') {
    return drawnPassUsd('priorArt', entry) ?? searching(SEARCHES_PER_PRIOR_ART)
  }
  if (verb === 'sweep') return drawnPassUsd('sweep', entry) ?? searching(SEARCHES_PER_SWEEP)
  if (verb === 'panel') return drawnPassUsd('panel', entry) ?? PANEL_SEATS * projectMoveUsd(price)
  return drawnPassUsd('move', entry) ?? projectMoveUsd(price)
}

/**
 * Tokens a request's own text is worth, counted to lean high: three characters of
 * ASCII to a token, and every other character a token of its own.
 *
 * For a PROJECTION, never a bill — `estimateSpendUsd` reads the provider's counts.
 * Three is below what English prose averages (nearer four), and a character
 * outside ASCII — an accent, a Greek letter in a formula, a CJK claim — is often
 * several bytes to a byte-level tokenizer and never less than one token's worth
 * here, so the error runs toward charging more than the pass will.
 */
export const projectTokens = (text: string): number => {
  let ascii = 0
  let other = 0
  for (const ch of text) {
    if (ch.charCodeAt(0) < 0x80) ascii++
    else other++
  }
  return Math.ceil(ascii / 3) + other
}

/**
 * Provider framing a request carries beyond its own text: the role markers, the
 * structured-output instructions a schema is turned into. Not measured per
 * provider, so set above what either known one adds.
 */
const REQUEST_FRAMING_TOKENS = 1_000

/**
 * What one scan is expected to draw, priced from the prompt it would send.
 *
 * The scan was priced as a move — `TOKENS_PER_MOVE` in, the same out — and its
 * input is not a move's. Counted by `projectTokens` over this repo's own builder,
 * a six-claim graph sends about 3,200 tokens and a forty-four-claim one about
 * 7,400 (the selection stops at forty-four lines, so a bigger graph adds only its
 * record), which the move figure charged as twelve thousand; and a scan across
 * six other explorations sends their digests too — about 14,000 with claims of
 * three hundred characters, more with longer ones — where the move figure was
 * LOW, the one direction invariant 5 forbids. So the input is the prompt itself:
 * `scanRequestText` in `reason.ts` builds it with the pass's own builder, and the
 * output is the pass's own cap, `SCAN_MAX_TOKENS`, all of it, reasoning
 * included. Every input token is charged as a cold write, as in
 * `projectMoveUsd`. Null where the routed model has no list rate.
 */
export function projectScanUsd(s: RouteView, request: string): number | null {
  const entry = entryFor(s, 'scan')
  if (!entry.price) return null
  const inputTokens = projectTokens(request) + REQUEST_FRAMING_TOKENS
  return estimateUsd(
    {
      inputTokens,
      outputTokens: SCAN_MAX_TOKENS,
      searches: 0,
      detail: { cacheWrite5m: inputTokens, cacheWrite30m: 0, cacheWrite1h: 0 },
    },
    entry.price,
  )
}

/**
 * A scan priced from the prompt it would send, or null where there is no prompt
 * to price.
 *
 * Every caller reads this while rendering — the composer's pre-flight, the gate's
 * figure, the Overview's press — so it must never throw. `scanRequestText`
 * builds the research contract the pass would send, and the contract refuses
 * exactly where the pass would: a research package with a private or changed
 * mathematical input, for one (`researchContractFor`). That refusal, raised
 * inside a render, took the whole composer down with it, and the Research Lab's
 * mathematics panel beneath. Now the press prints no figure, the pass itself says
 * why it cannot be sent, and the pre-flight holds a move for the scan
 * (`scanSlotUsd`), as it does wherever there is no projection. Null too where
 * the scan route has no list rate.
 */
export function projectScanPromptUsd(
  s: RouteView & Parameters<typeof scanRequestText>[0],
  across?: string,
): number | null {
  let request: string
  try {
    request = scanRequestText(s, across)
  } catch {
    return null
  }
  return projectScanUsd(s, request)
}

/**
 * A run's closing scan, priced as the Overview prices its press: from the prompt
 * it would send. Every run ends on a scan of the open graph under the reading the
 * workspace is set to (`scanForBridges({ scope: 'within' })`), which is exactly
 * `scanRequestText` over the store, so this is the figure the scan's gate prints
 * before Go. It was the move's figure, twelve thousand tokens in whatever the
 * graph held — low on a large graph, the one direction invariant 5 forbids.
 */
export const closingScanUsd = (
  s: RouteView & Parameters<typeof scanRequestText>[0],
): number | null => projectScanPromptUsd(s)

/**
 * What the pre-flight holds for the closing scan: its own prompt, priced now, and
 * never less than a move.
 *
 * The scan runs at the END of the run, over a graph the run has grown, and
 * before a run starts there is only today's graph to read. The prompt the scan
 * will send is usually the larger — the selection fills toward its forty-four
 * lines and the record lengthens with every check — so on a young graph, whose
 * scan is a quarter of a move's input, today's prompt alone would price low the
 * scan the run will actually make. The move's figure covers that growth, as it
 * always has; the prompt's covers a graph already large enough that the move's
 * was the low one. Neither is a bound — the reserve is (`worstPassUsd`) — and
 * the larger of the two is taken because an estimate here may lean only one
 * way. Absent a projection (a caller with no graph to read), the move's.
 */
const scanSlotUsd = (scan: ModelPrice, projected?: number | null): number =>
  Math.max(projectMoveUsd(scan), projected ?? 0)

/**
 * A hard upper bound on ONE pass, which is what the reserve has to be.
 *
 * This exists because a reserve sized from the typical figure above does not
 * actually hold the ceiling, and the difference is the whole guarantee. Every term
 * is a real cap rather than an estimate:
 *
 *  - `outputTokens` is the largest `maxTokens` any verb in `reason.ts` requests, so
 *    no pass can draw more output than this and be billed for it;
 *  - `searches` is one check's whole allowance, which is the most any single pass
 *    can spend on live requests;
 *  - `inputTokens` covers the complete brief and research contract. The shared
 *    reasoning boundary refuses more than 64k UTF-8 bytes of supplied text and
 *    schema in a capped run, leaving 16k for provider framing. It never clips
 *    definitions to squeeze a request under the reserve.
 *
 * THAT LAST BULLET IS TRUE OF THE BRIEF AND FALSE OF A PASS THAT SEARCHES, which
 * is why the allowance is not one number. A source check runs a server-side tool
 * loop whose fetched page text is replayed into every iteration and billed as
 * input — `addUsage` sums `usage.iterations`, so ONE pass carries the whole loop.
 * Reasoning only about the brief is why nobody noticed, and while it stood the
 * reserve did not bound the pass it most needed to.
 *
 * `SEARCHING_INPUT_TOKENS` is that allowance, and it is derived from this repo's
 * own measurements rather than chosen. Both are in `anthropic.ts` beside
 * `MAX_TURNS`:
 *
 *  - one REAL check billed 116,814 cached-read tokens against a 20,711 write
 *    inside a single turn — 137,525 input for a pass bounded, like every check,
 *    by `VERIFY_BUDGET`'s six searches and three fetches. Times `MAX_TURNS`,
 *    the cap on how often a paused loop is resumed, that is ~550,000;
 *  - a FORCED 49-request pass billed 1,483,887 across its whole life. The app
 *    allows nine of those 49, and the loop's input grows faster than its request
 *    count — every iteration re-reads what the last one fetched — so nine
 *    requests draw LESS than nine forty-ninths of that, ~272,000 at most.
 *
 * 600,000 covers both, the larger with headroom. It is a measured bound rather
 * than a guessed one, which is the whole difference: a reserve is a guarantee,
 * and a guarantee sized by feel is the defect it exists to prevent.
 *
 * Only the CHECK role draws it. `runVerify` and `runLinkScan` are the two passes
 * that declare `web`, both routed there; reasoning, panels and the closing scan
 * declare no tools and keep the 80k brief allowance, so a run that schedules no
 * checks stays on the figure it always drew — plus the write premium below,
 * which raises every allowance and is the one part no role escapes.
 *
 * The cost of a correct reserve is that a run under a ceiling leaves about this much
 * unspent. That is the price of the promise, and every surface says so.
 *
 * A FUNCTION and not a constant, which is not a style choice: `reason.ts` imports
 * `stopRun` from this file, so the two modules are a cycle, and a top-level object
 * literal reading `SEARCHES_PER_CHECK` is evaluated while `reason.ts` is still
 * initialising — a temporal dead zone error that takes the whole app down on boot.
 * Neither `tsc` nor the production bundler catches it; only actually loading the
 * page does. Inline it back into a `const` and the app stops starting.
 */
/** What a pass that runs the server-side tool loop can draw. Derived above from
 *  two measured passes; see `SEARCHES_PER_CHECK` and `MAX_TURNS` for the two
 *  bounds it is composed against. */
const SEARCHING_INPUT_TOKENS = 600_000

/**
 * The same allowance for a provider whose search loop cannot be resumed (D376).
 *
 * The 600,000 above is one turn times `MAX_TURNS`, and only the Anthropic adapter
 * has turns: its loop pauses and is resumed, and every resume re-reads the whole
 * conversation. OpenAI's Responses API runs the loop inside one request ("there is
 * no `pause_turn` to resume, so one request is one pass", `openai.ts`), and
 * Gemini's grounding is one request too. Charging them four turns held a check's
 * reserve at about $3.35 on GPT-5.6 Sol, so a $5 cap on a twelve-move run skipped
 * both its checks while its moves came to $0.70.
 *
 * One turn, sized on the heaviest single turn measured on any provider: the real
 * Anthropic check's 137,525 above, which replays fetched page text no other
 * provider returns. Rounded up to 150,000. Twenty-two real GPT-5.6 Sol checks drew
 * at most 48,271, with seven searches at most, so this is three times the largest.
 *
 * A custom endpoint keeps the four-turn allowance: it may front anything, and a
 * bound may not guess low.
 */
const ONE_TURN_SEARCHING_INPUT_TOKENS = 150_000

/** The searching allowance for a provider, by whether its search loop resumes. */
const searchingInputTokens = (provider: ProviderId): number =>
  provider === 'openai' || provider === 'google'
    ? ONE_TURN_SEARCHING_INPUT_TOKENS
    : SEARCHING_INPUT_TOKENS

/** The brief alone, for the passes that declare no tools. */
const BRIEF_INPUT_TOKENS = 80_000

/**
 * EVERY PROJECTED INPUT TOKEN IS CHARGED AS A CACHE WRITE, which is the second
 * half of the same bound and was missing from the first.
 *
 * `estimateUsd` adds the write premium off a row's breakdown. A projection has
 * no breakdown, so this one priced its whole allowance at base — and a real pass
 * whose input was written rather than read estimates ABOVE it, which is the one
 * thing a reserve may not permit. The pass this bounds is frequently the cold
 * one: a run's first pass writes the prefix every pass after it reads.
 *
 * The whole allowance rather than a share of it, because a projection cannot
 * know the share and a bound that guesses low is not a bound. Five minutes and
 * not an hour for the same reason the adapter files an unsplit write there: this
 * app's `cache_control` names no `ttl`, so a one-hour entry is not something a
 * pass can create. The cost is a floor a quarter higher on the input term.
 */
const passCeiling = (
  searching: boolean,
  provider: ProviderId,
  searches: number = SEARCHES_PER_CHECK,
) => {
  const writeClass = CACHE_WRITE_CLASS[provider]
  const inputTokens = searching ? searchingInputTokens(provider) : BRIEF_INPUT_TOKENS
  return {
    inputTokens,
    outputTokens: 20_000,
    // ZERO where the pass cannot search, which the input allowance already knew
    // and this line did not. `runVerify` and `runLinkScan` are the only passes
    // that declare `web` and both are routed to the check role; reasoning, the
    // panel, the closing scan and the auditor declare none, so nine searches
    // reserved against them is nine charges nothing can incur. Leaning high is
    // allowed of an ESTIMATE and not of a refusal — the same rule the scan
    // taught this file: where a pass provably cannot happen, nothing counts it.
    searches: searching ? searches : 0,
    // The provider's own short-lived category. Both known providers bill it at
    // 1.25×, but keeping five and thirty minutes apart makes the synthetic row
    // describe the same policy the corresponding live receipt will report.
    detail:
      writeClass === '5m'
        ? { cacheWrite5m: inputTokens, cacheWrite30m: 0, cacheWrite1h: 0 }
        : writeClass === '30m'
          ? { cacheWrite5m: 0, cacheWrite30m: inputTokens, cacheWrite1h: 0 }
          : undefined,
  }
}

/**
 * `searching` is a fact about the ROLE and not about the model: any model can be
 * routed to the check role, and none of them searches anywhere else. Defaulted
 * to false so a caller that names no role gets the brief allowance, which is
 * what every pass but one draws.
 */
const worstPassUsd = (
  p: ModelPrice,
  searching: boolean,
  /** Whose pass: it sets the cache-write premium and, for a searching pass,
   *  whether the loop can be resumed (`searchingInputTokens`). */
  provider: ProviderId,
  /** Live requests a searching pass may make — a source check's, unless a check
   *  slot can be the prior-art search's larger allowance. See `checkSlotSearches`. */
  searches: number = SEARCHES_PER_CHECK,
): number => estimateUsd(passCeiling(searching, provider, searches), p)

/**
 * Live requests one check slot may make.
 *
 * A source check's allowance, unless this run can spend a check slot on a
 * prior-art search — which it does for every conjecture it lands, and only when
 * it was given conjecture passes (`RunBudget.conjectures`). That search runs
 * eight searches and four fetches against the check's six and three, so a
 * projection or a reserve that priced every slot as a check would under-quote the
 * one pass this run can make that searches hardest. The larger of the two, and
 * only where a conjecture can land: a run that proposes none is priced exactly as
 * it always was.
 *
 * A function, never a module-scope constant: both figures live in `reason.ts`,
 * which imports this file, and a read at module evaluation is the boot cycle the
 * note above `SEARCHING_INPUT_TOKENS` describes.
 */
export const checkSlotSearches = (priorArt: boolean): number =>
  priorArt ? Math.max(SEARCHES_PER_CHECK, SEARCHES_PER_PRIOR_ART) : SEARCHES_PER_CHECK

/**
 * The models a run can bill, by the kind of pass that bills them.
 *
 * An object rather than three positional arguments because two of the three are
 * the same value in almost every call, and a positional triple of near-identical
 * entries is the shape that gets transposed in a refactor and quotes a panel at
 * the check model's rate with nothing to catch it.
 *
 * The scan and chat roles are absent on purpose: a run makes neither. Adding them
 * would put two models into the reserve that no pass of a run can reach, and a
 * reserve that bounds work the run cannot do is a ceiling that refuses runs it
 * should have allowed.
 */
export interface RunModels {
  reason: ModelEntry
  check: ModelEntry
  panel: ModelEntry
  /**
   * The closing synthesis scan, which every run ends on.
   *
   * Added because it was the one pass a run makes that the projection could not
   * see. `scanForBridges` resolves the `scan` role itself, so a
   * Sonnet-reasoning/Opus-scan configuration quoted the whole run at Sonnet's
   * rate and sized its reserve against the wrong model — understating the figure
   * on the pass that runs last, when the least budget is left to absorb it.
   *
   * `chat` is still absent, and still deliberately: a run makes no conversation.
   */
  scan: ModelEntry
}

/** What a run would resolve to, given the store. One place, so the pre-flight
 *  quote in the sidebar and the reserve inside `startRun` cannot disagree about
 *  which models a run is about to use. */
export const runModelsFor = (s: RouteView): RunModels => ({
  reason: entryFor(s, 'reason'),
  check: entryFor(s, 'check'),
  panel: entryFor(s, 'panel'),
  scan: entryFor(s, 'scan'),
})

/**
 * What a whole budget would draw, before any of it has run.
 *
 * The pre-flight figure. Charges every move, check, seat and claim at what passes
 * of its kind drew on its model here — the upper quartile of them, at the effort
 * the run would ask for — and each at its allowance until enough have run
 * (D311), which is an upper estimate of an upper estimate. That is why the
 * control that prints it says "up to about". Returns null for a model with no
 * known price rather than 0.
 */
export function projectRunUsd(
  models: RunModels,
  budget: RunBudget,
  /** The model an audit would run on, when this run allows any. Its price is a
   *  DIFFERENT house's, so the figure cannot be derived from the others — and a
   *  projection that costed an audit at the selected model's rate would quote the
   *  wrong provider's price for the one pass that deliberately leaves it. */
  auditor?: ModelEntry | null,
  /** Whether the closing scan can happen — see `dearestPass`. */
  scanInPlay = true,
  /** The closing scan's own prompt, priced now (`closingScanUsd`) — see
   *  `scanSlotUsd` for what the projection holds for the scan. */
  scanUsd?: number | null,
  /** Whether the run packs its reasoning. Absent is priced as though it did not,
   *  the dearer shape — see `runMoveUsd`. */
  execution?: RunExecution,
): number | null {
  const reason = models.reason.price
  const check = models.check.price
  const panel = models.panel.price
  const scan = models.scan.price
  // Any one of the passes this run can MAKE unpriced sinks the whole figure.
  // Returning a number that silently omitted a routed model's passes would be
  // worse than returning none: the ceiling is enforced against this, and a short
  // quote is a ceiling that does not hold. A scan that cannot run is not one of
  // them — sinking the figure over it left `startRun` accepting a ceiling and
  // then sizing its caps off the full requested budget, because a projection it
  // could not compute is a projection it does not apply.
  // Conditioned PER ROLE, because every term below is already: a check's passes
  // are multiplied by `budget.checks` and a panel's by `budget.panels`, so a run
  // that schedules neither is priced exactly whatever those two rows charge.
  // Refusing to compute over them said "no estimate" about a run whose estimate
  // was fully determined, and the app treats an absent projection as licence to
  // size its caps off the full requested budget — the scan's own lesson, two
  // roles over. See `RolesInPlay`.
  const roles = rolesInPlay(budget, scanInPlay)
  if (!reason || (roles.check && !check) || (roles.panel && !panel) || (roles.scan && !scan)) {
    return null
  }

  const move = runMoveUsd(models.reason, reason, execution)
  // Zero where the role is out of play, which is what every term below would come
  // to anyway — `budget.panels` and `budget.checks` are the multipliers, and both
  // are zero exactly then. Written out because the price is now optional.
  const panelMove = panel ? panelSeatUsd(models.panel, panel) : 0
  // A whole check slot, its live requests included (`checkSlotUsd`).
  const checkSlot = check ? checkSlotUsd(models.check, check, roles.priorArt === true) : 0

  // Two further passes per panel. The move slot itself is already inside
  // `budget.moves`, so only the second and third seats are added — and both are
  // charged in full even though they hit the cached prefix by construction, because
  // every term in this figure leans high on purpose.
  const panels = (budget.panels ?? 0) * 2 * panelMove
  // An audit's own pass, at the auditor's rate. Its move slot is also already inside
  // `budget.moves` and is deliberately NOT subtracted: the overlap is one move's
  // worth per audit, in the direction this whole figure errs.
  const audits =
    (budget.audits ?? 0) > 0 && auditor?.price
      ? (budget.audits ?? 0) * auditClaimUsd(auditor, auditor.price)
      : 0

  // The routing top-ups, and the reason this function did not simply start
  // charging every pass at its own rate.
  //
  // `budget.moves` covers one pass per panel and per check, charged at the
  // REASONING rate because that is what a move is. Route a panel or a check to a
  // dearer model and that in-budget pass is suddenly under-charged — the one
  // direction this figure is not allowed to be wrong in. These two terms pay the
  // difference, and only the difference.
  //
  // They are exactly zero when a role shares the selection's model, which is the
  // state every existing user is in and will stay in unless they go looking. So
  // routing changes this number for the people who routed something and for
  // nobody else — a quote that moved on its own would undermine the one thing it
  // is for.
  const panelTopUp = (budget.panels ?? 0) * Math.max(0, panelMove - move)
  // A check slot carries its live requests (D311): they bill apart from tokens and
  // at the CHECK model's rate, because the check is the pass that runs them, and a
  // drawn check already counted the ones it ran. Only the PROVIDER's searches and
  // fetches are in here: the research rows' lookups are keyless public-API GETs
  // with no published rate, so folding them into a dollar figure would be noise
  // wearing precision's clothes — they are counted where they belong instead, in
  // the run control's promise about what leaves the machine, off
  // `liveLookupsPerCheck`.
  const checkTopUp = budget.checks * Math.max(0, checkSlot - move)
  // The closing scan, on the same principle and for the same reason. Every run
  // ends on one and its move slot is already inside `budget.moves` at the
  // REASONING rate, so only the difference belongs here — and only when it is
  // positive. A scan routed somewhere cheaper does not refund the quote: this
  // figure errs high in one direction on purpose, and a term that could subtract
  // is a term that could make it err the other way. The scan's own figure is its
  // prompt's, never less than a move's (`scanSlotUsd`).
  const scanTopUp = scanInPlay && scan ? Math.max(0, scanSlotUsd(scan, scanUsd) - move) : 0

  return budget.moves * move + panels + audits + panelTopUp + checkTopUp + scanTopUp
}

/**
 * What PRESSING a run is expected to draw: the run, plus the summary the voice
 * gives when it ends (D295) — the figure every surface prints before the press.
 *
 * A separate function rather than a term inside `projectRunUsd`, because that
 * one has a second job this must not reach: the ceiling is enforced against it
 * and `projectMoveRange` divides it into moves. Under a ceiling the voice YIELDS
 * to the moves — the narrator checks the run's own drawn spend against the
 * ceiling before it speaks and stays quiet when its bound does not fit
 * (`voiceFitsCeiling`) — so the summary belongs in what a press is quoted at and
 * not in what a ceiling is sized from.
 */
export function projectRunPressUsd(
  s: RouteView,
  ...run: Parameters<typeof projectRunUsd>
): number | null {
  const moves = projectRunUsd(...run)
  const voice = voicePressUsd(s, 'run')
  return moves === null || voice === null ? null : moves + voice
}

/**
 * Reference request count under full packing and the configured grounding mix.
 *
 * A planning aid, not a bound, prediction, or receipt: a grounding slot with no
 * eligible target can lower the count, while graph shape, cadence boundaries,
 * stalls, and incomplete-packet fallback can raise it. It changes no dollar
 * estimate; the existing high-side sequential projection remains the consent
 * boundary until target-model receipts support a narrower one.
 */
export function projectModelPasses(
  budget: RunBudget,
  execution: RunExecution = DEFAULT_EXECUTION,
): number {
  const panels = budget.panels ?? 0
  if (execution === 'sequential') return budget.moves + panels * 2
  // A conjecture pass is never a packet angle — its schema is its own — so each
  // is one request of its own, like a grounding pass.
  const single = (budget.audits ?? 0) + (budget.conjectures ?? 0)
  const grounding = budget.checks + panels + single + 1
  const reasoning = Math.max(0, budget.moves - grounding)
  return Math.ceil(reasoning / 3) + budget.checks + panels * 3 + single + 1
}

/**
 * How many of a budget's moves a ceiling can actually pay for.
 *
 * A run under a ceiling smaller than the projection stops well short of its move
 * count, and until this existed nothing derived that figure — so the control said
 * "some of the 24 moves will go unspent" where "some" was fifteen, and the two per-run caps
 * in `runPolicy` were shares of twenty-four against a run that made nine moves. Both
 * readers need the same arithmetic, so it lives here beside the prices.
 *
 * Two ends, and neither is a guess dressed as the other:
 *
 *  - `likely` divides the usable money by the MEAN cost of a move under this budget's
 *    own mix. That is where the naive `ceiling ÷ total × moves` lands, and it is only
 *    honest because `projectRunUsd` already prices the configured check rate, the
 *    panel seats and the auditor's house — a mean over reasoning passes alone would
 *    overstate the count by exactly the passes that cost the most.
 *  - `fewest` divides it by the dearest KIND of move this budget schedules, each at
 *    the same typical-leaning-high size `likely` uses. A low end, and NOT the bound
 *    this comment used to call it.
 *
 * That correction is worth the space, because "a real lower bound: no run can make
 * fewer moves than this" was a claim the arithmetic never supported. `projectMoveUsd`
 * is `TOKENS_PER_MOVE`, a typical pass; the reserve's `worstPassUsd` is 80k, 150k or 600k,
 * an order of magnitude above it. A source check that draws its whole allowance costs
 * more than this term, so a run CAN make fewer moves than `fewest` says.
 *
 * It is not rebuilt from `worstPassUsd`, and that is a decision rather than an
 * omission. Two reasons, and the second is the one that settles it:
 *
 *  - Dividing by the worst pass reports 1 for nearly every configuration that
 *    schedules a check, because that pass alone is most of a usual ceiling. A range
 *    reading "1–12 of the 12 moves" answers the reader's question with noise.
 *  - It would STILL not be a bound. A true one is mix-aware — `budget.checks` caps
 *    how many moves can be the dear kind — so "every move is the worst pass" is not
 *    a run this budget can schedule either. Swapping one non-bound for a gloomier
 *    non-bound buys nothing and costs the figure its usefulness.
 *
 * What actually holds the guarantee is the reserve, which is a real bound on one
 * pass and is checked before every one of them. This pair is a projection, the
 * surface prints it as "you will likely get 7–12 … projected, not promised", and the
 * word that had to go was the one in here.
 *
 * The usable amount is the ceiling MINUS the reserve, because the loop stops before
 * the pass that would cross the ceiling rather than after it — a projection that spent
 * the reserve would promise a move the run is built never to make. The reserve is the
 * one held before every pass, `ceilingFloors`' `pass`; a check's larger one decides
 * only whether a check is scheduled, and under it the projection's mix has none.
 *
 * Null when any model in play has no published price, matching `projectRunUsd`: there
 * is no rate to project against, and a number that silently omitted a routed model's
 * passes would be worse than none.
 */
export interface MoveProjection {
  /** At the mix this budget schedules. The figure to print. */
  likely: number
  /** If every remaining move were the dearest KIND this budget allows, at the same
   *  typical size `likely` uses. A low end, not a bound — see the header for why it
   *  is not rebuilt from `worstPassUsd`, and where the guarantee actually lives. */
  fewest: number
}

export function projectMoveRange(
  models: RunModels,
  budget: RunBudget,
  auditor: ModelEntry | null,
  ceiling: number,
  /** Whether the closing scan can happen — see `dearestPass`. */
  scanInPlay = true,
  /** Whether this run's checks are its own to skip — see `ceilingFloors`. */
  checksDeferred = true,
  /** The closing scan's own prompt, priced now — as `projectRunUsd` takes it. */
  scanUsd?: number | null,
  /** Whether the run packs its reasoning — as `projectRunUsd` takes it. */
  execution?: RunExecution,
): MoveProjection | null {
  const reason = models.reason.price
  const check = models.check.price
  const panel = models.panel.price
  const scan = models.scan.price
  // Per role, matching `projectRunUsd` and the `dearest` terms below, which have
  // asked the budget since they were written: refusing here over a check or a
  // panel no pass can reach withheld a range the ceiling fully determines.
  const asked = rolesInPlay(budget, scanInPlay)
  if (!reason || (asked.check && !check) || (asked.panel && !panel) || (asked.scan && !scan)) {
    return null
  }
  // Conditioned on the budget exactly as `startRun` conditions it: a reserve sized
  // for an auditor a run may not call is a ceiling that refuses runs it should allow.
  const inPlay = (budget.audits ?? 0) > 0 ? auditor : null
  const floors = ceilingFloors(models, inPlay, asked, checksDeferred)
  if (floors === null) return null
  // A ceiling no larger than a check's own reserve never has a check's worth left,
  // so the loop skips every one and those slots are reasoning moves. Projecting
  // them as checks would print a range for a run that cannot happen — the mix is
  // the one the loop will make.
  const mix = floors.check && ceiling <= floors.check.usd ? { ...budget, checks: 0 } : budget
  const roles = rolesInPlay(mix, scanInPlay)
  const total = projectRunUsd(models, mix, auditor, scanInPlay, scanUsd, execution)
  if (total === null || total <= 0) return null

  // What the loop holds back before EVERY pass, which is the pass reserve — a
  // check's larger one only ever decides whether a check is scheduled.
  const usable = ceiling - floors.pass.usd
  // Each kind at the figure `projectRunUsd` charged it (D311), so the range and
  // the total read one set of figures.
  const dearest = Math.max(
    runMoveUsd(models.reason, reason, execution),
    roles.check && check ? checkSlotUsd(models.check, check, roles.priorArt === true) : 0,
    // Three seats, one pass each, all inside one move slot.
    roles.panel && panel ? PANEL_SEATS * panelSeatUsd(models.panel, panel) : 0,
    (budget.audits ?? 0) > 0 && inPlay?.price ? auditClaimUsd(inPlay, inPlay.price) : 0,
    // The closing scan, which takes a move slot like the rest. Omitting it
    // inverted the range: the scan top-up raises `total`, so `likely` falls,
    // while a `dearest` that could not see the scan stayed put — and a Sonnet run
    // with a Fable scan rendered "7–6 of the 12 moves", a lower bound above its
    // upper one. Its one guard is reachability, because "every run ends on a
    // scan" stops being true under an allowance below the scan's floor. Priced as
    // `projectRunUsd` prices it, so the range and the total read one figure.
    roles.scan && scan ? scanSlotUsd(scan, scanUsd) : 0,
  )
  const fit = (usd: number): number =>
    usd <= 0 ? budget.moves : Math.max(0, Math.min(budget.moves, Math.floor(usable / usd)))
  return { likely: fit(total / budget.moves), fewest: fit(dearest) }
}

/**
 * The smallest ceiling that can guarantee even one pass, for this model.
 *
 * The reserve, which is `worstPassUsd` — so below this figure the reserve exceeds
 * the ceiling before anything runs and the run would stop having done nothing. A
 * control that accepts a number and then produces an empty run has spent the user's
 * time to tell them nothing, so both the field and `startRun` refuse and name this.
 *
 * It is a bound on the worst pass and not on the usual one, which is why it looks
 * high next to what a pass actually draws. Lowering it would mean a ceiling that
 * holds most of the time, and a ceiling that holds most of the time is not one.
 */
export const minCeilingUsd = (entry: ModelEntry, searching = false): number | null =>
  entry.price ? worstPassUsd(entry.price, searching, entry.provider) : null

/**
 * The most one press of these kinds can draw at worst, for a caller that has to
 * know before it presses: the MCP server admits a press only when its worst case
 * fits what is left of the person's allowance (D315, D317).
 *
 * `minCeilingUsd` bounds one reasoning pass or one source check. These presses are
 * more than either, each sized from the budget its engine function declares, so the
 * figure moves when the press does:
 *
 *  - a prior-art search is a searching pass with `SEARCHES_PER_PRIOR_ART` live
 *    requests, against a check's `SEARCHES_PER_CHECK`;
 *  - a field sweep likewise, with `SEARCHES_PER_SWEEP`;
 *  - a link scan is three searching gathers (the conjunction's, then one per side)
 *    and the brief pass that reads them when the reading earned it. One that reads
 *    `already-made` off the conjunction stops at about a third of this.
 *
 * Null for a model with no list price, as `minCeilingUsd` is. The budgets are read
 * here, at call time, never at module scope: they live in `reason.ts`, which
 * imports this file.
 */
export const pressCeilingUsd = (
  entry: ModelEntry,
  press: 'priorArt' | 'sweep' | 'linkScan',
): number | null => {
  if (!entry.price) return null
  const who = entry.provider
  if (press === 'priorArt') return worstPassUsd(entry.price, true, who, SEARCHES_PER_PRIOR_ART)
  if (press === 'sweep') return worstPassUsd(entry.price, true, who, SEARCHES_PER_SWEEP)
  const both = LINK_CONJUNCTION_BUDGET.maxSearches + LINK_CONJUNCTION_BUDGET.maxFetches
  const side = LINK_GATHER_BUDGET.maxSearches + LINK_GATHER_BUDGET.maxFetches
  return (
    worstPassUsd(entry.price, true, who, both) +
    2 * worstPassUsd(entry.price, true, who, side) +
    worstPassUsd(entry.price, false, who)
  )
}

/**
 * Which routable roles a run can BILL, which is not the same as which it routes.
 *
 * An absent field means in play. Only the three that can be scheduled to zero are
 * here — reasoning is every move and has no off switch, and the auditor was
 * always passed as an entry-or-null for this same reason.
 *
 * One rule, and it is the one this file keeps relearning: **where a pass provably
 * cannot happen, nothing counts it.** A reserve sized for a check a run does not
 * schedule refuses a ceiling that was always enough; an unpriced-model refusal
 * over a panel role no pass can reach blocks a run for a rate nothing would have
 * charged; a projection that returns null over either says "no estimate" about a
 * run it could have priced exactly. The scan learned this first, on
 * reachability; check and panel learn it here, on the budget.
 *
 * The BUDGET is the authority and the spec is not: `resolveChecks` gives an
 * absent `spec.checks` the budget row's own default, so a run that asked for
 * nothing still schedules checks. Read `budgetFor(spec)` and ask it.
 */
export interface RolesInPlay {
  /** False when the run makes no REASONING pass, which only a programme can
   *  arrange — untick every reasoning step and what is left is checks, panels
   *  and audits. See `reasonWillRun`. Every other run reasons by definition,
   *  which is why this was the one role counted unconditionally. */
  reason?: boolean
  /** False when `budget.checks` is zero — no source check, so no check pass. */
  check?: boolean
  /** False when `budget.panels` is zero or absent. */
  panel?: boolean
  /** False when the closing scan is unreachable — see `scanIsReachable`. Not the
   *  same as unwanted: the scan is the one pass with no "turn it off", and only a
   *  budget allowing fewer than `SCAN_FLOOR` nodes cannot reach it. */
  scan?: boolean
  /**
   * True when a check slot can be a prior-art search — a run given conjecture
   * passes and checks both. Not a role: it widens the check role's allowance of
   * live requests (`checkSlotSearches`). And ABSENT MEANS NO, unlike the three
   * above, because it describes a pass that exists only when a conjecture can
   * land, and an unconditioned caller has none.
   */
  priorArt?: boolean
}

/**
 * The four, read off a budget that has already resolved three of them — and
 * whether a check slot may be the prior-art search.
 *
 * The reasoning role is the odd one out: it is a fact about the approved LIST
 * rather than about the budget, so it comes from the programme and an absent
 * programme means every run's default — a run that reasons. Callers that price a
 * generic run therefore keep the behaviour they had before this argument
 * existed.
 */
export const rolesInPlay = (
  budget: RunBudget,
  scanInPlay: boolean,
  programme?: Programme,
): RolesInPlay => ({
  reason: reasonWillRun(programme),
  check: budget.checks > 0,
  panel: (budget.panels ?? 0) > 0,
  scan: scanInPlay,
  priorArt: budget.checks > 0 && (budget.conjectures ?? 0) > 0,
})

/**
 * The same floor, taken over every model a run can reach.
 *
 * The reserve's whole job is to bound the dearest pass the run could make. Before
 * routing that was the selected model, with the auditor as the one documented
 * exception; now any of three roles can name a different model, and a reserve
 * sized off the selection alone would let a run route its panels to Fable 5 and
 * stop one pass late — the exact failure `worstPassUsd` exists to prevent.
 *
 * Returns null if ANY of them is unpriced, rather than skipping it: an unpriced
 * model in the mix means there is no rate to hold that pass to, and the caller
 * has to refuse the ceiling rather than quietly guarantee less than it says.
 *
 * `seats` rides out with the figure because the figure stopped being one pass
 * the moment the panel started counting three, and every surface that refuses a
 * ceiling says what it could not cover. Told "cannot cover a single pass" over a
 * figure that bounds a three-seat panel, a user whose ceiling covers two of them
 * is looking at a sentence that is false about their own case. One walk, one
 * fact, all of it — the same reason the entry rides out beside the number.
 */
export function dearestPass(
  models: RunModels,
  auditor?: ModelEntry | null,
  /** Which routable roles this run can actually bill — see `RolesInPlay`. An
   *  absent field means in play, which is what an unconditioned caller means. */
  roles: RolesInPlay = {},
): { entry: ModelEntry; usd: number; seats: number } | null {
  // Each role carries whether ITS pass searches, because the allowance differs by
  // an order of magnitude and the model does not decide it: the check role runs
  // the server-side loop wherever it is pointed, and no other role runs one
  // wherever it is pointed. A flat list of entries could not express that, and
  // sizing every role at the searching allowance would refuse ceilings over a
  // loop four of the five passes never enter.
  const inPlay: { entry: ModelEntry; searching: boolean; seats: number }[] = [
    // Conditioned like the other three, and for the same reason: a check-only
    // programme makes no reasoning pass, so a reserve sized on the selection
    // bounds work this run cannot do — and an unpriced selection sinks the whole
    // figure to null, which ends that run on its first move with
    // `reserveUsd = Infinity`. A list that comes out EMPTY still answers null,
    // which is the honest figure for a run that can make no pass at all; the
    // programme sheet refuses such a list before it can be started.
    ...(roles.reason === false ? [] : [{ entry: models.reason, searching: false, seats: 1 }]),
    ...(roles.check === false ? [] : [{ entry: models.check, searching: true, seats: 1 }]),
    ...(roles.panel === false
      ? []
      : // A panel is PANEL_SEATS billed requests inside one op, and `shouldStop`
        // does not run between them — so what this reserve has to cover is all
        // three, not the dearest one of them. Reserving a single pass admitted a
        // panel the remaining budget could pay for a third of.
        [{ entry: models.panel, searching: false, seats: PANEL_SEATS }]),
    ...(roles.scan === false ? [] : [{ entry: models.scan, searching: false, seats: 1 }]),
    ...(auditor ? [{ entry: auditor, searching: false, seats: 1 }] : []),
  ]
  let dearest: { entry: ModelEntry; usd: number; seats: number } | null = null
  for (const { entry: e, searching, seats } of inPlay) {
    if (!e.price) return null
    // Seats at the WORST pass each, not the cheaper ones the cache really makes
    // of seats two and three: the three send a byte-identical system block, so
    // the later two are mostly reads. Leaning high is what a reserve is for.
    const usd =
      worstPassUsd(e.price, searching, e.provider, checkSlotSearches(roles.priorArt === true)) *
      seats
    // Strictly greater, so a tie keeps the EARLIER role. The list is ordered
    // reason-first, and naming the selected model where two rates are equal is
    // the answer a reader can act on — being sent to re-point a panel that costs
    // exactly what the selection costs is advice that changes nothing.
    if (!dearest || usd > dearest.usd) dearest = { entry: e, usd, seats }
  }
  return dearest
}

/**
 * The floor alone, for the three callers that only need the number.
 *
 * Expressed through `dearestPass` rather than walking the same list again: the
 * figure and the NAME are one fact, and two loops over one list is how a message
 * comes to name a model the arithmetic did not choose. That is the bug this pair
 * replaced — the toast said the ceiling could not cover a pass on the SELECTED
 * model while the floor had been set by a routed one, so a run refused because
 * of a dear scan sent the user to look at a reasoning model that was affordable.
 */
export function minCeilingForRun(
  models: RunModels,
  auditor?: ModelEntry | null,
  roles: RolesInPlay = {},
): number | null {
  return dearestPass(models, auditor, roles)?.usd ?? null
}

/**
 * The reserve, per role: what every pass is held to, and what a source check is.
 *
 * One reserve used to cover every pass, sized to the dearest — and on every
 * model this app ships the dearest is a source check, whose searches and replayed
 * pages bound it several times above a reasoning pass (about $3.49 against $0.80
 * on Opus 5.5; about $1.24 against $0.80 on GPT-5.6 Sol since D376). So a cap anywhere between the two refused the whole run, and a
 * cap just above the higher one ran one pass and stopped: every pass was held
 * back by a check's bound, whether or not a check was due. The usability study
 * found every cap up to $3.00 refused, and it was the worst money moment in it.
 *
 * An ordinary run's checks are its one kind of pass it can SKIP without changing
 * what it is: the cadence places them, and a slot with no check in it is taken
 * by a reasoning move. So an ordinary run holds two reserves. `pass` is the
 * dearest pass it can make other than a scheduled check — the reserve before
 * every pass, as before, and the smallest ceiling it can start under. `check` is
 * a check's own bound, and a check is scheduled only while the ceiling still has
 * that much left; below it the run keeps reasoning and its account says which
 * checks it skipped and why.
 *
 * The guarantee is the same one, stated per pass: nothing is started unless what
 * is left of the ceiling covers the dearest thing THAT pass could draw. It is not
 * loosened for any pass — a check is held to a check's bound exactly as it was.
 *
 * `checksDeferred` is false for the two runs whose checks are not optional: a
 * checks-only run is nothing but its checks, and an agenda's check is a queued
 * step. Both keep the check inside the one reserve, and refuse a ceiling under it.
 */
export interface CeilingFloors {
  pass: { entry: ModelEntry; usd: number; seats: number }
  /** Null when the budget schedules no check, or when checks share `pass`. */
  check: { entry: ModelEntry; usd: number } | null
}

export function ceilingFloors(
  models: RunModels,
  auditor: ModelEntry | null,
  roles: RolesInPlay,
  checksDeferred = true,
): CeilingFloors | null {
  if (!checksDeferred || roles.check === false) {
    const pass = dearestPass(models, auditor, roles)
    return pass && { pass, check: null }
  }
  const pass = dearestPass(models, auditor, { ...roles, check: false })
  const usd = minCeilingUsd(models.check, true)
  // Null when either half is unpriced, matching `dearestPass`: there is no rate
  // to hold that pass to, and the caller refuses the ceiling rather than guess.
  return pass && usd !== null ? { pass, check: { entry: models.check, usd } } : null
}

/**
 * Whether a source check fits under what is left of a ceiling, asked as `shouldStop`
 * asks the same question of every other pass: strictly under, so a run holding
 * exactly its reserve starts nothing. Exported for its test — the sentence the
 * account prints is built from the same figure this compares.
 */
export const checkFits = (drawn: number, checkUsd: number, ceiling: number | null): boolean =>
  ceiling === null || drawn + checkUsd < ceiling

/** Consecutive model PASSES that return nothing before a run stops — passes, not
 *  logical moves, because a portfolio pass is two or three moves and counting its
 *  angles let one empty pass end a twelve-move run at its first request. See
 *  `portfolioPassTally`; the report prints the real count off the record. */
const STALL_LIMIT = 3
/** Refusals before a run stops. Counted apart from stalls because they are
 *  opposite findings: one is the model declining, the other is a branch with
 *  nothing left in it. */
const REFUSAL_LIMIT = 3
/** Failures in total, NOT consecutively. A rate limit alternates fail-succeed, so
 *  a consecutive counter never fires and a run burns half its budget on billed
 *  failures. */
const FAILURE_LIMIT = 4
/** New nodes before the closing scan is worth its pass. Exported for the policy's
 *  verb-mix simulation, which holds the scan's slot back exactly when this does,
 *  and so a test can sit either side of it rather than restating the number. */
export const SCAN_FLOOR = 12

/**
 * Whether the closing scan will run, decided BEFORE the last move is spent.
 *
 * The scan is a billed pass like any other, so it comes out of the budget rather
 * than being appended past it. Without this the loop stops at move 24 of 24 and
 * then pushes a 25th, and every surface prints "25 of 24 moves" under the head
 * "The move budget ran out" — while the pass itself draws past the token ceiling
 * the button promised.
 *
 * The money clause is the same argument applied to the ceiling: the scan sits
 * AFTER the loop's stop conditions, so without it a run that stopped on a
 * spending ceiling would then buy one more pass. It reads the reserve rather than
 * the bare estimate, so the question it asks is "can the ceiling still afford a
 * pass" and not "has the ceiling been crossed yet".
 *
 * And the session clause, because the scan sitting after the stop conditions
 * cuts the other way too: a run that stopped as `replaced` still reached this
 * block, and `scanForBridges` reads the CURRENT session — so the closing scan
 * used to run against, and bill, whichever exploration had just been opened,
 * while its move row was written into the old run's record.
 */
/**
 * Whether the graph on screen is still the one this run is bound to.
 *
 * Pure and exported for its test, which pins the session clause above: the
 * truth table is three rows, and the one that must never flip back is the
 * mismatch. Null means unbound — a run that never made a pass has no latch,
 * and cannot have produced the `SCAN_FLOOR` nodes the scan needs anyway — and
 * the binding is `l.sessionId`, which latches inside a pass, after `beginOp`'s
 * demo re-mint, so the worked example's paid rename never reads as a swap.
 */
export const scanSessionKept = (boundTo: string | null, currentId: string): boolean =>
  boundTo === null || currentId === boundTo

/** Whether the closing scan will happen — which decides the move slot held for
 *  it, the reserve's scan seat and the `moves` stop.
 *
 *  On the SCAN role and not on `canCall`: the pass runs wherever that role is
 *  pointed, so asking the selection skipped the closing scan of a run whose scan
 *  route was keyed and whose selection was not, silently, and reserved a slot
 *  for it in the run where the reverse held. The third place in this file to ask
 *  the selection about a routed pass. */
/**
 * The resources a PASS spends, one expression each.
 *
 * Two readers ask about these and they were two spellings: `shouldStop` names
 * the ceiling that was reached, and the closing scan asks whether it can afford
 * to happen at all. Round twenty-four put a move clause in the scan's predicate
 * and left the other three, which is the same drift under a smaller name — so
 * the expressions live here once and both read them.
 *
 * `nodesSpent` is deliberately NOT among the four `roomForAPass` asks about: the
 * closing scan writes BRIDGES and no nodes, so a run that filled its node
 * allowance can still scan. It is here because `shouldStop` names it.
 */
const movesSpent = (l: Loop): boolean => l.moves.length >= l.budget.moves
const clockSpent = (l: Loop): boolean =>
  l.budget.ms !== undefined && Date.now() - l.startedAt >= l.budget.ms
const tokensSpent = (l: Loop): boolean => drawnTokens(l) >= l.budget.outputTokens
const nodesSpent = (l: Loop): boolean => nodeHeadroom(l.produced, l.budget) === 0
const moneySpent = (l: Loop): boolean => l.usdMax !== null && drawnUsd(l) + l.reserveUsd >= l.usdMax

/** Whether one more billed pass fits inside every ceiling this run counts. */
const roomForAPass = (l: Loop): boolean =>
  !movesSpent(l) && !clockSpent(l) && !tokensSpent(l) && !moneySpent(l)

const scanOtherwiseDue = (l: Loop): boolean =>
  l.agendaIds === null &&
  l.checkTargets === null &&
  !l.stopping &&
  l.produced >= SCAN_FLOOR &&
  // Every ceiling, not the one that was noticed. The header above has always
  // said the scan comes out of the budget instead of being appended past it,
  // and the mechanism was `shouldStop` holding a move back — which only holds
  // while `shouldStop` is the reason the loop ended. A run stopping on `tokens`
  // with moves to spare then bought one more pass and reported that its token
  // ceiling was reached while drawing past it; the same for `clock`, and the
  // same for a spent move count reached any other way. `roomForAPass` is the
  // whole list, shared with `shouldStop` so the two cannot drift.
  roomForAPass(l) &&
  scanSessionKept(l.sessionId, useStore.getState().session.id)

const scanWillRun = (l: Loop): boolean =>
  scanOtherwiseDue(l) && resolveFor(useStore.getState(), 'scan') !== null
/**
 * Claims a run wants on the graph before it hands a branch to another house.
 *
 * The same argument as `SCAN_FLOOR` and the same shape: one audit covers up to
 * `AUDIT_MAX_CLAIMS` claims at once, so an audit fired at move two examines three
 * claims and burns the pass — while `auditCandidate` can only rank what exists.
 */
const AUDIT_FLOOR = 8

/**
 * Nodes a run wants on the graph before it spends a conjecture pass.
 *
 * The audit's number and nearly its argument: a conjecture pass starts from what
 * does not fit, and a graph of a handful of fresh nodes has had no time to
 * record anything that does not — no challenge has landed a contradiction yet,
 * and no check has disputed anything. `conjectureCandidate` can only aim at
 * records that exist.
 */
export const CONJECTURE_FLOOR = 8

/** How often the loop re-tests `busy` and the stop flag while waiting. */
const IDLE_SLICE = 200

/**
 * What a run was asked to do, as the caller states it.
 *
 * The seam between a control and the loop. It exists so that the grounding depth is
 * something a run is TOLD rather than something the loop infers from a move count:
 * the sidebar fills this today, and a parser reading `/check every` fills the same
 * object tomorrow without the loop learning that commands exist.
 *
 * `moves` and `usdMax` were this function's two positional arguments and mean
 * exactly what they meant; the other three are new and all three default to what
 * the budget rows already said, so an omitted field is the old behaviour.
 */
export interface RunSpec {
  moves: number
  /** Run the explicitly queued agenda through the existing guarded controller. */
  agenda?: boolean
  /** Request packing. Absent is `DEFAULT_EXECUTION` — portfolio, since D76 —
   *  for ordinary runs. Agenda runs remain sequential unless explicitly refused
   *  for asking for an incompatible execution mode. */
  execution?: RunExecution
  /** Compact leaves are independently gated and only apply to portfolio children. */
  detail?: RunDetail
  /** Opt-in confirm gate: every pass proposed and waited on, none taken alone.
   *  Absent preserves the walk-away run. See `RunMode` and `holdForGo`. */
  mode?: RunMode
  /** A ceiling on the ESTIMATE, in dollars, or null/absent for none. */
  usdMax?: number | null
  /**
   * Source-check passes to allow, or `'every'` for as many as the cadence permits.
   *
   * `'every'` is NOT "one per move". A check adds no nodes, and `verifyCandidates`
   * only considers what this run produced and the claims above it, so back-to-back
   * checks starve for candidates — which is why `checkDue` floors its spacing at
   * every second move.
   * `'every'` resolves to that floor's true maximum, so the figure the control
   * prints is the most this run could actually spend rather than an aspiration.
   * Agenda checks can be consecutive: `'every'` permits one in each move slot.
   */
  checks?: number | 'every'
  /** Panel passes to allow. One panel is three requests — see `RunBudget.panels`. */
  panels?: number
  /** Cross-examination passes to allow, each on a model from another house. */
  audits?: number
  /**
   * Conjecture passes to allow. Absent or zero is none, and that is the default:
   * opt-in like the agenda, until the live evaluation for this arm has run
   * (D281). Each conjecture a run lands also queues a prior-art search inside
   * `checks` — see `RunBudget.conjectures`.
   */
  conjectures?: number

  /* Steering. Passed straight to `freshRunState` and never stored on the budget:
     `RunBudget` is the record of what a run was ALLOWED TO SPEND, and a verb order is
     not a ceiling. What the run was steered onto is reported from the moves it
     actually made, which is the one account a policy cannot overstate. */

  /** The verb order for this run, replacing both ring ladders. One verb is `/only`. */
  ladder?: readonly DeriveOp[]
  /** The deepest ring this run works, replacing `MAX_DEPTH`. */
  depth?: number
  /** A node id to confine the run to, with its descendants. Resolved by the caller —
   *  `/focus` means "the selected node", and the parser has no access to a selection. */
  focus?: string
  /** Stop the moment this is met. See `UntilCondition` for what may be counted. */
  until?: UntilCondition
  /**
   * The candidate synthesis to pursue, by bridge id.
   *
   * The run is confined to the two branches the candidate was fused from and to
   * the synthesis itself (`RunState.cones`), grounds the synthesis with a source
   * check before anything else (`synthesisFirst`), and — unless an `until` was
   * given — stops the moment the candidate earns the next rung a run can earn
   * (`defaultPursueUntil`). Requires a REALISED candidate: there is nothing to
   * pursue until the synthesis is on the graph, and `startRun` refuses otherwise.
   */
  pursue?: string
  /**
   * The list a person approved as one batch.
   *
   * The loop walks it in order and chooses nothing itself; `programmeSizing`
   * derives the budget from it; the confirm gate and the packet planner are both
   * off, because the approval was of exactly these steps and a packet would add
   * angles nobody approved. It ends `programme` when the list is exhausted, or on
   * any stop every other run shares. See `Programme`.
   */
  programme?: Programme
  /**
   * A CHECKS-ONLY run: one source check on each of these claims, and no reasoning
   * move, panel, audit or closing scan. Built by `checksOnlySpec`, which the thin
   * cue's press fills from `restingTargets`.
   *
   * A run rather than three presses, so the ceiling, the reserve, Stop, the ledger,
   * `endsTheRun` and the account all apply without a line of their own — the loop
   * takes these through the same verify path its scheduled checks take. It checks
   * only these, in the order `nextRestingCheck` gives, and one that drops out
   * between passes is skipped rather than replaced.
   */
  checkTargets?: readonly string[]
  /**
   * The unspent allowance of a run stopped by hand, when this run continues it —
   * see `continuationOf`. It replaces the budget row `moves` would pick, because a
   * remainder is almost never 12, 24 or 48, and the row is where the output-token
   * and node allowances come from: an absent row falls back to 24 moves, which is
   * the brand-new run a Continue press exists not to start.
   */
  rest?: Pick<RunBudget, 'moves' | 'outputTokens' | 'nodes' | 'checks'>
}

/**
 * The budget a programme implies, from the list itself.
 *
 * A run-verb step is a move, a check step is a check, and the row is the smallest
 * that carries them all — `capFor` caps the three check kinds at a quarter of the
 * moves, so a list heavy in checks needs a taller row than its move count alone
 * would. One move is held for the closing scan, which every run ends on.
 *
 * EVERY step counts toward the row, not only the reasoning ones. `shouldStop`
 * bounds `l.moves.length`, and a check, a panel and an audit each push a row onto
 * `l.moves` exactly as a reasoning move does — so a row sized from the reasoning
 * steps alone is a row the list overruns. Ten reasoning steps and two checks sized
 * to 11, took the 12 row, and then lost its twelfth approved step to the scan's
 * held slot: the run ended `moves` with a step nobody withdrew, which is the one
 * thing an approved list must not do quietly.
 *
 * The execution is sequential and the mode is auto for the reason
 * `RunSpec.programme` gives: the list is the approval.
 */
export function programmeSizing(
  p: Programme,
): Pick<RunSpec, 'moves' | 'checks' | 'panels' | 'audits' | 'execution' | 'mode'> {
  let moves = 0
  let checks = 0
  let panels = 0
  let audits = 0
  for (const step of p.steps) {
    if (step.verb === 'verify') checks++
    else if (step.verb === 'panel') panels++
    else if (step.verb === 'audit') audits++
    else moves++
  }
  // Every step, plus the one move held for the closing scan; and still tall enough
  // that `capFor`'s quarter does not clip a check kind the list actually asked for.
  const need = Math.max(moves + checks + panels + audits + 1, 4 * checks, 4 * panels, 4 * audits)
  const rows = Object.keys(RUN_BUDGETS)
    .map(Number)
    .sort((a, b) => a - b)
  const row = rows.find((r) => r >= need) ?? rows[rows.length - 1]!
  return { moves: row, checks, panels, audits, execution: 'sequential', mode: 'auto' }
}

/**
 * Does this list fit this budget IN FULL?
 *
 * `programmeAllows` asks whether a budget permits a step of some KIND at all,
 * which is the right question inside the loop and the wrong one before the
 * press: five `deepen` steps under `maxMoves: 2` each pass it, and then the run
 * takes two and stops on `moves` having discarded the approved list. The sheet
 * says "approve N steps and run"; this is what makes that sentence true.
 *
 * The tally is `programmeSizing`'s, read the same way round: sizing turns a list
 * into the budget it needs, and this asks whether a budget that has since been
 * cut by a durable evidence profile still covers the list.
 *
 * INCLUDING the closing scan's held-back move, exactly as `programmeSizing`'s
 * `+ 1` does. I left it out first, reasoning that refusing a list which fits
 * every move it asked for is the same broken promise the other way round. It is
 * not, and the asymmetry is the whole point of this guard: a refusal is on
 * screen before the press, naming the protocol, and the person can untick a step
 * or raise the ceiling. The omission it prevents happens after `onStarted` has
 * discarded the list, takes the LAST approved step, and says nothing. With
 * twelve steps under `maxMoves: 12` the eleventh pass reaches `SCAN_FLOOR`,
 * `shouldStop` stops at `moves - 1`, and the twelfth step is simply gone.
 *
 * Only where the scan could actually happen, through `scanIsReachable` — which
 * now asks about the move count as well as the node allowance, so a budget the
 * first pass exhausts holds nothing back for a scan it can never buy.
 *
 * This carried a `steps.length > 1` carve-out of its own for one round, on the
 * argument that a one-step list reaches its only step with zero passes behind it
 * so `produced` is provably zero. That reasoned about the scan STEALING a step,
 * and the scan does not sit before the last step — it sits after the loop. The
 * move clause in `scanIsReachable` is the honest version of the same relief and
 * covers every budget rather than one shape of list, so the special case is
 * gone.
 */
/**
 * How many nodes one step's pass can ADD to the graph, at most.
 *
 * Read off the loop, which is the authority: the panel arm and the audit arm
 * both end `l.produced += made.length`, because a panel can admit a dissent and
 * an audit an objection. Only `verify` writes a reading onto a claim that
 * already exists and produces nothing, which is why it is the one that yields
 * zero.
 *
 * The three figures are not interchangeable, and reading them as one is what
 * round forty-two found. A panel is one: `runPanel` admits at most one dissent.
 * But an audit is not a pass over a NODE, it is a pass over a BRANCH —
 * `auditTargets` walks the target's descendants up to `AUDIT_MAX_CLAIMS`, and
 * every claim it hands over can come back with an objection that lands — so one
 * audit step can produce twelve nodes, which is `SCAN_FLOOR` exactly. Counting
 * each step as a single node therefore said three audits could not reach the
 * scan when one of them can, which is the under-count this predicate exists to
 * avoid.
 *
 * A reasoning verb is `Infinity` rather than a figure because nothing bounds it
 * here: `NODES_JSON` sets no `maxItems` and "3–4 children" is prose, not a rule,
 * so the budget's own allowance is the only ceiling.
 *
 * Exhaustive with no `default`, for the reason `endsTheRun` is: a check verb
 * added to `ProgrammeStep['verb']` later stops the build until somebody says
 * what it yields, rather than inheriting a number that is wrong in one
 * direction. The two directions are not symmetric. Under-counting skips the
 * scan's key and price at pre-flight and leaves `programmeFits` holding no move
 * for it, so a run that then reaches `SCAN_FLOOR` spends its whole approved list
 * and ends `unreachable`, or takes a scan no ceiling reserved for.
 * Over-counting costs one move of headroom.
 */
/**
 * The claims one audit step can be HANDED, as far as a pre-flight can soundly
 * know.
 *
 * `runAudit` walks the branch and then drops the claims the auditor itself
 * wrote — a model cannot cross-examine its own work — so the branch is the
 * outer figure and `auditableBy` is the one the pass runs on. Applying the
 * exclusion here is worth a rule of its own because it is only SOUND while the
 * auditor cannot move under it, and a bound that shrinks on an answer the pass
 * can contradict is the under-count that ends a run `unreachable`.
 *
 * `branchAuthor` is what decides that, and it answers in two ways. With a
 * STAMPED root it returns `root.model.provider` and stops — the root does not
 * change during a run, so the house being examined is fixed, `resolveAuditor`
 * reads only keys and the selection, and the auditor is settled before the first
 * pass. Every claim excluded now stays excluded, and the nodes the run adds are
 * already covered by the caller's headroom.
 *
 * With an UNSTAMPED root it falls through to a plurality over the branch, and a
 * plurality moves: a panel's dissent is stamped with the selection's house and
 * an audit's objections with the auditor's, so a programme can shift the vote
 * under itself, `auditorFor` can then name a different house, and claims that
 * were excluded become auditable. So the exclusion is skipped there and the
 * whole branch is counted — the safe direction, and the case it costs nothing
 * in, because a plurality auditor is by construction not the plurality author
 * and therefore excludes only the minority.
 *
 * An auditor that will not resolve at all counts the whole branch too. That run
 * is refused before it starts — `startRun` names the step whose branch has no
 * other keyed house — so tightening on it would be a bound doing another gate's
 * work with less information.
 */
function auditableTargets(s: ScanView, rootId: string): ReasonNode[] {
  const branch = auditTargets(s.session, rootId)
  const auditor = fixedAuditor(s, rootId)
  return auditor ? auditableBy(branch, auditor) : branch
}

/**
 * The provider an audit of this branch will run on, WHERE THAT CANNOT MOVE.
 *
 * `branchAuthor` returns a stamped root's own provider and stops, and the root
 * does not change during a run, so the house is settled before the first pass.
 * With an unstamped root it falls through to a plurality the list can shift
 * under itself, and `null` here is what stops every caller tightening on an
 * answer the pass can contradict. `null` also covers an auditor that will not
 * resolve at all — that run is refused before it starts, so a bound must not do
 * another gate's work with less information.
 */
function fixedAuditor(s: ScanView, rootId: string): ProviderId | null {
  if (!s.session.nodes[rootId]?.model) return null
  return auditorFor(s, rootId)?.entry.provider ?? null
}

/**
 * Where the PANEL role points, for the same reason `fixedAuditor` exists: a
 * panel's dissent carries this model's stamp, and `auditableBy` drops it from an
 * audit run by the same house. The role does not move mid-run.
 */
const panelProvider = (s: ScanView): ProviderId | null =>
  resolveFor(s, 'panel')?.entry.provider ?? null

/**
 * What `programmeScanInPlay` needs to know beyond the list and the budget.
 *
 * The audit resolution view plus `roleModels`, because the bound has to ask
 * where the PANEL role points as well as where an audit's own house lands: both
 * stamp the nodes they make, and `auditableBy` drops whichever of them the
 * auditor wrote.
 */
type ScanView = Parameters<typeof auditorFor>[0] & Pick<RouteView, 'roleModels'>

/**
 * Is `id` this node or one of its descendants?
 *
 * The `seen` guard is not for a graph this codec allows — `validateTopology`
 * forbids a cycle at both doors — but a walk that can hang is a worse failure
 * than one extra Set, and this runs on a hand-edited document's steps.
 */
const underNode = (id: string, ancestorId: string, nodes: Record<string, ReasonNode>): boolean => {
  let at: string | null | undefined = id
  const seen = new Set<string>()
  while (at && !seen.has(at)) {
    if (at === ancestorId) return true
    seen.add(at)
    at = nodes[at]?.parentId
  }
  return false
}

/**
 * Can what one step LANDS end up inside another step's audit branch?
 *
 * Every pass attaches what it makes under its own target, but the two that
 * produce do not attach at the same DEPTH, and reading them as one is round
 * forty-six's finding.
 *
 * A panel's dissent is a child of the claim it read — one node, one place. So it
 * falls inside a walk from `rootId` only when the panel's own target already
 * does: `underNode(panel target, root)`. A panel on an ANCESTOR of the root
 * lands a SIBLING of it, which no walk from the root will ever reach, and the
 * symmetric test counted it anyway.
 *
 * An audit's objections are children of the claims it was HANDED, and it is
 * handed a whole branch. So they can fall inside the root's branch whenever the
 * two branches meet at all — and two subtrees of a tree meet only when one root
 * is inside the other, which is why this arm is the symmetric one.
 *
 * `verify` produces nothing and a reasoning verb is unbounded and returns before
 * this is ever consulted, so the two arms are the whole of it.
 *
 * Still an over-answer within the audit arm, deliberately: overlap is read as
 * every one of that step's objections arriving, when only the ones whose claim
 * falls inside the root's branch do. Tightening past that would mean predicting
 * WHICH claim each objection lands under, which is the model's choice and not a
 * bound.
 */
/**
 * How many of one step's nodes can land INSIDE another step's audit branch.
 *
 * A count, where this was an overlap test for two rounds, and the count is
 * neither harder to compute nor harder to state: every pass attaches what it
 * makes under a claim it was HANDED, and which claims those are is known before
 * the run. So the question is not which claim a model will choose — that would
 * be predicting its answer — but how many of the claims it could choose from
 * fall inside the branch we are asking about.
 *
 * A PANEL is handed one claim, its target, and lands at most one dissent under
 * it: one if the target is inside, none otherwise.
 *
 * An AUDIT is handed `auditableTargets`, a whole branch, and lands at most one
 * objection under each. So its contribution is the size of the INTERSECTION of
 * that set with this branch — plus whatever earlier steps put inside this branch
 * first, since those become claims it can be handed too. That recursion is what
 * keeps this an upper bound rather than a guess; `inflow` carries it.
 *
 * Zero where the auditor cannot examine the nodes at all, which is the exclusion
 * `auditableTargets` applies to its own branch, applied here to the source: a
 * prior audit on the same house, or a panel whose role points there.
 */
function landsInside(
  prior: ProgrammeStep,
  made: number,
  inflow: number,
  rootId: string,
  auditor: ProviderId | null,
  s: ScanView,
): number {
  if (isRunVerb(prior.verb)) return Infinity
  if (prior.verb === 'verify') return 0
  if (prior.verb === 'panel') {
    if (auditor && panelProvider(s) === auditor) return 0
    return underNode(prior.nodeId, rootId, s.session.nodes) ? 1 : 0
  }
  if (auditor && fixedAuditor(s, prior.nodeId) === auditor) return 0
  const handed = auditableTargets(s, prior.nodeId).filter((n) =>
    underNode(n.id, rootId, s.session.nodes),
  ).length
  // `inflow` counts what earlier steps left inside THIS branch, and a prior
  // audit can only have been handed one of those if its own branch reaches
  // here. Two disjoint branches share no node, so `auditTargets` never offers
  // them and the objections credited by that term cannot be made — the bound
  // came out above what the programme can produce, and a scan it then reserved
  // and priced could refuse a run that was perfectly able to go.
  const overlaps =
    underNode(prior.nodeId, rootId, s.session.nodes) ||
    underNode(rootId, prior.nodeId, s.session.nodes)
  return Math.min(made, handed + (overlaps ? inflow : 0))
}

const nodeYield = (step: ProgrammeStep, s: ScanView, produced: number): number => {
  if (isRunVerb(step.verb)) return Infinity
  switch (step.verb) {
    case 'verify':
      return 0
    // A step whose node has left the graph produces nothing, for the reason
    // `programmeAuditors` omits one: `nextProgrammeStep` skips it, and a step
    // that will not run must not raise a floor or refuse a run.
    case 'panel':
      return s.session.nodes[step.nodeId] ? 1 : 0
    case 'audit':
      return Math.min(AUDIT_MAX_CLAIMS, auditableTargets(s, step.nodeId).length + produced)
  }
}

/**
 * Will a closing scan happen for THIS list, under this budget?
 *
 * `scanIsReachable` is a question about the budget — enough node allowance and a
 * move to spend. It cannot see that a list of `verify` steps produces no nodes
 * at all, and `scanWillRun` needs `l.produced >= SCAN_FLOOR`, so a check-only
 * programme can never reach the scan however tall its budget is. Asking the
 * budget alone therefore held a move for a pass that cannot fire, and refused
 * the whole run over a scan key nothing would have used.
 *
 * The step half was `reasonWillRun`'s test for one round, and that was the wrong
 * question wearing the right answer: it asks whether the list makes a REASONING
 * pass, which is what the reason ROLE needs to know. The two agree on a
 * verify-only list and part company on a panel or an audit — both of which
 * reason not at all and produce nodes anyway. `nodeYield` asks the question this
 * predicate is actually about, and asks it in NODES rather than in steps,
 * because one audit is worth twelve of them.
 */
export function programmeScanInPlay(
  budget: RunBudget,
  steps: readonly ProgrammeStep[],
  s: ScanView,
): boolean {
  if (!scanIsReachable(budget)) return false
  // Counted rather than answered with a flat false, because the bound is real in
  // BOTH directions: enough panels and audits genuinely reach the floor — one
  // audit on a tall enough branch does — and round thirty-one's finding was that
  // treating them as barren costs a run. A verify-only list is the case that
  // stays out at any length, and it is why the budget alone was never the whole
  // question.
  //
  // Each audit is given the HEADROOM the steps before it left, because the graph
  // does not stand still for the length of a list: their nodes can land inside
  // this audit's branch, so a bound read off the graph as it is now would be
  // smaller than the branch the pass will walk. Two panels then an audit of
  // their target is the small case; two audits on one root is the sharp one,
  // since the first lands its objections as children of the very claims the
  // second will be handed.
  //
  // The headroom is per BRANCH and not the running total, which is round
  // forty-five's finding. A panel somewhere else on the graph produces a node
  // that no walk from this audit's root will ever reach, so counting it into the
  // audit's ceiling AND into the total said four panels and an audit of five
  // could make thirteen when nine is the most they can. Over-counting is the
  // cheap direction only in the sense that it is safe; it still refuses a
  // runnable list over a scan key nothing would use, which is the whole reason
  // this predicate exists.
  const made: number[] = []
  let total = 0
  for (const step of steps) {
    let headroom = 0
    if (step.verb === 'audit') {
      // The auditor the exclusions are computed against, and only where it
      // cannot move — the guard `auditableTargets` explains. `null` means it was
      // unresolvable or the root is unstamped, and then nothing is excluded,
      // which is the safe direction.
      const mine = fixedAuditor(s, step.nodeId)
      // Forward, because a step's own inflow is what the ones before it left:
      // an audit can be handed claims an earlier pass put in this branch, and
      // then land objections under those. Walking backwards would lose that.
      for (const [i, prior] of steps.slice(0, made.length).entries()) {
        headroom += landsInside(prior, made[i]!, headroom, step.nodeId, mine, s)
      }
    }
    const yielded = nodeYield(step, s, headroom)
    made.push(yielded)
    total += yielded
    if (total >= SCAN_FLOOR) return true
  }
  return false
}

export function programmeFits(
  budget: RunBudget,
  steps: readonly ProgrammeStep[],
  s: ScanView,
): boolean {
  let checks = 0
  let panels = 0
  let audits = 0
  for (const step of steps) {
    if (step.verb === 'verify') checks++
    else if (step.verb === 'panel') panels++
    else if (step.verb === 'audit') audits++
  }
  return (
    steps.length + (programmeScanInPlay(budget, steps, s) ? 1 : 0) <= budget.moves &&
    checks <= budget.checks &&
    panels <= (budget.panels ?? 0) &&
    audits <= (budget.audits ?? 0)
  )
}

/**
 * The auditor each audit step in a programme would actually run on.
 *
 * An audit is resolved against the TARGET BRANCH's author and not against the
 * selected model — `auditorFor` is `runAudit`'s own resolution — so one
 * programme's audits can land on several different houses, and on none that the
 * run's own selection would have named. Asking `resolveAuditor` once against the
 * reasoning provider therefore answered a different question than the passes ask:
 * it could reserve for a model no step will use, refuse a programme every step of
 * which is keyed, and let a run cross a ceiling on a dearer house than the
 * reserve ever covered.
 *
 * Steps whose node has left the graph are omitted, for the reason
 * `nextProgrammeStep` skips them: a step that will not run must not refuse a run
 * or raise its floor.
 */
export function programmeAuditors(
  s: Parameters<typeof auditorFor>[0],
  steps: readonly ProgrammeStep[],
): Array<{ nodeId: string; auditor: ResolvedModel | null }> {
  const out: Array<{ nodeId: string; auditor: ResolvedModel | null }> = []
  for (const step of steps) {
    if (step.verb !== 'audit') continue
    if (!s.session.nodes[step.nodeId]) continue
    out.push({ nodeId: step.nodeId, auditor: auditorFor(s, step.nodeId) })
  }
  return out
}

/**
 * Whether this run will make an audit AT ALL.
 *
 * `allowed` is the audit count off the RESOLVED budget and never off the
 * request: `budgetForEvidenceProfile` runs first, and a durable profile can take
 * the allowance to zero after the person asked for one. Refusing a run for a key
 * that an audit which cannot happen would have needed is the same wrong answer
 * `scanIsReachable` exists to prevent for the closing scan, and under a ceiling
 * it also refused over an unpriced auditor no pass would ever reach.
 *
 * `steps` is a programme's live audit steps, or null for every other run. A
 * programme with the allowance but no surviving audit step makes no audit
 * either — the targets can be pruned between the sheet and the press.
 */
export function auditWillRun(
  allowed: number,
  steps: ReadonlyArray<{ auditor: ResolvedModel | null }> | null,
): boolean {
  return allowed > 0 && (steps ? steps.length > 0 : true)
}

/**
 * The one of those the reserve has to cover.
 *
 * The ceiling's guarantee is about the dearest pass the run CAN make, so a
 * programme reaching three houses is floored at the dearest of the three rather
 * than at whichever one its first audit happens to hit. The loop re-resolves per
 * target before each audit and the reserve only ever grows, so this is the upper
 * bound the run starts under and never the last word.
 *
 * An unpriced auditor wins outright: it sinks the floor to "no figure", which is
 * the honest answer, and under a ceiling the caller refuses on exactly this.
 */
export function dearestAuditor(
  steps: ReadonlyArray<{ auditor: ResolvedModel | null }>,
): ResolvedModel | null {
  let best: ResolvedModel | null = null
  let bestUsd = -1
  for (const { auditor } of steps) {
    if (!auditor) continue
    if (!auditor.entry.price) return auditor
    const usd = minCeilingUsd(auditor.entry) ?? 0
    if (usd > bestUsd) {
      bestUsd = usd
      best = auditor
    }
  }
  return best
}

/**
 * The next approved step whose node is still on the graph, from `from`.
 *
 * A step naming a pruned node is SKIPPED, never substituted — a substitute is a
 * choice nobody approved — and a `verify` step on the seed is skipped for the
 * reason `runVerify` refuses it: the seed is the question, not a claim. Returns
 * the index too, so the loop can advance past the skipped steps without
 * re-walking them.
 *
 * `allows` is the BUDGET, asked per step. A durable evidence profile can cut the
 * budget below what the approved list assumed — `maxMoves: 1` takes the check
 * allowance to zero — and a step the budget does not cover must be skipped here
 * rather than dispatched: the pre-flight resolves no route for a pass the budget
 * says will not happen, so dispatching it anyway means a pass with no key, no op
 * id, and a step the loop would offer again forever. It is also the arithmetic
 * the reserve was sized against, and a pass outside it runs outside the ceiling.
 */
export function nextProgrammeStep(
  p: Programme,
  from: number,
  nodes: Record<string, ReasonNode>,
  allows: (verb: ProgrammeStep['verb']) => boolean = () => true,
): { step: ProgrammeStep; index: number } | null {
  for (let i = from; i < p.steps.length; i++) {
    const step = p.steps[i]!
    const n = nodes[step.nodeId]
    if (!n) continue
    if (step.verb === 'verify' && n.parentId === null) continue
    if (!allows(step.verb)) continue
    return { step, index: i }
  }
  return null
}

/** Whether a programme step is a reasoning verb rather than one of the three checks. */
const isRunVerb = (v: ProgrammeStep['verb']): v is RunVerb =>
  v !== 'verify' && v !== 'panel' && v !== 'audit'

/**
 * Whether this run will make a REASONING pass at all.
 *
 * Every run but one: a programme is a list somebody approved, and a person can
 * untick every reasoning step and approve the checks that are left. `startRun`
 * demanded the reasoning route's key unconditionally, so an approved list of
 * nothing but source checks was refused over a model it would never call — a
 * routed role with no key must refuse its OWN pass, and must not refuse
 * somebody else's.
 */
/**
 * Whether the check route can make a source check AT ALL.
 *
 * A key is not the whole question. `runVerify` also refuses a model with no
 * server-side search while "Prefer my sources" is off, and refuses it BEFORE
 * `beginOp` — so no op id comes back, `checksRun` never advances, and for a
 * policy-chosen move `consumeStep` is a no-op. `checkDue` then picks the same
 * impossible check on the next turn of the loop, and the next, forever. A
 * missing key had that shape once and was caught behind the confirm gate; a
 * searchless route has it on every run, including the walk-away one, and after
 * the reasoning passes have already been paid for.
 *
 * The refusal is `runVerify`'s own, asked one layer up so it can be answered
 * before anything is spent rather than at the first check. Nothing is
 * substituted: invariant 4 is why a routed role that cannot do its job refuses
 * instead of borrowing a model that can.
 */
export function checkCanRun(s: RoutingState & { preferCorpus: boolean }): boolean {
  const m = resolveFor(s, 'check')
  if (!m) return false
  return m.entry.search !== 'none' || s.preferCorpus
}

export function reasonWillRun(programme: Programme | undefined): boolean {
  return !programme || programme.steps.some((step) => isRunVerb(step.verb))
}

/**
 * The roles a run's LIVENESS is tested against between passes.
 *
 * The loop ends `unreachable` when the model it needs has gone — a key removed
 * mid-run — and that test was `canCall`, the SELECTED model. Right for every run
 * that reasons and wrong for the one that does not: a check-only programme's
 * engine is its check route, so the selection's key decided whether an approved
 * list could run at all. It started, `onStarted` discarded the paid draft, and
 * the first iteration ended `unreachable` before a single check ran — the list
 * gone and nothing done.
 *
 * EVERY role the list reaches, not just the first one. This returned `['reason']`
 * and stopped for any list containing a reasoning step, so a MIXED programme
 * whose check key was removed after pre-flight stayed "reachable" on its
 * reasoning route while every `verify` refused quietly, was consumed by
 * `consumeStep`, and left the run to finish as `programme` — reporting that it
 * walked the approved list while silently omitting part of it. An approved batch
 * is approved whole; doing eight of ten and calling it done is the one report a
 * programme run must not make.
 *
 * The auditor is left out because it is resolved per target rather than as a
 * role: `programmeAuditors` validated every target before the run started, and
 * its own pass refuses for itself. The scan role is out for a different reason —
 * `scanWillRun` resolves it itself and a missing one skips the closing scan
 * rather than ending the run.
 *
 * `budget` is the RESOLVED one, after `budgetForEvidenceProfile`. A durable
 * profile can take the check or panel allowance to zero, and `nextProgrammeStep`
 * then skips those steps — so a role kept here for a pass that cannot happen
 * ends the run `unreachable` on its first iteration, after `onStarted` has
 * already discarded the paid list. The same question the pre-flight and the
 * sheet each had to be taught: ask what the run WILL do, never what was asked
 * for. Absent means every role is in play, which is what an unconditioned caller
 * means.
 */
export function liveRolesFor(
  programme: Programme | undefined,
  budget?: Pick<RunBudget, 'checks' | 'panels'>,
): Role[] {
  const roles = new Set<Role>()
  if (reasonWillRun(programme)) roles.add('reason')
  const checks = budget ? budget.checks : 1
  const panels = budget ? (budget.panels ?? 0) : 1
  for (const step of programme?.steps ?? []) {
    if (step.verb === 'verify' && checks > 0) roles.add('check')
    if (step.verb === 'panel' && panels > 0) roles.add('panel')
  }
  return [...roles]
}

/**
 * Whether EVERY one of them still resolves.
 *
 * `every` and not `some`, which is what this was. A run that can still make SOME
 * of its passes is not a run that can do what was approved: the ones it cannot
 * make refuse quietly, are consumed by `consumeStep`, and the run ends reporting
 * that it walked the list. Stopping is the honest outcome, and it is the reading
 * invariant 4 already takes of a routed role with no key — refuse, rather than
 * quietly serve the pass from somewhere else or not at all.
 *
 * An empty set — a programme of nothing but cross-examinations — is not a
 * liveness question this can answer, so it passes and each audit's own guard
 * refuses for itself.
 */
export function runIsReachable(
  s: Parameters<typeof resolveFor>[0],
  roles: readonly Role[],
): boolean {
  return roles.every((role) => resolveFor(s, role) !== null)
}

/**
 * The rung a pursue run stops on when nobody named one: the first rung above
 * what the candidate has already earned that THIS run can earn — `attacked` by a
 * challenge, `cross-examined` by an audit when audits are budgeted, `checked` by
 * a source check when checks are. `searched` and `realised` are not in the list:
 * no run verb runs a link scan or realises a synthesis. Null when nothing above
 * is reachable, and the run then ends on its budget like any other.
 */
/**
 * The dials, as a spec — every field of them, which is the point.
 *
 * `RunTune` is what the person configured for the next run, and each door that
 * starts one has to turn it into a `RunSpec`. The pursue button wrote its own
 * shorter version and forwarded the four counts and the mode, so sequential
 * execution, progressive detail, a custom ladder or depth and — worst — a stop
 * condition somebody typed were dropped on the floor without a word. `startRun`
 * honours an explicit `until` and never replaces it, so the only reason a pursue
 * run always stopped on the derived rung is that no condition ever reached it.
 *
 * The pairing rule is why this is a function and not a spread. Compact and
 * progressive leaves are portfolio children only, so `detail` is forced to
 * `full` under any other execution — a rule that lived in exactly one inline
 * expression, which is how a second copy came to be missing it.
 *
 * `focus`, `pursue`, `programme` and `usdMax` are the caller's own: they say
 * what this run is, not how the dials are set.
 */
export function specFromTune(tune: RunTune): RunSpec {
  const execution = tune.execution ?? DEFAULT_EXECUTION
  return {
    moves: tune.moves,
    checks: tune.checks,
    panels: tune.panels,
    audits: tune.audits,
    ...(tune.conjectures ? { conjectures: tune.conjectures } : {}),
    execution,
    detail: execution === 'portfolio' ? (tune.detail ?? 'full') : 'full',
    ...(tune.mode !== undefined ? { mode: tune.mode } : {}),
    ...(tune.ladder?.length ? { ladder: tune.ladder } : {}),
    ...(tune.depth !== undefined ? { depth: tune.depth } : {}),
    ...(tune.until ? { until: tune.until } : {}),
  }
}

export function defaultPursueUntil(
  earned: readonly BridgeRung[],
  spec: Pick<RunSpec, 'checks' | 'audits'>,
): UntilCondition | undefined {
  const reachable: BridgeRung[] = [
    'attacked',
    ...((spec.audits ?? 0) > 0 ? (['cross-examined'] as const) : []),
    ...(spec.checks !== 0 ? (['checked'] as const) : []),
  ]
  const rung = reachable.find((r) => !earned.includes(r))
  return rung ? { kind: 'rung', n: 1, rung } : undefined
}

/**
 * How many candidates have earned a rung, live — the one fact a `rung` condition
 * counts. Read off `bridgeStanding`, so off records and never off a score, and
 * confined to the pursued candidate on a pursue run, because "the candidate you
 * are pursuing earned checked" is the sentence that run promised. `earned` rather
 * than the contiguous rung, deliberately: a pursue run cannot run the link scan
 * a realised-but-unsearched candidate is missing, and a stop it could never reach
 * is not a stop.
 */
export function candidatesAtRung(s: Session, rung: BridgeRung, only: string | null): number {
  let n = 0
  for (const b of s.bridges) {
    if (only !== null && b.id !== only) continue
    const st = bridgeStanding(b, s.nodes)
    if (st.stop === null && st.earned.includes(rung)) n++
  }
  return n
}

/**
 * `spec.checks` as a number the budget can carry.
 *
 * Exported because the sidebar has to print the same figure the loop will enforce,
 * and two places deriving "every" independently is how a control comes to promise a
 * number of live web passes the run does not make.
 */
const resolveChecks = (moves: number, checks: RunSpec['checks'], fallback: number): number =>
  checks === undefined
    ? fallback
    : checks === 'every'
      ? Math.ceil(moves / 2)
      : Math.max(0, Math.trunc(checks))

/**
 * The ceilings a spec resolves to, as the run will actually enforce them.
 *
 * Exported and used by BOTH the control and the loop, which is the whole point: the
 * tune panel prints a pass count, a live-web-request count and a dollar estimate off
 * this object, and the loop gates on it. Built in two places instead, the sentence
 * under the button would drift from what the run does — and that sentence is this
 * app's stated posture that a control says what it is about to spend.
 */
/**
 * The most scheduled passes a move budget can actually fire.
 *
 * `scheduledDue` puts pass k at the midpoint of its slot, `(k − ½)·moves/cap`, so the
 * last one comes due at `moves − ½·moves/cap`. The loop reaches `moves − 2` at worst,
 * because `shouldStop` ends it at `moves` and the closing scan holds one back — so
 * every slot lands on a move that happens exactly while `½·moves/cap ≥ 2`, which is
 * `cap ≤ moves/4`. Derived, not chosen, and `run.test.ts` pins it against the true
 * figure found by walking the schedule for every budget row.
 *
 * Clamping here rather than letting the scheduler quietly drop the overflow is the
 * rule the rest of this file follows about its own limits: a budget that promised
 * twelve panels on a twelve-move run and fired three would overstate its own coverage
 * in the one direction nothing downstream could check.
 */
const capFor = (moves: number): number => Math.max(0, Math.floor(moves / 4))

export function budgetFor(spec: RunSpec, ceiling: number | null = null): RunBudget {
  const listed = spec.checkTargets?.length ?? 0
  // A checks-only run is sized by its list: one move and one check per named claim,
  // and a move's output allowance each. The node allowance is the ordinary one — a
  // check adds none, and a zero would read as spent and stop the run on `size`.
  const row = listed
    ? { moves: listed, outputTokens: listed * TOKENS_PER_MOVE, nodes: MAX_NODES, checks: listed }
    : (spec.rest ?? RUN_BUDGETS[spec.moves] ?? RUN_BUDGETS[24]!)
  const cap = capFor(row.moves)
  const fit = (n: number) => Math.min(cap, Math.max(0, Math.trunc(n)))
  return {
    // Spread, never mutated: `RUN_BUDGETS` rows are shared by every run in the
    // session, and writing a ceiling into one would give it to the next run too.
    ...row,
    // After the spread, so an asked-for depth replaces the row's default rather than
    // the row overwriting what the caller asked for.
    checks: listed
      ? listed
      : spec.agenda && spec.checks === 'every'
        ? row.moves
        : resolveChecks(row.moves, spec.checks, row.checks),
    ...(ceiling !== null ? { usd: ceiling } : {}),
    // Present-or-absent, matching how the codec reads them back: a run allowed none
    // records that it was offered none, which is a different fact from a build that
    // could not offer any.
    ...(spec.panels !== undefined ? { panels: fit(spec.panels) } : {}),
    ...(spec.audits !== undefined ? { audits: fit(spec.audits) } : {}),
    ...(spec.conjectures !== undefined ? { conjectures: fit(spec.conjectures) } : {}),
    ...(spec.until ? { until: spec.until } : {}),
  }
}

/**
 * The spec for a checks-only run over these claims, under this ceiling.
 *
 * One place, so the press that prices it and the press that starts it cannot size
 * two different runs. Sequential and auto, because each pass is one check on one
 * claim and a confirm gate over a list the person has just approved by pressing it
 * would ask the same question three times.
 */
export function checksOnlySpec(targets: readonly string[], usdMax: number | null): RunSpec {
  return {
    moves: targets.length,
    checks: targets.length,
    checkTargets: [...targets],
    execution: 'sequential',
    mode: 'auto',
    usdMax,
  }
}

/**
 * Whether a stored run was a checks-only run.
 *
 * Read off the budget rather than a field of its own, so no record needs a codec
 * change: an ordinary run's budget spaces its checks and allows fewer than its moves
 * (`budgetForEvidenceProfile`), an agenda says so on the record, and only a
 * checks-only run is allowed a check on every move.
 */
export const checksOnlyRun = (run: Pick<Run, 'agenda' | 'budget'>): boolean =>
  !run.agenda && run.budget.moves > 0 && run.budget.checks >= run.budget.moves

/**
 * Whether a run's ending leaves its unspent allowance for a press to pick up.
 *
 * `stopped` is the person, so the rest is theirs to spend. `unreachable` is the
 * provider (D337): a rate limit that outlasted the waits, an outage, an empty
 * account, a key the provider rejected — and none of those is a judgment the run
 * reached about the graph, so continuing one overrules nothing. Only where the
 * record shows it: the last pass the loop itself took FAILED, by the record's own
 * reading of a failed pass (`failedPassesFor` — an error row, or a portfolio pass
 * that answered with nothing usable, as the loop counted it toward
 * `FAILURE_LIMIT`) — outcomes, never an error's English (invariant 7). The same
 * word also closes a run whose key went away between moves, whose pick refused
 * before it started (`unstarted`), or whose closing scan lost its route after the
 * loop had already stopped on something else — and that last one REWRITES an
 * earlier ending, a spent token ceiling or a stall, which a continuation would
 * then overrule. None of those leaves a failed pass last, so the predicate keeps
 * them all where they were. The closing scan is not the loop's move (`kind:
 * 'bridge'` is only ever that scan here), so a scan that ran after the failures
 * does not hide them.
 *
 * Every other ending is the run's own: a budget spent, a ceiling reached, a
 * frontier emptied, a streak of nothing, a model that declined. Offering to
 * "continue" one of those would be offering to overrule the stop condition that
 * ended it, which is exactly the decision invariant 6 keeps away from anything
 * but a resource, a predicate or the person (D120).
 */
function endingLeavesRest(run: Run): boolean {
  if (run.stop === 'stopped') return true
  if (run.stop !== 'unreachable') return false
  const last = run.moves.findLast((m) => m.kind !== 'bridge')
  return last !== undefined && failedPassesFor(run.moves).includes(last.opId)
}

/**
 * What a run stopped by hand, or by a provider that stopped answering, left
 * unspent, as a spec a new run can start on — or null when there is nothing to
 * continue. Which endings qualify is `endingLeavesRest`.
 *
 * Everything is a REMAINDER, read off the stored record and nothing else: moves
 * are logical moves (D47, D74), nodes are the ones this run added, the grounding
 * passes are what the schedule had not yet fired, and the ceiling is what is left
 * of the one the ended run was given. Not that ceiling again — a $5 cap stopped
 * at $3 and continued under a fresh $5 could draw $8 across what the person reads
 * as one run, and a ceiling that holds only per press is the tripwire this file
 * refuses to call a ceiling. A ceiling with nothing left continues nothing, rather
 * than handing `startRun` a zero it reads as "no ceiling". A failed move is a
 * move the run spent, as it was while the run was live: its budget counted it,
 * and the remainder does too.
 *
 * Still a press, after either ending: nothing here retries on its own, and an
 * account with no credits simply fails again — under the cause line the strip
 * already prints (`unreachableWhy`) — until somebody tops it up.
 *
 * The steering — execution, ladder, depth, ask-first — is not on the record (see
 * `RunSpec`), so the caller supplies it from the dials, which are the ones the
 * ended run was started under unless somebody has since moved them. Lens and
 * temper are the session's, and a run never carried its own.
 */
export function continuationOf(run: Run): RunSpec | null {
  if (!endingLeavesRest(run) || run.finishedAt === undefined || run.agenda) return null
  // A checks-only run's named claims are not on the record, and its remainder spent
  // as an ordinary run would buy reasoning nobody pressed for. Its press is still
  // there, and pressing it again re-ranks and skips what this one checked.
  if (checksOnlyRun(run)) return null
  const moves = run.budget.moves - run.moves.length
  const nodes = run.budget.nodes - run.produced
  if (moves < 1 || nodes < 1) return null
  const usd =
    run.budget.usd === undefined ? null : run.budget.usd - (run.estimatedUsd ?? run.budget.usd)
  if (usd !== null && usd <= 0) return null
  const fired = (kind: LogicalOpKind) => run.moves.filter((m) => m.kind === kind).length
  const left = (allowed: number | undefined, kind: LogicalOpKind) =>
    allowed === undefined ? undefined : Math.max(0, allowed - fired(kind))
  const checks = Math.max(0, run.budget.checks - fired('verify'))
  const panels = left(run.budget.panels, 'panel')
  const audits = left(run.budget.audits, 'audit')
  return {
    moves,
    checks,
    ...(panels !== undefined ? { panels } : {}),
    ...(audits !== undefined ? { audits } : {}),
    ...(run.budget.until ? { until: run.budget.until } : {}),
    usdMax: usd,
    rest: { moves, outputTokens: moves * TOKENS_PER_MOVE, nodes, checks },
  }
}

/** The stricter of the one-off ceiling and the durable evidence constitution. */
export function evidenceProfileCeiling(
  requested: number | null | undefined,
  profile: EvidenceRuleProfile,
): number | null {
  const user = typeof requested === 'number' && requested > 0 ? requested : null
  const declared = normalizeEvidenceProfile(profile).budget.maxEstimatedUsd
  if (user === null) return declared
  if (declared === null) return user
  return Math.min(user, declared)
}

/**
 * Apply a durable evidence constitution before route checks or projections.
 * Every derived cadence is reduced with the move count, so a two-move protocol
 * cannot keep promising checks and panels that only a 24-move schedule reaches.
 */
export function budgetForEvidenceProfile(
  spec: RunSpec,
  profile: EvidenceRuleProfile,
  ceiling: number | null = evidenceProfileCeiling(spec.usdMax, profile),
): RunBudget {
  const normalized = normalizeEvidenceProfile(profile)
  const base = budgetFor(spec, ceiling)
  const moves = Math.min(base.moves, normalized.budget.maxMoves ?? base.moves)
  const scheduledCap = capFor(moves)
  // An agenda and a checks-only run may check on every move; an ordinary run spaces
  // its checks, and its budget can never allow as many as it has moves — which is
  // what `checksOnlyRun` reads a stored record by.
  const checkCap =
    spec.agenda || spec.checkTargets?.length ? moves : Math.max(0, Math.floor((moves - 1) / 2))
  return {
    ...base,
    moves,
    outputTokens: Math.min(base.outputTokens, moves * TOKENS_PER_MOVE),
    checks: Math.min(base.checks, checkCap),
    ...(base.panels !== undefined ? { panels: Math.min(base.panels, scheduledCap) } : {}),
    ...(base.audits !== undefined ? { audits: Math.min(base.audits, scheduledCap) } : {}),
    ...(base.conjectures !== undefined
      ? { conjectures: Math.min(base.conjectures, scheduledCap) }
      : {}),
    ...(normalized.budget.maxElapsedMinutes !== null
      ? { ms: normalized.budget.maxElapsedMinutes * 60_000 }
      : {}),
  }
}

export interface EvidenceProfileStoppingFacts {
  supported: number
  refuted: number
  /** Undefined means nobody recorded a structured signal. */
  decisionCriterionMet?: boolean
}

/**
 * The only three evidence-profile findings allowed to end a run.
 * Counts come from compiler-earned labels; the third input is a structured user
 * signal. No model-authored prose or self-rating is an input.
 */
export function evidenceProfileStoppingRule(
  profile: EvidenceRuleProfile,
  facts: EvidenceProfileStoppingFacts,
):
  'stopping.minimum-supported' | 'stopping.minimum-refuted' | 'stopping.decision-criterion' | null {
  const stopping = normalizeEvidenceProfile(profile).stopping
  if (stopping.minSupported !== null && facts.supported >= stopping.minSupported) {
    return 'stopping.minimum-supported'
  }
  if (stopping.minRefuted !== null && facts.refuted >= stopping.minRefuted) {
    return 'stopping.minimum-refuted'
  }
  if (stopping.stopOnDecisionCriterion && facts.decisionCriterionMet === true) {
    return 'stopping.decision-criterion'
  }
  return null
}

function evidenceStatusFacts(compilation: EpistemicCompilation): EvidenceProfileStoppingFacts {
  return {
    supported: compilation.claims.filter(
      (claim) => claim.earnedStatus === 'supported' || claim.earnedStatus === 'observed-supported',
    ).length,
    refuted: compilation.claims.filter(
      (claim) => claim.earnedStatus === 'refuted' || claim.earnedStatus === 'observed-refuted',
    ).length,
    ...(() => {
      const receipt = compilation.policyReceipts.find(
        (row) => row.rule === 'stopping.decision-criterion',
      )
      return receipt?.outcome === 'met'
        ? { decisionCriterionMet: true }
        : receipt?.outcome === 'not-met'
          ? { decisionCriterionMet: false }
          : {}
    })(),
  }
}

/**
 * What a run may still add: its node allowance less what it has produced.
 *
 * Off the run's own count and never the graph's size. The allowance was the size
 * the graph stopped at until D74, which made a run on any exploration past it a
 * run that ended before its first move; a graph has no ceiling of its own, and
 * this is the one arithmetic the stop, the portfolio planner and the reserve
 * share. Pure and exported for its test, like `scheduledDue`.
 */
export const nodeHeadroom = (produced: number, budget: RunBudget): number =>
  Math.max(0, budget.nodes - produced)

/**
 * Whether a run under this budget could reach the closing scan at all.
 *
 * `scanWillRun` needs `SCAN_FLOOR` new nodes, and the loop stops once the run has
 * added `budget.nodes`, so a budget allowing fewer than the floor cannot reach
 * the scan however the run goes. The scan-key refusal reads this: a run that
 * cannot reach the scan must not be refused for a key the scan would have
 * needed.
 *
 * A property of the budget alone. While the allowance was the graph's size this
 * read the graph too, and a 249-node exploration could not reach the scan; a run
 * counts its own nodes now (D74), so every exploration starts one with the whole
 * allowance in hand. The NODE allowance is the only bound that gives certainty
 * either way. A move budget does not: one pass can return five nodes, so twelve
 * moves are no promise of twelve nodes and no proof of fewer. Refusing only where
 * it is provable keeps the guarantee the refusal exists for — that no run reaches
 * its last pass and discovers the scan was impossible all along.
 */
export const scanIsReachable = (budget: RunBudget): boolean =>
  nodeHeadroom(0, budget) >= SCAN_FLOOR &&
  // And a move to spend on it. The scan is a pass, so a budget of one move is
  // spent by the first thing the run does — asking for a scan key to run it was
  // refusing a run over a pass that could never happen, which is the rule this
  // predicate exists to enforce, applied to only one of its two ceilings.
  budget.moves >= 2

/**
 * Whether the user's condition is met, over the facts a run has collected.
 *
 * Pure and exported for its tests, like `scheduledDue`. The three inputs are the
 * three fact stores the loop already keeps for the account — readings, splits,
 * verdicts — so the condition is evaluated over exactly what the report will
 * print, and the two can never disagree about whether it was reached.
 */
export function untilMet(
  u: UntilCondition | undefined,
  facts: {
    readings: SourceReading[]
    splits: number
    verdicts: Audit['verdict'][]
    /** Candidates that have earned the condition's rung — `candidatesAtRung`,
     *  computed by the caller because only it knows which candidates count. */
    atRung?: number
  },
): boolean {
  if (!u) return false
  const count =
    u.kind === 'refuted'
      ? facts.readings.filter((r) => r === 'refuted').length
      : u.kind === 'supported'
        ? facts.readings.filter((r) => r === 'supported').length
        : u.kind === 'split'
          ? facts.splits
          : u.kind === 'rung'
            ? (facts.atRung ?? 0)
            : facts.verdicts.filter((v) => v === 'objection').length
  return count >= u.n
}

/**
 * Count what a pass landed that the model closed, by the ending it reached.
 *
 * `dead` is every terminal node; `endings` is the same nodes told apart by
 * `reasonedEndingOf` — the model's own ending, not a result recorded since —
 * so the account can say "1 objection stood, 1 open: reasoning can't settle it"
 * where it used to say "2 dead ends" over a question that is not one. Read at
 * landing and stored, like `dead`: a prune afterwards changes neither.
 */
function tallyEndings(
  l: Pick<Loop, 'dead' | 'endings'>,
  nodes: Record<string, ReasonNode>,
  ids: readonly string[],
): void {
  for (const id of ids) {
    const n = nodes[id]
    const e = n ? reasonedEndingOf(n) : null
    if (!e) continue
    l.dead++
    l.endings[e] = (l.endings[e] ?? 0) + 1
  }
}

interface Loop {
  id: string
  /** The starting queue only; another explicit start can authorize later additions. */
  agendaIds: string[] | null
  /** A checks-only run's named claims, or null for every other run. See
   *  `RunSpec.checkTargets`. */
  checkTargets: readonly string[] | null
  budget: RunBudget
  /** Normalized once at preflight; later profile edits apply to the next run. */
  evidenceProfile: EvidenceRuleProfile
  execution: RunExecution
  detail: RunDetail
  /** Which mode the loop is in NOW, not which it started in — `approveRest`
   *  turns a confirm run's remaining gates into a standing go. */
  mode: RunMode
  /** The confirm gate's answer slot: reset before each wait, read once on
   *  release. The two approvals write only into silence and `declineHere`
   *  writes always, so within one pause a refusal beats a go in either order
   *  — see `approveMove` for why that asymmetry is the safe one. A press that
   *  lands with no gate open is erased by the next reset, so nothing can be
   *  pre-approved from outside a pause. */
  gate: 'go' | 'aside' | null
  /** A malformed/truncated packet falls back to sequential for the rest of this run. */
  portfolioDisabled: boolean
  st: RunState
  /** Set by `stopRun`, tested after every await and inside every wait. */
  stopping: boolean
  /** A failure that will never resolve on its own — a rejected key or an empty
   *  account. Ends the run at once rather than after four identical rejections,
   *  each of which reopens the setup sheet, or after a whole budget of doomed
   *  requests against an account with no credits in it.
   *
   *  Named `terminal` and not `auth` because it has meant both since the quota
   *  arm landed, and a flag that says `auth` while a run ends on billing is how
   *  the next reader concludes the stop condition is broken. See `endsTheRun`. */
  terminal: boolean
  /**
   * A wait the run owes before its next move, after one failed on the API's side
   * or the wire's — see `pauseAfter`. Taken at the loop's top, where Stop and the
   * stop conditions answer for it like any other gap.
   */
  pause: { cause: WaitCause; until: number } | null
  /** Captured after the FIRST `beginOp`, never before: `beginOp` re-mints the id
   *  of a pinned demo session the moment a spending op runs on it, so a session
   *  anchored at start would report "the exploration changed" after pass one on
   *  the boot state of a first-run user. */
  sessionId: string | null
  moves: RunMove[]
  checkReadings: SourceReading[]
  /** Check PASSES that ran, which is what the button's "N source checks, which is
   *  at most M live web requests" promised. Counting readings instead would let a check
   *  that came back with nothing recorded go uncounted and the run exceed the
   *  number of live web passes the user agreed to. */
  checksRun: number
  /** Panel passes that ran, counted like `checksRun` and for the same reason: one
   *  panel is three billed requests, and a panel whose seats all failed still spent
   *  them. */
  panelsRun: number
  /** Cross-examinations that ran. Counted here rather than from `auditVerdicts`,
   *  which is empty for a pass that ran and came back with nothing usable. */
  auditsRun: number
  /** Verdicts they collected, one per claim examined. Handed over by `runAudit`,
   *  because the loop cannot recover it from the graph — see `Run.auditVerdicts`. */
  auditVerdicts: Audit['verdict'][]
  /** Scheduled conjecture passes that ran, counted like `auditsRun`: a pass that
   *  landed nothing is an answer, and it still spent its slot. */
  conjecturesRun: number
  /**
   * Conjectures this run landed that are owed a prior-art search, oldest first.
   * Filled only when the run was given conjecture passes (`budget.conjectures`),
   * because that is what the projection and the reserve priced a check slot as a
   * possible search for — and drained at check slots, before any source check.
   */
  priorArtQueue: string[]
  /** Panels this run landed whose three seats did not agree. Counted at the move,
   *  by `panelSplit` — the same mechanical count every other surface uses — so an
   *  `until` on splits reads the number the inspector would show. */
  panelSplits: number
  /** The auditor resolved at the start, or null when this run may not audit. Fixed
   *  before the first pass so the reserve can be sized against its price: a run that
   *  discovered its auditor mid-loop would already have spent under a reserve that
   *  did not cover it. */
  auditor: ResolvedModel | null
  /** Nodes this run has put on the graph, counted from its own ops as they land.
   *  The allowance `budget.nodes` is spent against this and the scan's floor is
   *  read off it — never off the graph's size, which is not the run's (D74). */
  produced: number
  dead: number
  /** `dead`, by the ending each reached (`tallyEndings`). */
  endings: ReasonedEndingCounts
  nothing: number
  refused: number
  failed: number
  stalls: number
  declined: number
  startedAt: number
  spendBefore: { inputTokens: number; outputTokens: number; searches: number }
  /** The ledger's whole estimate at the moment this run started, so what the run
   *  itself has drawn is a subtraction and never a second tally. Priced per row by
   *  each row's own model, so a model switched mid-run is costed correctly. */
  usdBefore: number
  /** The ceiling, or null. */
  usdMax: number | null
  /** Whether this app has a list price for the model the run started on. False
   *  makes every money figure meaningless rather than zero, and three surfaces
   *  need to know which of those it is. */
  priced: boolean
  /** Held back before every pass. The dearest pass seen so far, floored at the
   *  projection — so the reserve is never smaller than one move's worth even when
   *  the run's first passes came back cheap. */
  reserveUsd: number
  /** Whether a scheduled check is this run's to skip — an ordinary run's are; a
   *  checks-only run's and an agenda's are not. See `ceilingFloors`. */
  checksDeferred: boolean
  /** A source check's own reserve, when `checksDeferred`: a check is scheduled only
   *  while the ceiling has this much left. Grown like `reserveUsd`, by what a check
   *  actually drew, and never by any other kind of pass. Unused otherwise — the
   *  check is inside `reserveUsd` then. */
  checkReserveUsd: number
  /** The move count at which too little of the ceiling was left for a check, or
   *  null while checks are on. Monotone: the estimate only grows and so does the
   *  reserve, so a run that could not afford a check cannot later. */
  checksOffAt: number | null
  /** The move count at which a scheduled check came due with nothing to read on
   *  its route — 0 when the run started that way — and the model it was routed to
   *  then; or null while checks can read. The other way an ordinary run's checks go
   *  off, and whichever of the two comes first is the one: neither is set once the
   *  other is, so no slot is counted twice. Never cleared, so the account can count
   *  the slots it passed; a route that searches again does not turn them back on
   *  (D293). An agenda's and a checks-only run's checks are their work, and stop
   *  the run instead. */
  checksUnread: { at: number; model: string } | null
  model: ModelStamp
  capNote: string | null
  /** The candidate a pursue run is confined to, and its synthesis. Null on every
   *  other run. */
  pursue: { bridgeId: string; nodeId: string } | null
  /** The approved list a programme run walks, and the index of the step it is on.
   *  `step` advances past a taken step when its move is recorded, and past a
   *  skipped one when `nextProgrammeStep` skips it. */
  programme: Programme | null
  step: number
  /** See `liveRolesFor`: what `shouldStop` tests when it asks whether this run
   *  can still make a pass at all. */
  liveRoles: Role[]
  /**
   * The judge resolved at the start, or null when no TypeSafe key is stored.
   *
   * Fixed before the first pass like `auditor`, and for a weaker reason: its
   * draw is cents, so the reserve does not need it, but a run that consulted a
   * judge for its first check and found none for its second would be ordering
   * two checks by two rules and saying so nowhere. `cache` holds what it said
   * about each claim, keyed by node and fingerprinted on the claim's wording,
   * so a rewrite is asked again and a claim it has rated is never asked twice.
   * `off` is set by a failure no retry can fix — a rejected key — after which
   * the run stops asking rather than spending its checks on the same refusal.
   */
  judge: {
    resolved: ResolvedJudge
    model: ModelStamp
    cache: Map<string, { hash: string; p: number }>
    asked: number
    failed: number
    note: string | null
    off: boolean
  } | null
}

type AgendaMove = NonNullable<RunMove['agenda']>

/** The judge's account, as the run record carries it. */
function judgeAccount(l: Loop): NonNullable<Run['judge']> | undefined {
  if (!l.judge) return undefined
  return {
    model: l.judge.model,
    asked: l.judge.asked,
    failed: l.judge.failed,
    ...(l.judge.note ? { note: l.judge.note } : {}),
  }
}

/**
 * Ask the judge about every claim a check could take that it has not rated yet.
 *
 * Returns the map `verifyCandidates` orders ties by, or nothing when no judge is
 * configured — in which case the ranking is exactly what it was before a judge
 * existed. The SET rated is `checkableCandidates`, the same set the ranking sees,
 * so the judge can neither rate a claim the policy would refuse nor miss one it
 * would take. Batched at the gateway's bound; billed on the ledger under the
 * judge's own stamp; and never fatal to the run — a call that fails leaves its
 * claims ordered by arrival, counts on the account, and is said there in words.
 *
 * What this never does is decide whether a check happens. The candidate set is
 * untouched, `checkDue` is untouched, and a run with no judge and a run whose
 * judge failed every call make the same number of checks over the same claims,
 * in a different order among ties. That is the whole of D106's line, and
 * `runPolicy.test.ts` holds it.
 */
async function rateCheckability(
  l: Loop,
  s: Session,
): Promise<ReadonlyMap<string, number> | undefined> {
  const j = l.judge
  if (!j) return undefined
  const candidates = checkableCandidates(s, l.st)
  const hashOf = (claim: string): string => textHash(foldText(claim))
  const fresh = j.off ? [] : candidates.filter((n) => j.cache.get(n.id)?.hash !== hashOf(n.claim))
  for (let start = 0; start < fresh.length && !l.stopping; start += JUDGE_QUESTIONS_MAX) {
    const chunk = fresh.slice(start, start + JUDGE_QUESTIONS_MAX)
    const built = checkabilityQuestions(
      chunk.map((n) => ({ id: n.id, label: n.label, claim: n.claim })),
    )
    try {
      const res = await askJudge(j.resolved, built)
      useStore.getState().addSpend(res.spend)
      j.model = res.model
      chunk.forEach((n, i) => {
        const p = checkability(res.answers, i)
        if (p === null) return
        j.cache.set(n.id, { hash: hashOf(n.claim), p })
        j.asked++
      })
    } catch (err) {
      if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent)
      j.failed++
      j.note = err instanceof ModelError ? err.message : 'The judge did not answer.'
      // A rejected key or an empty account will not answer differently next
      // time; keep the checks and stop asking.
      if (err instanceof ModelError && (err.kind === 'auth' || err.kind === 'quota')) j.off = true
    }
  }
  const out = new Map<string, number>()
  for (const n of candidates) {
    const c = j.cache.get(n.id)
    if (c) out.set(n.id, c.p)
  }
  return out
}

function agendaPickFor(l: Loop) {
  const session = useStore.getState().session
  const agenda = session.research?.agenda
  if (!l.agendaIds || !agenda) return null
  const available = (step: AgendaStep) => {
    if (!l.agendaIds!.includes(step.id) || underClosed(session, step.nodeId, l.st)) return false
    let node = session.nodes[step.nodeId]
    const seen = new Set<string>()
    while (node && !seen.has(node.id)) {
      if (node.collapsed) return false
      seen.add(node.id)
      if (!node.parentId) break
      node = session.nodes[node.parentId]!
    }
    return true
  }
  // Retain all attempted rows for dependency and unknown-attempt locks, even
  // when the user added them after this run's explicit queue snapshot.
  return pickAgendaStep(session, {
    ...agenda,
    steps: agenda.steps.filter((step) => step.status !== 'pending' || available(step)),
  })
}

function beginQueuedMove(
  l: Loop,
  picked: NonNullable<ReturnType<typeof agendaPickFor>>,
): AgendaMove {
  const s = useStore.getState()
  const intent = { stepId: picked.step.id, attemptId: uid('agenda-attempt'), rule: picked.rule }
  const agenda = beginAgendaAttempt(s.session, intent.stepId, intent.attemptId, Date.now())
  // This mutation precedes beginOp and the transport's durable beforePaidCall
  // flush. A crash cannot leave an eligible step behind a charged request.
  s.setResearchState({ ...s.session.research!, agenda })
  l.capNote = `${picked.step.purpose} ${picked.rule}`
  return intent
}

function patchQueuedMove(
  l: Loop,
  intent: AgendaMove | null,
  patch: Partial<Pick<AgendaAttempt, 'opId' | 'finishedAt' | 'status'>>,
) {
  if (!intent) return
  const s = useStore.getState()
  if (l.sessionId !== null && s.session.id !== l.sessionId) return
  const research = s.session.research
  if (!research?.agenda) return
  s.setResearchState({
    ...research,
    agenda: patchAgendaAttempt(research.agenda, intent.stepId, intent.attemptId, patch),
  })
}

function settleQueuedMove(l: Loop, intent: AgendaMove | null, opId: string | null) {
  if (!intent) return
  const op = useStore.getState().session.ops.find((row) => row.id === opId)
  const calls = op?.modelCalls
  const unknown =
    opId !== null &&
    op?.errorKind !== 'storage' &&
    (!op?.finishedAt ||
      !calls?.length ||
      calls.some(
        (call) =>
          !call.usage ||
          (call.outcome.kind === 'failed' &&
            call.usage.inputTokens === 0 &&
            call.usage.outputTokens === 0),
      ))
  const failed =
    !opId ||
    op?.error ||
    op?.refused ||
    calls?.some((call) => call.outcome.kind !== 'completed' || call.outcome.truncated)
  patchQueuedMove(l, intent, {
    finishedAt: Date.now(),
    status: unknown ? 'unknown' : failed ? 'failed' : 'completed',
  })
}

let loop: Loop | null = null

export const runLive = (): boolean => loop !== null

/**
 * Stop the run, from anywhere.
 *
 * Idempotent, and safe to call when nothing is running — `cancelCurrent` calls it
 * unconditionally, because a user pressing Stop means both "abort this pass" and
 * "do not start another".
 */
export function stopRun(): void {
  if (!loop) return
  loop.stopping = true
  // And abort the pass it is inside. The flag alone only stops the NEXT move,
  // which would leave a paid pass the user has already stopped streaming for up to
  // a minute — while the pod's own tooltip says it was cancelled.
  abortInFlight()
}

/**
 * Close the branch the run is on for the rest of the run.
 *
 * The whole steering surface, and it is deliberately not a modal question before
 * each move: an intent window blocks every pass on a countdown, adds dead latency
 * to the whole run, and means you cannot walk away — which is the one thing an
 * autonomous mode is for. This takes effect on the next selection instead.
 *
 * It closes the node and everything under it. What is already on the canvas stays:
 * a run does not delete, and undoing is a separate, deliberate act.
 */
export function declineHere(): void {
  if (!loop) return
  const id = useStore.getState().run?.now?.nodeId
  if (!id) return
  // Also the third answer at a confirm gate: "Not there" closes the branch AND
  // releases the pause, so the loop picks elsewhere rather than holding open a
  // question about a branch the person just refused. Deliberately before the
  // dedupe below — a second refusal of the same root must not count twice, but
  // it must still answer the gate that is showing it. Outside a pause the flag
  // is inert: the gate resets it before it waits.
  loop.gate = 'aside'
  const s = useStore.getState().session
  // Already under a closed root: nothing changes, so nothing is counted. A second
  // press inside a branch the user has already refused must not make the account
  // claim they closed two.
  if (underClosed(s, id, loop.st)) return
  // The ROOT only. Materialising the subtree here would miss the children of the
  // very pass being declined — it is still streaming — and the run would resume on
  // the branch that was just refused. `eligible` walks up instead.
  loop.st.closed.add(id)
  loop.declined++
}

/**
 * The person's go, from any surface showing the pause.
 *
 * Only meaningful while the loop is holding at its confirm gate — see the
 * `gate` field for why a press outside one pre-approves nothing. Idempotent
 * like `stopRun`, and for the same reason: two surfaces show the same pause,
 * and two presses are one answer.
 */
export function approveMove(): void {
  if (!loop) return
  // Into silence only: the first answer to a gate is THE answer. "Not there"
  // can land a poll slice before this press, and it already closed the branch
  // — a go read last would then run the refused move while the record keeps
  // the refusal. The decline is the one press that overrides, in both orders,
  // because whenever a refusal is among a pause's answers the branch is shut.
  if (loop.gate === null) loop.gate = 'go'
}

/**
 * A standing go: this move and every one after it, unasked.
 *
 * Flips the loop to auto, and the published mode follows on the next
 * projection, so every surface stops saying the run will ask. This is the one
 * direction a mode can move mid-run: a gate can be given away by the person
 * holding it, while one imposed mid-flight would strand a run somebody had
 * already walked away from.
 */
export function approveRest(): void {
  if (!loop) return
  loop.mode = 'auto'
  // Same silence-only write as `approveMove`: after a "Not there" the branch
  // stays refused — the standing go still takes effect, from the next pick.
  if (loop.gate === null) loop.gate = 'go'
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Wait for the app to be idle, testing the stop flag on every slice. A user verb
 *  in the gap takes the lock, and the run waits behind it rather than racing it. */
async function waitForIdle(l: Loop): Promise<void> {
  while (useStore.getState().busy && !l.stopping) await sleep(IDLE_SLICE)
}

/**
 * A pass that never started, and whether asking for it again can go differently.
 *
 * Every verb refuses before `beginOp` when it cannot run, silently when the loop
 * asked it to be quiet, so all the loop learns is that `onStart` never fired.
 * One refusal clears on its own: the lock, taken by a hand-fired verb in the gap,
 * which the top of the loop waits on — `lockHeld` is `busy` less the worked
 * example's unfurl, so a verb refused on the lock always leaves `busy` set. Every
 * other one — a routed role whose key left, an auditor with no other house left
 * to run on — refuses the same pick on the next lap, and nothing between the two
 * laps waits on a timer: the loop spins on microtasks and the tab hangs. So
 * without the lock the run ends, as the confirm gate already ends it over a route
 * that stopped resolving (D61) — the mid-run form of the pre-flight refusal. A
 * check with nothing to READ is asked about before it is proposed, so it never
 * reaches here (D293).
 */
function unstarted(l: Loop): void {
  if (!useStore.getState().busy) l.terminal = true
}

/** What a confirm gate's release resolves to. `repick` sends the loop back to
 *  its top, where `shouldStop` and the policy answer for the world as it now
 *  is; only `run` lets the proposed pass itself go. */
export type GateOutcome = 'run' | 'repick' | 'replaced'

/**
 * The gate's release, decided over four mechanical facts.
 *
 * Pure and exported for its tests, like `scheduledDue`. Every input is a
 * press of the user's or a structural fact about the graph — nothing here
 * reads what any model said, which is invariant 6's rule applied to the one
 * new place a run now waits.
 *
 * `replaced` outranks everything but the stop, and it is deliberately NOT
 * conditioned on the node's fate: a fork carries its source's node ids under
 * a new session id, so the proposed node surviving proves nothing about the
 * graph it survives in — a first pause's gate answered over a freshly opened
 * fork would otherwise carry the run onto a document it never worked. The one
 * id change that is not a swap, the demo's paid re-mint, never reaches this
 * input at all: `holdForGo` follows renames through `core/rename.ts`, whose
 * whole point is that watching the id and guessing was a real bug, reverted —
 * the store announces, followers follow.
 */
export function gateOutcome(f: {
  stopping: boolean
  gate: 'go' | 'aside' | null
  nodeGone: boolean
  replaced: boolean
}): GateOutcome {
  // The user's stop outranks their own earlier go — the two can race by one
  // poll slice, and the press that says "no further" is the one that holds.
  if (f.stopping) return 'repick'
  if (f.replaced) return 'replaced'
  if (f.nodeGone) return 'repick'
  return f.gate === 'go' ? 'run' : 'repick'
}

/**
 * The reserve, re-floored against the routes as they stand NOW.
 *
 * The floor was taken when the run started, from the routes as they stood
 * then — the assumption every gap between passes already makes, but a gap of
 * milliseconds cannot meet the settings panel and a confirm gate's can: a
 * check re-routed to a dearer house while the gate held would run under a
 * reserve that never covered it, crossing the one ceiling this app promises
 * to stop short of. Only ever raised, like the reserve itself.
 *
 * A route that now resolves to a model with NO list price sinks `priced` —
 * every money figure becomes meaningless rather than quietly small — and
 * under a ceiling it puts the reserve past any ceiling at all: no finite
 * reserve can hold an unlisted rate, which is the pre-flight's own refusal
 * ("a spending ceiling needs a published price") arrived mid-run, expressed
 * in the reserve's arithmetic so `shouldStop` ends the run with the
 * ceiling's own sentence rather than a new stop word.
 *
 * The scan is counted only while the node allowance still leaves it reachable —
 * the same arithmetic as `scanIsReachable`, read off what the run has produced.
 */
function refloorReserve(l: Loop): void {
  const st = useStore.getState()
  // Only the passes this run can STILL make — the file's own rule once more:
  // where a pass provably cannot happen, nothing counts it. A check role
  // re-routed after its last check ran must not raise a floor no remaining
  // pass can draw, and an unpriced route there must not sink figures or end
  // a run over a rate nothing left can be billed at. Checks switched off — for
  // want of headroom, or of anything to read — are the same case: none of them
  // will run. So is a check whose route has nothing to read now, which the next
  // one due switches off, or stops an agenda at (D293): an endpoint with no
  // search and no price must not end a run under a ceiling over it.
  const auditor = l.auditsRun < (l.budget.audits ?? 0) ? (l.auditor?.entry ?? null) : null
  const roles: RolesInPlay = {
    // The list decides this one and the list does not change mid-run, so it is
    // the same answer `startRun` reached — restated here because this object is
    // built by hand rather than through `rolesInPlay`, whose other three rows
    // are counters this run has been spending.
    reason: reasonWillRun(l.programme ?? undefined),
    check: checksOn(l) && checkCanRead(st) && l.checksRun < l.budget.checks,
    panel: l.panelsRun < (l.budget.panels ?? 0),
    priorArt: l.checksRun < l.budget.checks && (l.budget.conjectures ?? 0) > 0,
    // Earned already, or still earnable. `scanWillRun`'s floor is on PRODUCED
    // nodes, so a run that has its twelve keeps the scan in this floor even
    // with its allowance spent — the headroom is the start-time reachability
    // question asked of what the run has left, and a floor that dropped an
    // earned scan would let one rerouted dearer at its gate run under a
    // reserve that never covered it.
    scan:
      l.agendaIds === null &&
      l.checkTargets === null &&
      (l.produced >= SCAN_FLOOR || nodeHeadroom(l.produced, l.budget) >= SCAN_FLOOR),
  }
  const d = ceilingFloors(runModelsFor(st), auditor, roles, l.checksDeferred)
  if (d === null) {
    l.priced = false
    if (l.usdMax !== null) l.reserveUsd = Number.POSITIVE_INFINITY
    return
  }
  if (l.usdMax === null) return
  l.reserveUsd = Math.max(l.reserveUsd, d.pass.usd)
  if (d.check) l.checkReserveUsd = Math.max(l.checkReserveUsd, d.check.usd)
}

/**
 * Confirm mode's pause: publish the proposed move and hold the loop, between
 * passes, until the person answers. Auto-mode callers never come here.
 *
 * The wait mirrors `waitForIdle` — the stop flag tested on every slice, no
 * countdown, nothing scheduled but the next look. It holds no lock, so the
 * pause is the ordinary gap made to last: questions, chips and hand-fired
 * verbs all still work, which is what "waits for your input" is for. An
 * answer that lands while a hand-fired verb still holds the lock is honoured
 * after it — the trailing `waitForIdle` — so an approved pass queues behind
 * the person's own work exactly as the loop's top does.
 *
 * On a `replaced` release the run binds itself to the graph it was pausing
 * over, so the 'replaced' ending can reach the record `beginRun` wrote there.
 * A run whose gate opened before its first pass has no other way to learn
 * which document that was — `sessionId` deliberately latches inside a pass,
 * after `beginOp`'s demo re-mint, and such a run never had one.
 */
async function holdForGo(l: Loop, now: NonNullable<RunLive['now']>): Promise<GateOutcome> {
  // The pausing document's id, FOLLOWED through renames the way every
  // id-keyed holder in this app is (`chat.ts`'s payers): the demo's paid
  // re-mint changes the id while the document stays, and only the store knows
  // which id changes are that — so it announces, and this follows. Any change
  // the announcement does not cover is a different document under the run.
  const sid = { id: useStore.getState().session.id }
  const unfollow = onRenamed(({ from, to }) => {
    if (sid.id === from) sid.id = to
  })
  try {
    l.gate = null
    project(l, now, 'waiting', null)
    // The third exit is the document itself going. Opening another exploration
    // takes no lock, so a pause is the one place a run can be orphaned with
    // every surface that could answer it unmounted — the pod, the gate row and
    // the composer's Stop all render off the projection `restoreSession`
    // clears. A poll with only the two press exits would then spin forever
    // behind a graph that shows no run, refusing every future run over a Stop
    // nobody can reach.
    while (l.gate === null && !l.stopping && useStore.getState().session.id === sid.id) {
      await sleep(IDLE_SLICE)
    }
    await waitForIdle(l)
    const s = useStore.getState()
    const out = gateOutcome({
      stopping: l.stopping,
      gate: l.gate,
      // The scan's whole-graph move has no node to lose, and the empty id must
      // not read as one gone.
      nodeGone: now.nodeId !== '' && s.session.nodes[now.nodeId] === undefined,
      replaced: s.session.id !== sid.id,
    })
    // The rename-followed OLD id, which is the name the outgoing document is
    // stored under now — see the binding note in the header.
    if (out === 'replaced') l.sessionId ??= sid.id
    return out
  } finally {
    unfollow()
  }
}

/** The live projection the pod renders. Rebuilt rather than patched, because it is
 *  small and a partial update is how two numbers in one panel come to disagree. */
function project(
  l: Loop,
  now: RunLive['now'],
  phase: RunLive['phase'],
  stop: RunStop | null,
  pause?: RunLive['pause'],
) {
  const s = useStore.getState().session
  useStore.getState().setRun({
    id: l.id,
    ...(pause ? { pause } : {}),
    budget: l.budget,
    movesRun: l.moves.length,
    passesRun: modelPassesFor(l.moves),
    now,
    phase,
    mode: l.mode,
    stop,
    open: countOpen(s, l.st),
    counts: {
      nodes: l.produced,
      dead: l.dead,
      endings: { ...l.endings },
      nothing: l.nothing,
      refused: l.refused,
      failed: l.failed,
      checks: l.checkReadings.length,
      declined: l.declined,
    },
    // Published whether or not a ceiling was set: the estimate is the thing
    // someone watching a run for nine minutes most wants to see, and withholding
    // it until they had thought to set a limit would be the wrong way round.
    spend: { usd: drawnUsd(l), ceiling: l.usdMax, priced: l.priced },
    capNote: l.capNote,
  })
}

/**
 * What a finished op actually did.
 *
 * Four outcomes and not two, because `produced.length === 0` is true for an
 * exhausted branch, a refusal and a network error alike — and reporting a refusal
 * as an exhausted branch is a claim about the idea rather than about the pass.
 */
function outcomeOf(
  opId: string,
  produced: number,
): { outcome: RunMove['outcome']; note?: string; errorKind?: ModelError['kind'] } {
  const op = useStore.getState().session.ops.find((o) => o.id === opId)
  if (op?.refused)
    return { outcome: 'refused', ...(op.refusalNote ? { note: op.refusalNote } : {}) }
  // `note` is what the move record shows a person. `errorKind` is what the loop
  // decides on. They were the same channel until the second one existed.
  if (op?.error)
    return {
      outcome: 'error',
      note: op.error,
      ...(op.errorKind ? { errorKind: op.errorKind } : {}),
    }
  return { outcome: produced > 0 ? 'nodes' : 'nothing' }
}

/**
 * The nodes an op put on the graph, by id.
 *
 * `runOp` returns these directly; `runPanel` and `runAudit` return a boolean, and
 * both can land nodes — a panel's dissent, an audit's objections. Read off the op
 * rather than added to those two signatures, because the op row is already the
 * record and a second channel could disagree with it.
 */
const producedBy = (opId: string): string[] =>
  useStore.getState().session.ops.find((o) => o.id === opId)?.produced ?? []

/** The claims this run has already sent to a source check, whatever came back. Read
 *  off the move rows, which are the record — a check that failed was still paid for,
 *  and a checks-only run must not buy the same claim twice. */
const checkedBy = (l: Loop): ReadonlySet<string> =>
  new Set(l.moves.filter((m) => m.kind === 'verify').map((m) => m.nodeId))

/**
 * Whether a finished pass SPENDS the node's and branch's allowance.
 *
 * The allowances count passes that RAN. A network failure is not the node's
 * fault, and letting three of them exhaust a node's allowance would push the run
 * off a branch it never reasoned about — so `error` and `refused` never charge.
 *
 * The third case is the one that was missing. A portfolio angle marked
 * `incomplete` still puts its admitted children on the graph, and the branch has
 * grown whatever the angle's verdict was, so it has taken a move. Counting only
 * the clean outcomes let a packet that overran its ceiling — or hit the
 * graph-size drop, or repeated a node — add a full set of children without
 * consuming anything, and repeat. The model picks that outcome, which is the
 * shape invariant 6 exists to keep away from a stop condition: no run policy
 * should be steerable by what a response happens to contain.
 *
 * `produced` and not the outcome alone, because `incomplete` also covers a pass
 * that landed nothing — the single-move path drops the subtree when a compact
 * target's detail never arrives — and an angle that put no node on the graph has
 * not grown the branch. And the outcome and not `produced` alone, because a
 * refused part carries its landed nodes too, and what a refusal advances is
 * decided deliberately elsewhere.
 */
export const spendsAllowance = (outcome: RunMove['outcome'], produced: number): boolean =>
  outcome === 'nodes' || outcome === 'nothing' || (outcome === 'incomplete' && produced > 0)

/**
 * A failure that will never resolve on its own.
 *
 * Two of them. A rejected key was always here; an exhausted balance joined it
 * after a live session spent its remaining passes discovering, one doomed request
 * at a time, that the account had no credits. Both end a run at once as
 * `unreachable` — "The model stopped answering", which is exactly true.
 *
 * This used to be a regex over the user-facing note the verb had stored —
 * `/API key was rejected/i` and `/no credits left/i`. The typed channel already
 * existed and this path ignored it, so a copy edit to a provider's error string
 * disabled the run's stop condition and nothing failed. It was wrong in both
 * directions: rewording stopped a run that should have stopped, and a note that
 * merely quoted the phrase stopped a run that should have continued.
 *
 * Written as an exhaustive switch on purpose. There is no `default`, so adding a
 * member to `ModelError['kind']` makes this stop compiling until somebody decides
 * whether the new failure is one a run can survive. That compile error IS the
 * feature — it is the review this decision never used to get.
 */
export function endsTheRun(kind?: ModelError['kind']): boolean {
  switch (kind) {
    case 'auth':
    case 'quota':
    case 'storage':
      return true
    case 'rate':
    case 'network':
    case 'server':
    case 'request':
    // The user's own hand. The run is already stopping by another route and must
    // not be relabelled as a model that went away.
    case 'aborted':
    case 'timeout':
    case 'unknown':
    // A pass that failed on its own terms rather than the provider's: no kind was
    // recorded, and that is a failed move, not a dead account.
    case undefined:
      return false
  }
}

/** How long a run waits before its next move after a rate limit — see
 *  `pauseAfter`. Half the minute a per-minute limit refills over. */
export const RATE_PAUSE_MS = 30_000
/** …and after the API failed on its side or the wire dropped. */
export const FAULT_PAUSE_MS = 15_000

/**
 * How long the run waits before its next move, after a move failed this way.
 *
 * The transport has already asked the failed request again, on the SDKs' terms,
 * two times in a second or two — so the failure a move reports is one that
 * outlasted that. A run then took its next move 500 ms later, into the same
 * limit: measured, four passes turned away in six seconds, and the run ended on
 * `FAILURE_LIMIT` as "The model stopped answering" before a per-minute limit had
 * refilled at all. So a move that failed on a wall that comes down by itself
 * buys the run a wait instead, said on the strip while it lasts, and stopped by
 * Stop like every other gap. It spends nothing and costs nothing but time: the
 * failure still counts toward `FAILURE_LIMIT`, so a limit that never lifts still
 * ends the run — four failures over a couple of minutes instead of six seconds.
 *
 * A wait, never a stop condition: it reads only the error's type, never what the
 * model said (invariants 6 and 7), and exhaustive with no `default` for the
 * reason `endsTheRun` is. The walls that never come down end the run instead;
 * a timeout has already waited ten minutes; a failure of the request itself, or
 * an unknown one, says nothing about the next.
 */
export function pauseAfter(kind?: ModelError['kind']): number {
  switch (kind) {
    case 'rate':
      return RATE_PAUSE_MS
    case 'server':
    case 'network':
      return FAULT_PAUSE_MS
    case 'auth':
    case 'quota':
    case 'storage':
    case 'request':
    case 'aborted':
    case 'timeout':
    case 'unknown':
    case undefined:
      return 0
  }
}

/** How much of `pauseAfter` a run actually waits: all of it in the app. A test
 *  of the loop's other behaviour sets it to nothing rather than sit through
 *  fifteen real seconds per failed move (`scaleRunPauses`). */
let pauseScale = 1
/** Exported for tests only. */
export function scaleRunPauses(scale: number): void {
  pauseScale = Math.max(0, scale)
}

/** A move failed: count it, end the run on a wall that never comes down, and owe
 *  a wait on one that does. The one place a run decides what a failure does. */
function failedMove(l: Loop, kind?: ModelError['kind']): void {
  l.failed++
  if (endsTheRun(kind)) l.terminal = true
  const ms = pauseAfter(kind) * pauseScale
  const cause = pauseCause(kind)
  if (ms > 0 && cause) l.pause = { cause, until: Date.now() + ms }
}

/**
 * Wait out what `failedMove` owed, on the strip, a second at a time — and not a
 * slice longer than Stop or a swapped exploration allows. Everything else the
 * stop conditions ask is asked at the loop's top, on the far side of the wait.
 */
async function waitOutPause(l: Loop): Promise<void> {
  const p = l.pause
  l.pause = null
  if (!p) return
  let shown = -1
  const here = () => l.sessionId === null || useStore.getState().session.id === l.sessionId
  while (!l.stopping && Date.now() < p.until && here()) {
    const left = p.until - Date.now()
    const sec = Math.ceil(left / 1000)
    if (sec !== shown) {
      shown = sec
      project(l, null, 'moving', null, { cause: p.cause, msLeft: left })
    }
    await sleep(Math.min(IDLE_SLICE, left))
  }
  project(l, null, 'moving', null)
}

/**
 * Whether a completed run should immediately open the coach.
 *
 * An explicit Stop is the person asking the app to stand down, and an unreachable
 * provider is already an interruption. Both keep Guide me available without
 * seizing the camera. Every ordinary mechanical terminus can begin automatically.
 */
export function guideAutoStarts(stop: RunStop): boolean {
  switch (stop) {
    case 'stopped':
    case 'unreachable':
    case 'replaced':
      return false
    case 'moves':
    case 'tokens':
    case 'clock':
    case 'size':
    case 'spend':
    case 'until':
    case 'protocol':
    case 'frontier':
    case 'programme':
    // A candidate coming off the ladder is a finding, and the coach reads the
    // graph it left behind — the same treatment every other mechanical
    // terminus gets.
    case 'pursuit':
    case 'stalled':
    case 'declined':
      return true
  }
}

/** Tokens this run has drawn, as a subtraction from the ledger it started against.
 *  Never a second tally: a run that counted its own tokens could disagree with the
 *  sidebar, and then neither figure is worth reading. */
function drawnTokens(l: Loop): number {
  const spend = useStore.getState().session.spend
  let out = 0
  for (const r of spend) out += r.outputTokens
  return Math.max(0, out - l.spendBefore.outputTokens)
}

/** What this run has drawn in money, the same way: a subtraction from the ledger
 *  it started against, priced row by row. An estimate that leans high — see
 *  `estimateUsd`. */
const drawnUsd = (l: Loop): number =>
  Math.max(0, estimateSpendUsd(useStore.getState().session.spend).usd - l.usdBefore)

/** Compile the live graph against this run's frozen constitution and actual counters. */
function liveEvidenceCompilation(l: Loop, now = Date.now()): EpistemicCompilation {
  const session = useStore.getState().session
  const research = session.research
  return compileSessionResearch({
    session,
    profile: l.evidenceProfile,
    ...(research
      ? {
          overrides: research.genealogyOverrides,
          extraRecords: research.evidenceRecords,
          annotations: research.evidenceAnnotations,
          manualEvidenceBindings: research.manualEvidenceBindings,
          claimAnnotations: research.claimAnnotations,
          decisionCriterionSignals: research.decisionCriterionSignals,
          auditReceipts: research.auditReceipts,
          coverageByOpId: coverageTracesByOpId(research.coverageTraces),
        }
      : {}),
    compileContext: {
      moves: l.moves.length,
      estimatedUsd: drawnUsd(l),
      estimateComplete: l.priced,
      elapsedMinutes: Math.max(0, (now - l.startedAt) / 60_000),
    },
    now,
  }).compilation
}

/**
 * Why the run should stop now, or null to keep going.
 *
 * Every condition is a resource, a mechanical predicate over records,
 * or the user — and NONE of them reads what the model said. There is deliberately
 * no condition meaning "it found something": the loop has no way to know that, and
 * a stop the model could reach by rating its own work is the one thing this tool
 * must not ship.
 *
 * A profile clock is explicitly a between-pass ceiling: it decides whether another
 * pass may start. It does not pretend to interrupt a hung request; the transport's
 * per-pass deadline remains that independent bound.
 */
function shouldStop(l: Loop): RunStop | null {
  if (l.stopping) return 'stopped'
  const s = useStore.getState()
  if (l.sessionId !== null && s.session.id !== l.sessionId) return 'replaced'
  if (!runIsReachable(s, l.liveRoles)) return 'unreachable'
  if (l.terminal) return 'unreachable'
  if (l.failed >= FAILURE_LIMIT) return 'unreachable'
  if (l.refused >= REFUSAL_LIMIT) return 'declined'
  if (l.stalls >= STALL_LIMIT) return 'stalled'
  // The pursued candidate STOPPED, which is the finding a pursue run exists to
  // reach — most often because the synthesis-first check refuted the synthesis
  // the whole run rests on. `candidatesAtRung` deliberately counts only live
  // candidates, so an `until` on a rung reads zero here and would never fire:
  // without this arm the run would spend the rest of a 48-move budget reasoning
  // inside a candidate `startRun` itself refuses to begin on. A dismissed
  // candidate ends it too — there is nothing left to pursue either way.
  if (l.pursue) {
    const b = s.session.bridges.find((x) => x.id === l.pursue!.bridgeId)
    if (!b || bridgeStanding(b, s.session.nodes).stop !== null) return 'pursuit'
  }
  // The user's own condition, tested before even their ceiling: both are theirs,
  // but only one of them is a FINDING — a run that met the condition and crossed
  // the ceiling in the same pass succeeded, and the ending should say so rather
  // than name the limit.
  if (
    untilMet(l.budget.until, {
      readings: l.checkReadings,
      splits: l.panelSplits,
      verdicts: l.auditVerdicts,
      atRung:
        l.budget.until?.kind === 'rung'
          ? candidatesAtRung(
              s.session,
              l.budget.until.rung ?? 'realised',
              l.pursue ? l.pursue.bridgeId : null,
            )
          : 0,
    })
  ) {
    return 'until'
  }
  const evidenceRule = evidenceProfileStoppingRule(
    l.evidenceProfile,
    evidenceStatusFacts(liveEvidenceCompilation(l)),
  )
  if (evidenceRule) return 'protocol'
  // The user's own ceiling, tested BEFORE this app's internal ones. Two ceilings
  // can be crossed by the same pass, and reporting an amount somebody typed is
  // worth more than reporting a token limit they have never heard of.
  //
  // Plus the reserve, which is what makes it a ceiling: `drawnUsd >= usdMax`
  // alone would notice the crossing one pass after it happened.
  if (moneySpent(l)) return 'spend'
  // One move held back when a scan is going to take it. `scanWillRun` reads only
  // counters, the key and the session's identity, never the model's output. The
  // hold lives HERE and not in `movesSpent`, because `scanWillRun` reads that
  // one — a hold inside it would ask the scan about itself.
  if (scanWillRun(l) && l.moves.length >= l.budget.moves - 1) return 'moves'
  if (movesSpent(l)) return 'moves'
  if (clockSpent(l)) return 'clock'
  if (tokensSpent(l)) return 'tokens'
  // The run's own count, never the graph's: a run on a large exploration is not
  // a run that has already finished. D74.
  if (nodesSpent(l)) return 'size'
  return null
}

/** Whether this move should be a source check rather than a reasoning verb.
 *  Evenly spaced through the budget, so the checks land on material the run has
 *  actually built rather than all at the end where a stop would lose them.
 *  Exported for the policy's verb-mix simulation, which has to spend a budget the
 *  way this loop does: a cadence copied into a test drifts from the one that runs. */
export const checkDueAt = (done: number, checksRun: number, budget: RunBudget): boolean => {
  if (checksRun >= budget.checks) return false
  return done > 0 && done % checkEvery(budget) === 0
}

/** The cadence's spacing, shared by the schedule and by the count of the slots a
 *  run passed with its checks off — two copies of one modulo is how the account
 *  comes to name a check the schedule would never have made. */
const checkEvery = (budget: RunBudget): number =>
  Math.max(2, Math.floor(budget.moves / (budget.checks + 1)))

/**
 * How many scheduled source checks a run passed with checks switched off, between
 * the move count where they went off and the one where its loop ended.
 *
 * A slot is the cadence's own — `checkDueAt`'s modulo — so this counts exactly the
 * checks the schedule would have made, and no more than the run had left. It is
 * arithmetic rather than a tally kept at each slot because a portfolio packet may
 * span several slots once checks are off: holding the packet to a slot it cannot
 * use would pack one angle per request for the rest of the run, which is the
 * spend the ceiling was set to prevent. `to` is the loop's last count, before the
 * closing scan, because a slot the loop never reached was ended by something else.
 * Pure and exported for its test, like `scheduledDue`.
 */
export function checksSkippedBetween(
  from: number,
  to: number,
  checksRun: number,
  budget: RunBudget,
): number {
  const left = Math.max(0, budget.checks - checksRun)
  const every = checkEvery(budget)
  let n = 0
  for (let k = Math.max(1, from); k < to && n < left; k++) if (k % every === 0) n++
  return n
}

function checkDue(l: Loop): boolean {
  return checkDueAt(l.moves.length, l.checksRun, l.budget)
}

/** Whether an ordinary run's scheduled checks are still on: neither the ceiling
 *  nor the route has switched them off. */
const checksOn = (l: Loop): boolean => l.checksOffAt === null && l.checksUnread === null

/** Both ways back to a source check that has something to read. One sentence for
 *  the two notes below, which are the two ways a run stops making checks for want
 *  of one. */
const CHECK_WAYS_BACK = 'Route the check to a model that searches, or turn on “Prefer my sources”.'

/** What the pod says once an ordinary run's checks are off for want of anything
 *  to read. Names the model, because nobody chose "the check route" by that name. */
const checksOffNote = (model: string): string =>
  `Source checks are off: ${model} has no web search here, so the run keeps reasoning. ${CHECK_WAYS_BACK}`

/**
 * A source check about to be proposed, or made, with nothing to read on its route
 * as things stand — and what that does to the run. Null when it can read.
 *
 * Settings are reachable between passes in either mode, and inside the judge's
 * call and a confirm gate's wait, and a check with nothing to read is refused
 * before `beginOp` — so proposing it anyway proposes it forever. An agenda's check
 * is queued work, so the agenda stops at the step and keeps it queued (`'stop'`),
 * as it does for want of an allowance. An ordinary run's checks go off for the rest
 * of it (`'off'`), as they would have from the start. Either way the run has
 * changed, so a caller that goes back to the top cannot meet the same check. A
 * checks-only run never gets here: the route is re-asked for it just before, and
 * it ends the way a rejected key does (D293).
 */
function checkUnread(l: Loop): 'stop' | 'off' | null {
  const st = useStore.getState()
  if (checkCanRead(st)) return null
  const model = entryFor(st, 'check').name
  if (l.agendaIds) {
    l.capNote = `The next queued step needs a source check, and ${model} has no web search here. ${CHECK_WAYS_BACK}`
    return 'stop'
  }
  l.checksUnread = { at: l.moves.length, model }
  l.capNote = checksOffNote(model)
  return 'off'
}

/**
 * A pursue run's first check: the synthesis itself, before anything under it.
 *
 * Fires while no check has run, only when the synthesis carries no live check
 * already and the budget allows one — counters and records, never the model's
 * words. Ahead of the cadence on purpose: a run that spent six moves under a
 * synthesis nobody had checked would be building on the thing it was meant to
 * test. The reason it prints is `synthesis-first`.
 */
function synthesisFirst(l: Loop, s: Session): { node: ReasonNode; why: MoveReason } | null {
  if (!l.pursue || l.checksRun > 0 || l.budget.checks < 1) return null
  const n = s.nodes[l.pursue.nodeId]
  if (!n || n.parentId === null || verificationLive(n)) return null
  return { node: n, why: 'synthesis-first' }
}

/**
 * Whether this run's budget still covers a step of this kind.
 *
 * The same counters the cadences read, asked as a ceiling rather than as a
 * schedule: a programme was approved as a list and its budget comes from the
 * list, but a durable evidence profile can cut that budget afterwards, and the
 * pre-flight refusals and the spending reserve are both computed against what
 * the budget ended up saying. A step outside it never runs.
 */
function programmeAllows(l: Loop, verb: ProgrammeStep['verb']): boolean {
  if (verb === 'verify') return l.checksRun < l.budget.checks
  if (verb === 'panel') return l.panelsRun < (l.budget.panels ?? 0)
  if (verb === 'audit') return l.auditsRun < (l.budget.audits ?? 0)
  return true
}

/**
 * A programme step whose pass never started is CONSUMED, not retried.
 *
 * Every dispatch below treats a null op id as "the call never ran" and goes
 * back to the top. For a policy-chosen move that is right: the selector will
 * pick something, and a lock taken in the gap is a real race. For an approved
 * step it is a hang — the step is the same one next time round, nothing
 * advances, and a run whose check route lost its key spins until the tab
 * closes. Consuming it costs one step of a list a person can re-approve; the
 * alternative costs the whole session.
 */
function consumeStep(l: Loop): void {
  if (l.programme) l.step++
}

/** The check the loop would make now, if any: the pursue run's grounding first,
 *  then the cadence. ONE selector, read at the proposal and again on the far side
 *  of a confirm gate, so the two cannot disagree about which check was approved. */
function checkPick(
  l: Loop,
  s: Session,
  checkability?: ReadonlyMap<string, number>,
): { node: ReasonNode; why: MoveReason } | null {
  return synthesisFirst(l, s) ?? (checkDue(l) ? verifyCandidates(s, l.st, checkability) : null)
}

/**
 * Whether this move should be a panel or a cross-examination.
 *
 * Behind-schedule accounting rather than `checkDue`'s modulo, and the difference is
 * not stylistic. Three gates now compete for the same move and the source check wins
 * — so a panel whose modulo hit on a checking move would lose its slot and then wait
 * a whole cycle for the cadence to come round again, which on a small budget means
 * it never runs at all. This asks "is it behind" instead of "is it exactly now", so a
 * lost slot is taken on the next move rather than forfeited.
 *
 * `checkDue` is deliberately left as it was. It is proven, its spacing floor is what
 * keeps a raised check count from starving `verifyCandidates`, and changing the
 * cadence of the pass that spends live web requests is not a free edit.
 */
/**
 * Whether a scheduled pass is overdue, as arithmetic alone.
 *
 * Pure and exported ONLY so it can be tested, and it earns that: the version without
 * the `+ 0.5` shipped a run that accepted "one cross-examination" and ran none, in
 * silence, because the single slot came due at exactly `budget.moves` — a move
 * `shouldStop` has already ended the loop at, and one earlier still when the closing
 * scan holds a move back. The half puts each pass at the MIDPOINT of its slot
 * instead, so the last one lands on a move the loop actually reaches. `capFor`
 * bounds `cap` to the range where that is true for every slot.
 */
export const scheduledDue = (done: number, ran: number, cap: number, total: number): boolean => {
  if (cap <= 0 || ran >= cap) return false
  const owed = Math.floor((done / Math.max(1, total)) * cap + 0.5)
  return done > 0 && ran < owed
}

const behindSchedule = (l: Loop, ran: number, cap: number): boolean =>
  scheduledDue(l.moves.length, ran, cap, l.budget.moves)

const panelDue = (l: Loop): boolean => behindSchedule(l, l.panelsRun, l.budget.panels ?? 0)

/** Plus the floor, which the panel has no equivalent of: a panel rules on ONE claim
 *  and is useful the moment there is one, while an audit takes a whole branch. */
const auditDue = (l: Loop): boolean =>
  l.auditor !== null &&
  l.produced >= AUDIT_FLOOR &&
  behindSchedule(l, l.auditsRun, l.budget.audits ?? 0)

/** As `auditDue` does it, with its own floor and count — and no auditor to wait
 *  for, since a conjecture pass runs on the reasoning role. The fourth gate, after
 *  the three that ground a claim, because it proposes rather than examines: when
 *  two are due on one move, the examination goes first. */
const conjectureDue = (l: Loop): boolean =>
  l.produced >= CONJECTURE_FLOOR && behindSchedule(l, l.conjecturesRun, l.budget.conjectures ?? 0)

/**
 * The oldest conjecture this run landed that is still owed a prior-art search,
 * or null — dropping from the front every entry that no longer is.
 *
 * Owed means: still on the graph, still a conjecture, never searched under the
 * wording it has now, and not somewhere the run was told to leave. Read at the
 * slot rather than when it was queued, because a person can search, rewrite or
 * prune a conjecture in the gap between passes.
 */
function priorArtDue(l: Loop, s: Session): ReasonNode | null {
  while (l.priorArtQueue.length) {
    const id = l.priorArtQueue[0]!
    const n = s.nodes[id]
    const owed =
      n?.kind === 'conjecture' &&
      n.conjecture !== undefined &&
      !n.conjecture.priorArt?.some((p) => p.contentHash === n.conjecture!.registered.contentHash) &&
      !underClosed(s, id, l.st) &&
      underFocus(s, id, l.st) &&
      !foldedAway(s, id)
    if (owed) return n
    l.priorArtQueue.shift()
  }
  return null
}

/** Whether a node or anything above it is folded away — which, like "Not there",
 *  tells a run to leave it. Cycle-guarded, as every upward walk here is. */
function foldedAway(s: Session, id: string): boolean {
  let cur: string | null = id
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    if (s.nodes[cur]?.collapsed === true) return true
    guard.add(cur)
    cur = s.nodes[cur]?.parentId ?? null
  }
  return false
}

/** Queue a prior-art search for each conjecture a pass landed — only on a run
 *  given conjecture passes; see `Loop.priorArtQueue`. */
function queuePriorArt(l: Loop, landed: readonly string[]): void {
  if ((l.budget.conjectures ?? 0) < 1) return
  const nodes = useStore.getState().session.nodes
  for (const id of landed) if (nodes[id]?.kind === 'conjecture') l.priorArtQueue.push(id)
}

export interface PortfolioScheduleState {
  checksRun: number
  panelsRun: number
  auditsRun: number
  auditEnabled: boolean
  /** Optional so a caller that schedules no conjecture passes states nothing. */
  conjecturesRun?: number
  produced: number
}

/** Pure schedule boundary used by the live wrapper below and pinned in tests. */
export function portfolioSlotsBeforeBoundary(
  done: number,
  budget: RunBudget,
  state: PortfolioScheduleState,
): number {
  // A closing scan may become eligible inside one packet, so it always keeps the
  // final logical slot while portfolio execution is active. The stall streak is
  // NOT a boundary here any more: it counts passes, a packet is one pass and moves
  // it by at most one, and `shouldStop` reads it before the next — so it cannot
  // fall inside a packet. It used to narrow the packet, back when it counted
  // angles and could.
  const width = Math.min(3, Math.max(0, budget.moves - done - 1))
  if (width < 2) return width

  const dueAt = (at: number, possibleProduced: number): boolean =>
    checkDueAt(at, state.checksRun, budget) ||
    scheduledDue(at, state.panelsRun, budget.panels ?? 0, budget.moves) ||
    (state.auditEnabled &&
      possibleProduced >= AUDIT_FLOOR &&
      scheduledDue(at, state.auditsRun, budget.audits ?? 0, budget.moves)) ||
    (possibleProduced >= CONJECTURE_FLOOR &&
      scheduledDue(at, state.conjecturesRun ?? 0, budget.conjectures ?? 0, budget.moves))

  if (dueAt(done, state.produced)) return 1
  for (let delta = 1; delta <= width; delta++) {
    // Five is the largest existing per-angle yield. Using the upper bound makes
    // an audit crossed into eligibility by this packet land on the next loop,
    // rather than after the packet has jumped past its scheduled slot.
    if (dueAt(done + delta, state.produced + delta * 5)) return delta
  }
  return width
}

/**
 * Logical angles a portfolio may own before the loop must re-run its grounding
 * gates. The closing scan keeps one slot in portfolio mode: a packet can cross
 * `SCAN_FLOOR` in one response, so deciding only from the pre-packet node count
 * would append that scan beyond the move budget.
 */
function portfolioLogicalSlots(l: Loop): number {
  return portfolioSlotsBeforeBoundary(l.moves.length, l.budget, {
    // Checks switched off — for want of headroom, or of anything to read — are no
    // boundary: none of them will run, and holding a packet to a slot it cannot
    // use would pack one angle per request for the rest of the run.
    // `checksSkippedBetween` counts those slots.
    checksRun: checksOn(l) ? l.checksRun : l.budget.checks,
    panelsRun: l.panelsRun,
    auditsRun: l.auditsRun,
    auditEnabled: l.auditor !== null,
    conjecturesRun: l.conjecturesRun,
    produced: l.produced,
  })
}

/**
 * What one portfolio pass does to the run's mechanical counters.
 *
 * ONE PASS, ONE READING. A packet is one provider request however many logical
 * angles it owns, and every counter here advances at most once for it — the rule
 * the failure and refusal counters already followed and the stall streak did not.
 * Counted per angle, three empty angles in one response were three stalls, and a
 * twelve-move run stopped `stalled` after its first request, having asked once.
 *
 * - `failed`: the request failed, or it answered with a packet that was malformed
 *   or incomplete AND admitted no node. Nothing usable came back, which is a
 *   failure and the `unreachable` path's to count, never evidence that the branch
 *   is exhausted. An incomplete packet that did land nodes delivered something, so
 *   it is neither — and it ends portfolio execution either way (`l.portfolioDisabled`).
 * - `stalled`: every part arrived complete, valid and empty. The one reading that
 *   means "nothing new here", and it is read off outcomes the adapter decided —
 *   no model text, rating or verdict reaches it (invariant 6).
 * - `landed`: some part put a node on the graph, which breaks the streak, whatever
 *   the packet's other parts came to.
 *
 * Exported for `run.test.ts`, which pins each arm against the result shapes
 * `runPortfolio` actually returns.
 */
export function portfolioPassTally(
  result: Pick<PortfolioResult, 'parts' | 'requestFailed' | 'refused' | 'incomplete'>,
): { failed: boolean; stalled: boolean; landed: boolean } {
  const landed = result.parts.some((part) => part.produced.length > 0)
  const broken = !result.refused && !result.requestFailed && result.incomplete && !landed
  return {
    failed: result.requestFailed || broken,
    stalled:
      !result.refused &&
      !result.requestFailed &&
      !result.incomplete &&
      result.parts.length > 0 &&
      result.parts.every((part) => part.outcome === 'nothing'),
    landed,
  }
}

/**
 * Start a run. Resolves when it has stopped for any reason.
 *
 * Every guard here refuses out loud: a control that silently does nothing is worse
 * than one that is disabled, and this one spends money.
 *
 * `usdMax` is a ceiling on the ESTIMATE, in dollars. Both guards on it refuse the
 * run rather than quietly dropping it — a ceiling that is accepted and then not
 * applied is the worst outcome available here, because the user would have no way
 * to tell until the bill arrived.
 */
export async function startRun(
  asked: RunSpec,
  /**
   * Fires once, if and ONLY if this run got past every refusal and the loop
   * began. `runOp`'s `onStart` contract, at the run's scale, and it exists for
   * a caller holding something it should not throw away on a refusal: the
   * programme sheet holds a list somebody paid a pass for, and every refusal
   * below — a check route with no key, an auditor that will not resolve, an
   * unpriced model under a ceiling — is a settings problem the person can fix
   * and press again. Clearing on the press meant paying twice for that.
   */
  opts: { onStarted?: () => void } = {},
): Promise<void> {
  const s0 = useStore.getState()
  // The dials a programme implies, before anything reads them: every refusal
  // below asks about the run that would actually start.
  const spec: RunSpec = {
    ...asked,
    ...(asked.programme ? programmeSizing(asked.programme) : {}),
  }
  if (
    s0.session.research?.mathematicsInvalid ||
    (s0.session.research?.mathematics?.enabled && !spec.agenda)
  ) {
    s0.toast(
      s0.session.research?.mathematicsInvalid
        ? 'Repair the invalid mathematical workspace before execution.'
        : 'Mathematical mode runs its declared agenda. Queue an obligation action in Research Lab and run that agenda.',
      'warn',
    )
    return
  }
  if (loop) {
    s0.toast('A run is already going. Stop it, or let it finish.', 'warn')
    return
  }
  // The two run shapes that rewrite the spec before anything reads it. A pursue
  // run needs a realised, unstopped candidate and takes its cones and its default
  // stop from it; a programme run takes its whole budget from the list. Both are
  // refused here, before a key or a dollar is checked, because neither has
  // anything to run without what it asks for.
  let pursue: Loop['pursue'] = null
  let cones: string[] | undefined
  let pursueEarned: readonly BridgeRung[] = []
  if (asked.pursue !== undefined) {
    const b = s0.session.bridges.find((x) => x.id === asked.pursue)
    if (!b) {
      s0.toast('That candidate is no longer in the list, so there is nothing to pursue.', 'warn')
      return
    }
    const st = bridgeStanding(b, s0.session.nodes)
    if (!st.nodeId) {
      s0.toast(
        'Realise the synthesis first. A pursue run works the branches under it, and there is nothing to work until it is on the graph.',
        'warn',
      )
      return
    }
    if (st.stop) {
      s0.toast(
        `This candidate has stopped — ${STOP_RULE[st.stop].toLowerCase()} There is nothing left to pursue.`,
        'warn',
      )
      return
    }
    pursue = { bridgeId: b.id, nodeId: st.nodeId }
    cones = conesOf(b, st.nodeId)
    // The rungs it has EARNED, kept for the default stop — which cannot be
    // derived here. `defaultPursueUntil` asks which rungs this run can reach,
    // and that is a fact about the BUDGET, which a durable evidence profile has
    // not yet been applied to. Derived below, once the budget is real.
    pursueEarned = st.earned
  }
  if (asked.programme && !asked.programme.steps.length) {
    s0.toast('The approved list is empty, so there is nothing to run.', 'warn')
    return
  }
  if (
    spec.agenda &&
    (spec.execution === 'portfolio' ||
      spec.detail === 'progressive' ||
      (spec.panels ?? 0) > 0 ||
      (spec.audits ?? 0) > 0 ||
      (spec.conjectures ?? 0) > 0 ||
      spec.ladder?.length ||
      spec.depth !== undefined ||
      spec.focus !== undefined ||
      spec.until?.kind === 'split' ||
      spec.until?.kind === 'objection')
  ) {
    s0.toast(
      'An agenda runs only its queued actions, one at a time. Disable portfolio detail, panels, audits, insights and frontier steering before starting it.',
      'warn',
    )
    return
  }
  // A checks-only run is its list and nothing else, so what would schedule a pass of
  // another kind is refused out loud rather than ignored under it.
  const checkTargets = spec.checkTargets?.length ? [...spec.checkTargets] : null
  if (checkTargets && (spec.agenda || (spec.panels ?? 0) > 0 || (spec.audits ?? 0) > 0)) {
    s0.toast(
      'A checks-only run makes one source check per named claim and nothing else. Start the agenda, the panels or the cross-examinations as a run of their own.',
      'warn',
    )
    return
  }
  const agendaIds = spec.agenda
    ? (s0.session.research?.agenda?.steps.map((step) => step.id) ?? [])
    : null
  if (agendaIds && !agendaIds.length) {
    s0.toast('Queue an agenda step before starting its run.', 'info')
    return
  }
  const execution = spec.execution ?? (spec.agenda ? 'sequential' : DEFAULT_EXECUTION)
  const evidenceProfile = normalizeEvidenceProfile(
    s0.session.research?.evidenceProfile ?? DEFAULT_EVIDENCE_PROFILE,
  )
  const profileIssue = evidenceProfileStartIssue(evidenceProfile)
  if (profileIssue) {
    s0.toast(profileIssue, 'warn')
    return
  }
  const ceiling = evidenceProfileCeiling(spec.usdMax, evidenceProfile)
  const wants = budgetForEvidenceProfile(spec, evidenceProfile, ceiling)
  if (spec.agenda)
    wants.checks = Math.min(
      wants.checks,
      s0.session.research?.agenda?.steps.filter(
        (step) => step.status === 'pending' && step.action === 'verify',
      ).length ?? 0,
    )
  const activeDecisionSignals = (s0.session.research?.decisionCriterionSignals ?? []).filter(
    (signal) =>
      (s0.session.brief ?? []).some(
        (directive) => directive.kind === 'criterion' && directive.text === signal.criterion,
      ),
  )
  if (evidenceProfile.stopping.stopOnDecisionCriterion && !activeDecisionSignals.length) {
    s0.toast(
      'This evidence protocol stops on a decision criterion, but no current “what would settle it” line has a recorded status. Record one in Research Lab before starting the run.',
      'warn',
    )
    return
  }
  const initialResearch = compileSessionResearch({
    session: s0.session,
    profile: evidenceProfile,
    ...(s0.session.research
      ? {
          overrides: s0.session.research.genealogyOverrides,
          extraRecords: s0.session.research.evidenceRecords,
          annotations: s0.session.research.evidenceAnnotations,
          manualEvidenceBindings: s0.session.research.manualEvidenceBindings,
          claimAnnotations: s0.session.research.claimAnnotations,
          decisionCriterionSignals: s0.session.research.decisionCriterionSignals,
          auditReceipts: s0.session.research.auditReceipts,
          coverageByOpId: coverageTracesByOpId(s0.session.research.coverageTraces),
        }
      : {}),
    compileContext: {
      moves: 0,
      estimatedUsd: 0,
      estimateComplete: true,
      elapsedMinutes: 0,
    },
  }).compilation
  if (evidenceProfileStoppingRule(evidenceProfile, evidenceStatusFacts(initialResearch))) {
    s0.toast(
      'The evidence protocol’s stopping threshold is already met, so no model pass was started.',
      'info',
    )
    return
  }
  if (wants.ms === 0) {
    s0.toast(
      'This evidence protocol allows zero elapsed minutes, so no model pass can start. Raise or clear that ceiling in Research Lab.',
      'warn',
    )
    return
  }
  const m = resolveFor(s0, 'reason')
  // A programme of nothing but checks makes NO reasoning pass — a person can
  // untick every reasoning step and approve the rest — so demanding that route's
  // key refuses an approved list over a model it will never call. Invariant 4's
  // concern read backwards: a routed role with no key must refuse its own pass,
  // and must not refuse somebody else's. Every other run reasons by definition.
  if (!m && reasonWillRun(asked.programme)) {
    s0.toast(
      `A run makes a pass for every move, so it needs your ${providerFor(s0, 'reason').name} key.`,
      'warn',
    )
    s0.askForMissingKey('reason')
    return
  }
  // The routed reasoning ENTRY, which needs no key: it names the provider an
  // auditor must differ from, prices the reserve's reasoning seat, and stamps
  // the run. A check-only programme has all three questions and no `m`.
  const reasonEntry = m?.entry ?? entryFor(s0, 'reason')
  // The other two roles a run can reach, checked BEFORE anything is spent and only
  // when the budget actually asks for them. A run that reasons on a house you have
  // a key for and checks sources on one you do not would otherwise get forty moves
  // in before discovering that every check it scheduled is impossible — having
  // spent real money to arrive at a run that cannot do what was asked of it.
  // The scan is not in this loop, and the difference is that it is not optional:
  // every run ends on one, so there is no "turn them off" to offer. It is checked
  // just below with its own sentence.
  //
  // Read off the BUDGET rather than the spec, which is the only place that knows.
  // `resolveChecks` gives an ABSENT `spec.checks` the budget row's own default, so
  // the old `(spec.checks ?? 0) > 0` read "no checks wanted" for every run that
  // simply did not name a number — which is the default, and therefore the case
  // this refusal most needed to cover. A default run with the check role pointed
  // at a house holding no key reached the first check and failed there, having
  // spent everything up to it.
  for (const [role, wanted] of [
    ['check', wants.checks > 0],
    ['panel', (wants.panels ?? 0) > 0],
  ] as const) {
    if (!wanted || resolveFor(s0, role)) continue
    s0.toast(
      `This run schedules ${role === 'check' ? 'source checks' : 'panels'}, and those are routed to ` +
        `${entryFor(s0, role).name} — which needs your ${providerFor(s0, role).name} key. Add it, or ` +
        `turn them off.`,
      'warn',
    )
    s0.askForMissingKey(role)
    return
  }
  // Every pass of an agenda's checks and of a checks-only run is a source check, so
  // a route with nothing to read would make the whole run passes that read nothing.
  if ((spec.agenda || checkTargets) && wants.checks > 0 && !checkCanRead(s0)) {
    s0.toast(
      checkTargets
        ? searchlessCheckSentence(entryFor(s0, 'check').name)
        : 'The queued source checks need a model with search or the supplied-source option. Update that route before starting the agenda.',
      'warn',
    )
    return
  }
  // An ordinary run's checks are the budget's schedule, and the control has already
  // said which way this goes: “all of them reasoning, since a source check needs a
  // search this endpoint may not have”. So they are off from the start and the run
  // goes on, saying so on the pod and in its account — not refused under a sentence
  // that promised it would run, and not proposing the same impossible check lap
  // after lap, which hung the tab. The quote and the reserve still count them, on
  // both sides, so the field and the button agree about the ceiling (D293).
  const checksUnread =
    !spec.agenda && !checkTargets && wants.checks > 0 && !checkCanRead(s0)
      ? { at: 0, model: entryFor(s0, 'check').name }
      : null
  // The closing scan, resolved before anything is spent for the same reason as
  // the two above. Routed somewhere this app has no key for, it used to refuse at
  // the very end — after the whole budget had been spent reaching it, on the one
  // pass whose job is to tie the run together.
  //
  // Conditioned on the scan being REACHABLE, which is not the same as wanted. A
  // budget allowing fewer than `SCAN_FLOOR` nodes cannot produce the new nodes
  // the scan needs however the run goes, so refusing there would block moves
  // that are still worth making over a pass the loop could never make.
  // Asked of the LIST as well, where there is one. `scanIsReachable` answers
  // whether the node allowance leaves room for the closing scan, which is the
  // whole question for every run that reasons — and only half of it for a
  // programme, because a list of nothing but checks, panels and audits produces
  // no nodes to scan however tall its budget is. `ui/Programme.tsx` has asked
  // `programmeScanInPlay` at all three of its sites since that function existed;
  // this one was left on the budget alone, so the sheet enabled an approval that
  // `startRun` then refused over a scan key nothing would have used. The field
  // arguing with the button is the failure this pair exists to prevent.
  //
  // A checks-only run and an agenda add no node either, so neither can earn the
  // scan's floor; both are told so here rather than reserved against or refused
  // over a scan route.
  const scanInPlay =
    !spec.agenda &&
    !checkTargets &&
    (asked.programme
      ? programmeScanInPlay(wants, asked.programme.steps, s0)
      : scanIsReachable(wants))
  if (scanInPlay && !resolveFor(s0, 'scan')) {
    s0.toast(
      `Every run ends on a breakthrough scan, and yours is routed to ${entryFor(s0, 'scan').name} — ` +
        `which needs your ${providerFor(s0, 'scan').name} key. Add it, or point the scan back at your ` +
        `selected model.`,
      'warn',
    )
    s0.askForMissingKey('scan', 'The closing scan')
    return
  }
  if (s0.session.order.length < 2) {
    s0.toast('A run works the open ends of a graph. Explore an idea first.', 'info')
    return
  }
  // The judge, resolved once here for the reason `Loop.judge` gives. Null is
  // the ordinary state and refuses nothing: a judge is not a role. A checks-only
  // run has none either: its claims were named at the press, so there are no ties
  // among candidates for a judge to order (D106), and asking would bill for nothing.
  const judge = checkTargets ? null : resolveJudge(s0)
  const models = runModelsFor(s0)
  // Every model this run can bill, not just the selected one. Routing means an
  // unpriced endpoint can now arrive in any of three slots, and a ceiling that
  // covers two of the three is a ceiling the user was told they had.
  //
  // "Can bill" and not "is routed": a role no pass of this run can reach is left
  // out, on the same rule as the key refusals above — the scan on reachability,
  // the check and the panel on what the budget schedules, and the reasoning role
  // on the approved list, exactly as the key refusal a few lines up already asks
  // it. Otherwise a run whose scan cannot run loses its ceiling — and with it the
  // run — over an unpriced endpoint no pass would have reached, a run with
  // `checks: 0` loses it over a check route that cannot bill a cent, and a
  // check-only programme loses it over the selected model, which is the one case
  // where the person refused has routed every pass they approved to a priced
  // house. See `RolesInPlay`.
  //
  // WHAT THIS DOES NOT REACH, and deliberately: `projectRunUsd` and
  // `projectMoveRange` still require the reasoning price whatever the list says,
  // because there it is not a role's seat but the per-move BASELINE the whole
  // figure is built on — every move is charged at the reasoning rate and each
  // other role pays only the positive difference. So a check-only programme
  // under an unpriced selection now starts, holds its ceiling on the reserve
  // below and on `drawnUsd`, which prices the LEDGER row by row and needs no
  // rate for a pass nobody made — and gets no `effectiveMoves`, which is the
  // state every unbounded run is already in. Making the baseline follow the
  // list would change the quoted figure for every run, so it is named here
  // rather than done in passing.
  const roles = rolesInPlay(wants, scanInPlay, asked.programme)
  const unpriced = [
    ...(roles.reason ? [models.reason] : []),
    ...(roles.check ? [models.check] : []),
    ...(roles.panel ? [models.panel] : []),
    ...(roles.scan ? [models.scan] : []),
  ].find((e) => !e.price)
  if (ceiling !== null && unpriced) {
    // The sidebar already hides the field for this model, so reaching here means
    // the palette or a keybinding got in. Refusing is still right: there is no
    // rate to enforce the number against.
    s0.toast(
      `A spending ceiling needs a published price, and ${unpriced.name} runs on an endpoint you supplied — what it charges is between you and whoever runs it. Run it without a ceiling, or switch models.`,
      'warn',
    )
    return
  }
  // The auditor, resolved BEFORE anything is spent.
  //
  // An audit is the only pass a run makes on another provider, and the reserve is
  // what turns a ceiling from a tripwire into a ceiling. Sized off the selected
  // model alone, the reserve does not bound an audit pass — so a run could stop
  // "before the pass that would cross the ceiling" and be wrong about which pass
  // that was. Resolving here also means the two refusals below happen before the
  // user's money is committed rather than at the move where the audit was due.
  //
  // A PROGRAMME knows its targets, so it resolves one auditor per approved step
  // and floors on the dearest — see `programmeAuditors` for why the selected
  // model's own provider is the wrong thing to ask against. Every other run picks
  // its audit target at the move, so there is nothing to ask yet and the
  // selection stands as the representative; the loop corrects it per target
  // before each audit, which is what actually holds the ceiling.
  //
  // Asked of `wants` and never of `spec`: `budgetForEvidenceProfile` has already
  // run, and a durable profile can take the audit allowance to zero after the
  // request. Refusing a run for a key that an audit which cannot happen would
  // have needed is the same wrong answer `scanIsReachable` exists to prevent for
  // the scan — and under a ceiling it also refused over an unpriced auditor no
  // pass would ever reach.
  const auditsAllowed = wants.audits ?? 0
  const auditSteps =
    auditsAllowed > 0 && spec.programme ? programmeAuditors(s0, spec.programme.steps) : null
  // For a programme, whether an audit happens is also a fact about the LIVE steps
  // and not about the row `programmeSizing` computed: a target pruned between the
  // sheet and this press leaves an allowance no step can draw, and refusing a run
  // over an audit that cannot happen is that same wrong answer from the other side.
  const wantsAudit = auditWillRun(auditsAllowed, auditSteps)
  const unkeyed = auditSteps?.find((a) => a.auditor === null) ?? null
  const auditor = !wantsAudit
    ? null
    : auditSteps
      ? dearestAuditor(auditSteps)
      : // Against the reasoning route's house (`runAuditor`), the one reading the run
        // gate and the composer's projection make of it too.
        runAuditor(s0)
  if (wantsAudit && (unkeyed || !auditor)) {
    s0.toast(
      unkeyed
        ? `A cross-examination of “${s0.session.nodes[unkeyed.nodeId]?.label ?? 'a step'}” needs a key for a second provider — an audit runs on a house other than the one that wrote the branch, and every house other than that one is unkeyed here. Untick that step, or add a key.`
        : 'A cross-examination needs a key for a second provider — the point is a model from another company, and one from the same one would be the panel again. Turn it off, or add a key.',
      'warn',
    )
    s0.askForOtherKey([reasonEntry.provider], {
      what: 'A cross-examination',
      on: 'another company’s model',
    })
    return
  }
  if (ceiling !== null && auditor && !auditor.entry.price) {
    // The same refusal, and for the same reason, as the unpriced-model check above:
    // a ceiling this app cannot enforce against a pass the run will actually make is
    // a number the user set being silently ignored while their money is spent.
    s0.toast(
      `A cross-examination would run on ${auditor.entry.name}, which is an endpoint you supplied — there is no rate here to hold it to, so a spending ceiling cannot cover it. Drop the ceiling, or turn the cross-examination off.`,
      'warn',
    )
    return
  }

  // The RESERVE, not the typical per-move figure. See `worstPassUsd`: this is what
  // makes the ceiling hold rather than merely be noticed a pass late. Taken over
  // EVERY model this run can reach — the three routable roles and the auditor —
  // because the guarantee is about the dearest pass the run could make and not
  // about the dearest pass the selected model could.
  //
  // Per role, for an ordinary run: its checks are held to their own reserve and
  // skipped while the ceiling cannot cover one, so a cap between the two floors
  // runs its reasoning rather than refusing it — see `ceilingFloors`.
  const checksDeferred = !spec.agenda && !checkTargets
  const floors = ceilingFloors(models, auditor?.entry ?? null, roles, checksDeferred)
  const dearest = floors?.pass ?? null
  const floor = dearest?.usd ?? 0
  if (ceiling !== null && ceiling < floor) {
    // The model that SET the floor, which is not always the selected one. A run
    // refused over a dear routed scan used to send the reader to re-point a
    // reasoning model that was affordable all along.
    s0.toast(
      `${fmtUsd(ceiling)} cannot be guaranteed to cover a single pass on ${dearest?.entry.name ?? reasonEntry.name}, so the run would stop before its first move. ${fmtUsd(floor)} is the smallest ceiling that can.`,
      'warn',
    )
    return
  }
  /**
   * A pursue run's default stop, derived from the budget the run will actually
   * have rather than from the one it asked for.
   *
   * `defaultPursueUntil` picks the first rung above what the candidate has
   * earned that THIS run can earn, and a durable evidence profile can take the
   * audit allowance to zero after the request. Asked of the request, it could
   * name `cross-examined` on a run that will make no audit — a condition that
   * can never fire, so the run would sail past the `checked` rung it could
   * reach and stop on a limit instead. A condition the user typed is theirs and
   * is never replaced.
   */
  const budget: RunBudget =
    pursue && asked.until === undefined
      ? (() => {
          const derived = defaultPursueUntil(pursueEarned, {
            checks: wants.checks,
            ...(wants.audits !== undefined ? { audits: wants.audits } : {}),
          })
          return derived ? { ...wants, until: derived } : wants
        })()
      : wants

  // What the ceiling can actually pay for, when that is less than what was asked for.
  // Handed to the policy so the branch share and the per-node allowance are shares of
  // the run that will happen rather than of the one that was requested — see
  // `RunState.effectiveMoves`. Absent whenever the move count binds first, so an
  // unbounded run behaves exactly as before.
  const projected =
    ceiling !== null
      ? projectMoveRange(
          models,
          budget,
          auditor?.entry ?? null,
          ceiling,
          scanInPlay,
          checksDeferred,
          // The closing scan as its own prompt prices it, the figure the
          // sidebar's range was drawn with.
          scanInPlay ? closingScanUsd(s0) : null,
          // Packed or not, as the sidebar's range was drawn (D311).
          execution,
        )
      : null
  const effectiveMoves = projected && projected.likely < budget.moves ? projected.likely : undefined

  // Replay slices `order` at the playhead and its interval re-reads the live
  // length every tick, so against a growing graph it can never terminate — and a
  // run behind a playhead adds nodes nobody can see.
  s0.setPlaying(false)
  s0.setPlayhead(null)
  // The camera follows again from here. A pan latches `viewPinned` and nothing
  // else clears it, so without this a run can add sixty nodes outside the frame in
  // silence. After this the run never fights the camera — it announces instead.
  s0.unpinView()

  const before = s0.session.spend.reduce(
    (a, r) => ({
      inputTokens: a.inputTokens + r.inputTokens,
      outputTokens: a.outputTokens + r.outputTokens,
      searches: a.searches + r.searches,
    }),
    { inputTokens: 0, outputTokens: 0, searches: 0 },
  )

  const l: Loop = {
    id: uid('run'),
    agendaIds,
    checkTargets,
    budget,
    evidenceProfile,
    execution,
    detail: execution === 'portfolio' ? (spec.detail ?? 'full') : 'full',
    mode: spec.mode ?? 'auto',
    gate: null,
    portfolioDisabled: false,
    st: freshRunState(budget.moves, {
      ...(spec.ladder?.length ? { ladder: spec.ladder } : {}),
      ...(spec.depth !== undefined ? { maxDepth: spec.depth } : {}),
      ...(spec.focus ? { focus: spec.focus } : {}),
      ...(cones ? { cones } : {}),
      ...(effectiveMoves !== undefined ? { effectiveMoves } : {}),
    }),
    stopping: false,
    terminal: false,
    pause: null,
    sessionId: null,
    moves: [],
    checkReadings: [],
    checksRun: 0,
    panelsRun: 0,
    auditsRun: 0,
    auditVerdicts: [],
    conjecturesRun: 0,
    priorArtQueue: [],
    panelSplits: 0,
    auditor,
    produced: 0,
    dead: 0,
    endings: {},
    nothing: 0,
    refused: 0,
    failed: 0,
    stalls: 0,
    declined: 0,
    startedAt: Date.now(),
    spendBefore: before,
    usdBefore: estimateSpendUsd(s0.session.spend).usd,
    usdMax: ceiling,
    priced: !unpriced,
    // Floored at the projection from the start. A run whose first pass came back
    // unusually cheap must not shrink its own guard and then be crossed by a
    // normal one — the reserve only ever grows.
    reserveUsd: floor,
    checksDeferred,
    checkReserveUsd: floors?.check?.usd ?? 0,
    checksOffAt: null,
    checksUnread,
    model: stampOf(reasonEntry),
    capNote: checksUnread ? checksOffNote(checksUnread.model) : null,
    judge: judge
      ? {
          resolved: judge,
          // The alias until the first answer names the version that answered.
          model: judgeStampFor(judge.entry),
          cache: new Map(),
          asked: 0,
          failed: 0,
          note: null,
          off: false,
        }
      : null,
    pursue,
    programme: spec.programme ?? null,
    step: 0,
    liveRoles: liveRolesFor(spec.programme, budget),
  }
  loop = l
  opts.onStarted?.()

  // ONE step of history for the whole run. `restore` carries the ops, the ledger
  // and the run records forward, so a step back withdraws the nodes and leaves the
  // account of what they cost — which is the rule the undo model already states.
  useStore.getState().mark('Autonomous run')

  const record: Run = {
    id: l.id,
    startedAt: l.startedAt,
    budget,
    ...(agendaIds ? { agenda: { stepIds: [...agendaIds] } } : {}),
    moves: [],
    produced: 0,
    dead: 0,
    endings: {},
    checkReadings: [],
    declined: 0,
    open: countOpen(useStore.getState().session, l.st),
    spendBefore: before,
    model: l.model,
    // On the record from the start, like the budget: what a run was confined to
    // and what list it walked are facts about what it was allowed to do.
    ...(pursue ? { pursues: pursue.bridgeId } : {}),
    ...(l.programme ? { programme: l.programme } : {}),
    ...(judgeAccount(l) ? { judge: judgeAccount(l) } : {}),
    evidencePolicy: {
      profile: evidenceProfile,
      profileId: evidenceProfile.id,
      profileName: evidenceProfile.name,
      profileVersion: evidenceProfile.version,
      maxMoves: evidenceProfile.budget.maxMoves,
      maxEstimatedUsd: evidenceProfile.budget.maxEstimatedUsd,
      maxElapsedMinutes: evidenceProfile.budget.maxElapsedMinutes,
      minSupported: evidenceProfile.stopping.minSupported,
      minRefuted: evidenceProfile.stopping.minRefuted,
      stopOnDecisionCriterion: evidenceProfile.stopping.stopOnDecisionCriterion,
    },
  }
  // Written at the START, so a tab that dies mid-run still leaves behind what the
  // run had done and no `finishedAt` — the absence being the information.
  useStore.getState().beginRun(record)
  project(l, null, 'moving', null)

  let stop: RunStop = 'moves'
  try {
    for (;;) {
      await waitForIdle(l)
      const why = shouldStop(l)
      if (why) {
        stop = why
        break
      }

      // The estimate before this pass, so its own cost can be read off afterwards.
      // Both branches below use it, and both grow the reserve — a source check
      // carries nine BILLED web requests and is routinely the dearest pass a run
      // makes, so sizing the guard off reasoning moves alone would undersize it.
      // A `let`, because a confirm gate re-takes it on the far side of the wait:
      // the pause invites hand-fired passes, they bill the same ledger, and a
      // reserve only ever grows — measured from before the gate, somebody's
      // mid-pause panel would be booked as the approved pass's cost and hold
      // every later pass to a bound nothing the RUN ever drew.
      let usdAt = drawnUsd(l)

      const s = useStore.getState().session
      // A programme run takes the next approved step and chooses nothing; every
      // other run asks the cadences and the policy below. The step is consumed
      // when its move is recorded, so a pass the lock refused is tried again.
      const step = l.programme
        ? nextProgrammeStep(l.programme, l.step, s.nodes, (verb) => programmeAllows(l, verb))
        : null
      if (l.programme) {
        if (step === null) {
          stop = 'programme'
          break
        }
        l.step = step.index
      }
      const agendaPick = l.agendaIds ? agendaPickFor(l) : null
      if (l.agendaIds && !agendaPick) {
        const pending = s.research?.agenda?.steps.some(
          (step) =>
            l.agendaIds!.includes(step.id) &&
            ['pending', 'running', 'unknown'].includes(step.status),
        )
        l.capNote = pending
          ? 'The agenda is blocked by changed inputs, prerequisites, a folded branch or an unresolved attempt.'
          : 'All steps in this run’s agenda have been attempted or dismissed.'
        stop = 'frontier'
        break
      }
      if (agendaPick?.step.action === 'verify' && l.checksRun >= l.budget.checks) {
        l.capNote =
          'The next queued step needs a source check, and this run’s check allowance is exhausted.'
        stop = 'frontier'
        break
      }
      // And for want of anything to read on its route, the step still queued — see
      // `checkUnread`.
      if (agendaPick?.step.action === 'verify' && checkUnread(l) === 'stop') {
        stop = 'frontier'
        break
      }
      // A checks-only run takes its next named claim on every move and nothing else;
      // with none left it has finished its list, which is a fact about the list.
      const listed = l.checkTargets ? nextRestingCheck(s, l.st, l.checkTargets, checkedBy(l)) : null
      if (l.checkTargets && !listed) {
        l.capNote =
          'Every claim this run was pressed for has been checked, or has since been folded, closed, pruned or checked by hand.'
        stop = 'frontier'
        break
      }
      // A failed move's wait, owed only where there is a next move to wait for —
      // after the checks that end a run with nothing left on its list — and then
      // back to the top: the world is asked again on the far side of it, as on
      // the far side of every other wait.
      if (l.pause) {
        await waitOutPause(l)
        continue
      }
      // A due check with nothing to read on its route switches an ordinary run's
      // checks off — see `checkUnread`. Asked where one comes due, not on every lap
      // as the ceiling is below: a route passing through a model with no search on
      // its way to another is a legitimate state for as long as no check is due.
      if (!l.agendaIds && !l.checkTargets && checksOn(l) && checkDue(l)) checkUnread(l)
      // Checks go off the moment the ceiling can no longer hold one back, which is
      // the per-role reserve (`ceilingFloors`): the run keeps reasoning under the
      // pass reserve `shouldStop` has just cleared, and the account counts the
      // slots it passed. Asked of every ordinary run with checks left, due or not,
      // so a portfolio packet is planned against the schedule the run will keep.
      if (
        l.checksDeferred &&
        checksOn(l) &&
        l.checksRun < l.budget.checks &&
        !checkFits(drawnUsd(l), l.checkReserveUsd, l.usdMax)
      ) {
        l.checksOffAt = l.moves.length
        // Said on the pod as it happens, not only in the account afterwards: the
        // run someone is watching has just changed what it will do.
        l.capNote = `Source checks are off from here: under ${fmtUsd(l.checkReserveUsd)} of your ceiling is left for one, so the run keeps reasoning.`
      }
      // A prior-art search owed to a conjecture this run landed takes a due check
      // slot before any source check. Inside the check allowance and counted as
      // one check, because it is one live search pass; first come, first served,
      // so a small allowance can go entirely to searches — the account says so,
      // and `/check` raises it (D281). Off whenever the checks are, for either
      // reason: it is one of them (D293).
      const searchPick =
        !l.agendaIds && !l.checkTargets && checksOn(l) && checkDue(l) ? priorArtDue(l, s) : null
      // A check model with no web search can run no prior-art search, and the pass
      // refuses in silence rather than come back empty from nowhere. The queue
      // goes, and the account says why — before anything is proposed, so a
      // confirm run is never asked to approve a search that cannot happen.
      const searchRoute = searchPick ? resolveFor(useStore.getState(), 'check') : null
      if (searchPick && searchRoute?.entry.search === 'none') {
        l.priorArtQueue = []
        l.capNote = `${searchRoute.entry.name}, where source checks go, has no web search, so the insights this run proposes are left unsearched.`
        continue
      }
      if (searchPick) {
        const target = searchPick
        const proposed: NonNullable<RunLive['now']> = {
          kind: 'priorArt',
          nodeId: target.id,
          label: target.label,
          why: 'landed-unsearched',
        }
        if (l.mode !== 'auto') {
          if ((await holdForGo(l, proposed)) !== 'run') continue
          // The source check's re-checks, for its reasons.
          if (resolveFor(useStore.getState(), 'check') === null) {
            l.terminal = true
            continue
          }
          refloorReserve(l)
          if (shouldStop(l) !== null) continue
          if (priorArtDue(l, useStore.getState().session)?.id !== target.id) continue
          usdAt = drawnUsd(l)
        }
        const at = l.moves.length + 1
        project(l, proposed, 'moving', null)
        let opId: string | null = null
        await runPriorArt(target.id, {
          quiet: true,
          onStart: (id) => {
            opId = id
            l.sessionId ??= useStore.getState().session.id
          },
        })
        if (opId === null) {
          // Another verb took the lock in the gap: the search is still owed, and
          // the loop's own wait comes first. Anything else that stopped it before
          // it started will stop it again, so it is dropped rather than retried.
          if (!useStore.getState().busy) l.priorArtQueue.shift()
          continue
        }
        l.priorArtQueue.shift()
        l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)
        // The pass RAN, and it was one of the live search passes this run agreed to.
        l.checksRun++
        const searched = !!useStore
          .getState()
          .session.nodes[target.id]?.conjecture?.priorArt?.some((p) => p.opId === opId)
        const { outcome, note, errorKind } = outcomeOf(opId, 0)
        if (outcome === 'refused') l.refused++
        else if (outcome === 'error') failedMove(l, errorKind)
        l.moves.push({
          at,
          kind: 'priorArt',
          nodeId: target.id,
          label: target.label,
          why: 'landed-unsearched',
          opId,
          outcome: searched ? 'nodes' : outcome,
          produced: 0,
          ...(note ? { note } : {}),
        })
        // Neither stalls nor un-stalls, as a source check does not: it adds no node
        // by design.
        useStore.getState().patchRun(l.id, {
          moves: l.moves.slice(),
          open: countOpen(useStore.getState().session, l.st),
        })
        project(l, null, 'moving', null)
        continue
      }

      const doCheck = !l.agendaIds && !l.checkTargets && checksOn(l) && checkDue(l)
      // The judge's numbers for this check's candidates, asked for before the
      // ranking and only when a check is due — an await, so the session is read
      // again on the far side of it, exactly as it is on the far side of a gate.
      const checkability = doCheck ? await rateCheckability(l, s) : undefined
      if (l.stopping) continue
      // And the money asked again on the far side of it, because the judge bills
      // the same ledger: cents, but a reserve is a bound and not a habit. Back to
      // the top, where the stop conditions and the check's own reserve answer for
      // the estimate as it now stands.
      if (
        doCheck &&
        (shouldStop(l) !== null || !checkFits(drawnUsd(l), l.checkReserveUsd, l.usdMax))
      )
        continue
      const sAfterJudge = checkability ? useStore.getState().session : s
      const pick = step
        ? step.step.verb === 'verify'
          ? { node: s.nodes[step.step.nodeId]!, why: 'programme' as const }
          : null
        : agendaPick?.step.action === 'verify'
          ? { node: s.nodes[agendaPick.step.nodeId]!, why: 'agenda' as const }
          : listed
            ? listed
            : !l.agendaIds && !l.checkTargets && checksOn(l)
              ? checkPick(l, sAfterJudge, checkability)
              : null

      if (pick) {
        const target = pick.node
        const proposed: NonNullable<RunLive['now']> = {
          kind: 'verify',
          nodeId: target.id,
          label: target.label,
          why: pick.why,
        }
        // The confirm gate. A release that is not a go — and a go the pause
        // outlived, which the re-run of `shouldStop` below catches: the
        // ledger and the graph both kept moving while the loop held — goes
        // back to the top, where the stop conditions and the policy answer
        // for the world as it now stands.
        if (l.mode !== 'auto') {
          if ((await holdForGo(l, proposed)) !== 'run') continue
          // The reserve before the stop conditions, because the spend arm
          // reads it — a route grown dearer while the gate held has to be in
          // the floor before the floor is asked about.
          refloorReserve(l)
          if (shouldStop(l) !== null) continue
          // And the check's own, which the pause can have outlived the same way:
          // hand-fired passes bill the ledger a go was given against.
          if (l.checksDeferred && !checkFits(drawnUsd(l), l.checkReserveUsd, l.usdMax)) continue
          // And the go is honoured only if the selector that proposed this
          // move, re-asked over the graph as it now stands, still answers
          // with it: a branch folded away — the pod promises folding stops
          // the run going there — the very chip hand-served, a cap newly
          // crossed, each re-proposes rather than runs. A pass the policy
          // would no longer pick must not run on an approval given to a
          // graph that is gone.
          // A programme's step was approved as part of the list, and the list does
          // not change mid-run, so there is nothing to re-pick for one.
          if (
            !step &&
            (agendaPick
              ? agendaPickFor(l)?.step.id !== agendaPick.step.id
              : l.checkTargets
                ? nextRestingCheck(useStore.getState().session, l.st, l.checkTargets, checkedBy(l))
                    ?.node.id !== target.id
                : checkPick(l, useStore.getState().session, checkability)?.node.id !== target.id)
          )
            continue
          usdAt = drawnUsd(l)
        }
        // The route, asked again immediately before the dispatch — settings are
        // reachable inside a confirm pause and between moves, and a check route
        // that cannot run one is refused by `runVerify` before the op opens: no
        // id comes back, nothing advances, and the same check is proposed
        // forever. This is the mid-run form of the pre-flight refusal and ends
        // the run the way a rejected key does. It stands in front of EVERY check
        // dispatch rather than only behind the gate, because the walk-away run
        // is the one with nobody watching it spin.
        const at = l.moves.length + 1
        // A run whose every pass is a check re-asks the pre-flight before each one: a
        // route whose key left, or that stopped searching, would refuse silently in
        // `runVerify` — no op, no count — and the same claim would be proposed again
        // forever. Ended the way a rejected key ends a run.
        if (
          (agendaPick || l.checkTargets) &&
          (resolveFor(useStore.getState(), 'check') === null ||
            (l.checkTargets !== null && !checkCanRead(useStore.getState())))
        ) {
          l.terminal = true
          continue
        }
        // And once more where the check is made, for the other two: the judge's call
        // and the confirm gate above are both awaits a route can change inside.
        const unread = checkUnread(l)
        if (unread === 'stop') {
          stop = 'frontier'
          break
        }
        if (unread) continue
        // And AFTER `checkUnread`, which is D293's door: an ordinary run whose
        // route cannot READ turns its checks off and goes on, saying so. What is
        // left for this guard is the route with no KEY at all — `runVerify`
        // refuses it before the op opens, so no id comes back, nothing advances,
        // and the same check is proposed forever. That ends the run the way a
        // rejected key does, in front of EVERY check dispatch rather than only
        // behind the gate, because the walk-away run is the one with nobody
        // watching it spin.
        if (!checkCanRun(useStore.getState())) {
          l.terminal = true
          continue
        }
        const intent = agendaPick ? beginQueuedMove(l, agendaPick) : null
        project(l, proposed, 'moving', null)
        let opId: string | null = null
        const before = useStore.getState().session.nodes[target.id]?.verification
        try {
          await runVerify(target.id, {
            mark: false,
            quiet: true,
            ...(intent ? { maxRetries: 0, investigationPurpose: agendaPick!.step.purpose } : {}),
            onStart: (id) => {
              opId = id
              l.sessionId ??= useStore.getState().session.id
              patchQueuedMove(l, intent, { opId: id })
            },
          })
        } finally {
          settleQueuedMove(l, intent, opId)
        }
        // A guard that never fired means the call never ran, and nothing is recorded
        // for a move that did not happen. Whether to ask again is `unstarted`'s call.
        if (opId === null) {
          unstarted(l)
          consumeStep(l)
          continue
        }
        // Into the check's own reserve where it has one: what a check drew says
        // nothing about a reasoning pass, and growing the pass reserve by it would
        // hold every later move to a check's bound — the refusal this split ended.
        if (l.checksDeferred) l.checkReserveUsd = Math.max(l.checkReserveUsd, drawnUsd(l) - usdAt)
        else l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)
        // The pass RAN. Counted here rather than from the readings, so a check
        // that recorded nothing still spends its slot — the button promised a
        // number of live web passes, not a number of results.
        l.checksRun++
        const v = useStore.getState().session.nodes[target.id]?.verification
        const landed = !!v && v !== before
        if (landed && v) l.checkReadings.push(v.reading)
        const { outcome, note, errorKind } = outcomeOf(opId, landed ? 1 : 0)
        if (outcome === 'refused') l.refused++
        else if (outcome === 'error') {
          failedMove(l, errorKind)
        }
        const rated = checkability?.get(target.id)
        l.moves.push({
          at,
          kind: 'verify',
          nodeId: target.id,
          label: target.label,
          why: pick.why,
          ...(intent ? { agenda: intent } : {}),
          opId,
          outcome: landed ? 'nodes' : outcome,
          produced: 0,
          ...(note ? { note } : {}),
          // The judge's number on this claim, whether or not it decided anything:
          // a run that paid for the answer owes the reader the answer.
          ...(l.judge && rated !== undefined
            ? { judge: { checkability: rated, model: l.judge.model } }
            : {}),
        })
        // A check neither stalls nor un-stalls a run: it adds no nodes by design,
        // and counting it as "nothing new" would end a run on its own arithmetic.
        if (l.programme) l.step++
        useStore.getState().patchRun(l.id, {
          moves: l.moves.slice(),
          checkReadings: l.checkReadings.slice(),
          open: countOpen(useStore.getState().session, l.st),
          ...(judgeAccount(l) ? { judge: judgeAccount(l) } : {}),
        })
        project(l, null, 'moving', null)
        continue
      }

      // The panel, then the cross-examination, both after the source check.
      //
      // Three gates now want the same move. The order is the order in which the
      // three ground a claim — what sources say, then what other lenses say, then
      // what another house says — and it is also cheapest-first. `behindSchedule` is
      // what keeps the two below from being starved by the one above; see `panelDue`.
      //
      // Neither touches `l.stalls`, and that is the same rule the check follows: both
      // produce at most a node or two BY DESIGN, so counting a pass that recorded
      // three verdicts as "nothing new" would end a run on its own arithmetic.
      const panelPick = step
        ? step.step.verb === 'panel'
          ? { node: s.nodes[step.step.nodeId]!, why: 'programme' as const }
          : null
        : !l.agendaIds && panelDue(l)
          ? panelCandidate(s, l.st)
          : null
      if (panelPick) {
        const target = panelPick.node
        const proposed: NonNullable<RunLive['now']> = {
          kind: 'panel',
          nodeId: target.id,
          label: target.label,
          why: panelPick.why,
        }
        if (l.mode !== 'auto') {
          if ((await holdForGo(l, proposed)) !== 'run') continue
          // The verify gate's re-checks, for its reasons: a route that
          // stopped resolving ends the run rather than proposing an
          // impossible pass forever, the reserve is re-floored before the
          // stop conditions read it, and a selector that no longer answers
          // with this node — folded, hand-seated, capped — re-proposes.
          if (resolveFor(useStore.getState(), 'panel') === null) {
            l.terminal = true
            continue
          }
          refloorReserve(l)
          if (shouldStop(l) !== null) continue
          if (panelCandidate(useStore.getState().session, l.st)?.node.id !== target.id) continue
          usdAt = drawnUsd(l)
        }
        const at = l.moves.length + 1
        project(l, proposed, 'moving', null)
        let opId: string | null = null
        let panelPasses = 0
        const had = useStore.getState().session.nodes[target.id]?.panel
        await runPanel(target.id, {
          mark: false,
          quiet: true,
          onStart: (id) => {
            opId = id
            l.sessionId ??= useStore.getState().session.id
          },
          onAttempt: (attempts) => {
            panelPasses = attempts
          },
        })
        // A guard that never fired means the call never ran, and nothing is recorded
        // for a move that did not happen. Whether to ask again is `unstarted`'s call;
        // the approved list moves on regardless, so a step nobody could take does not
        // hold the run standing on it.
        if (opId === null) {
          unstarted(l)
          consumeStep(l)
          continue
        }
        l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)
        // The pass RAN, whatever its seats came back with — three requests were
        // billed either way, exactly as `checksRun` counts a check that found nothing.
        l.panelsRun++
        const made = producedBy(opId)
        for (const id of made) l.st.produced.add(id)
        l.produced += made.length
        const nodes = useStore.getState().session.nodes
        tallyEndings(l, nodes, made)
        const now = nodes[target.id]?.panel
        // Recorded three verdicts and landed no node is still a pass that changed the
        // graph, so it reads as `nodes` — the same reading, for the same reason, that
        // the check above gives a verification that landed with no node behind it.
        const landed = !!now && now !== had
        if (landed && now && panelSplit(now)) l.panelSplits++
        const { outcome, note, errorKind } = outcomeOf(opId, made.length)
        if (outcome === 'refused') l.refused++
        else if (outcome === 'error') {
          failedMove(l, errorKind)
        }
        l.moves.push({
          at,
          kind: 'panel',
          nodeId: target.id,
          label: target.label,
          why: panelPick.why,
          opId,
          outcome: landed ? 'nodes' : outcome,
          produced: made.length,
          modelPasses: panelPasses,
          ...(note ? { note } : {}),
        })
        if (l.programme) l.step++
        useStore.getState().patchRun(l.id, {
          moves: l.moves.slice(),
          produced: l.produced,
          dead: l.dead,
          endings: { ...l.endings },
          open: countOpen(useStore.getState().session, l.st),
        })
        project(l, null, 'moving', null)
        continue
      }

      const auditPick = step
        ? step.step.verb === 'audit'
          ? { node: s.nodes[step.step.nodeId]!, why: 'programme' as const }
          : null
        : !l.agendaIds && auditDue(l)
          ? auditCandidate(s, l.st)
          : null
      if (auditPick) {
        const target = auditPick.node
        const proposed: NonNullable<RunLive['now']> = {
          kind: 'audit',
          nodeId: target.id,
          label: target.label,
          why: auditPick.why,
        }
        if (l.mode !== 'auto') {
          if ((await holdForGo(l, proposed)) !== 'run') continue
        }
        // The auditor is THIS BRANCH's other house, and which house that is was
        // not knowable when the run started: `auditorFor` — `runAudit`'s own
        // resolution — resolves against the target branch's author, while the
        // pre-flight could only ask against the selected model. So this runs on
        // every audit and in every mode. It used to sit inside the confirm gate,
        // which left auto runs, and therefore every programme, reserved for a
        // model the pass was not going to use: a ceiling could be crossed by an
        // audit on a dearer house than the floor ever covered.
        //
        // Under a confirm gate it also catches what the PAUSE changed — a key
        // removed, a custom endpoint added — which is why it is resolved on the
        // far side of the wait rather than before it. An unpriced custom auditor
        // sinks `priced` and, under a ceiling, ends the run through the refloor's
        // own arithmetic instead of running under a hosted model's stale reserve.
        //
        // A null ends an ordinary run, because `auditDue` would offer the same
        // target again and meet the same silent refusal forever. A PROGRAMME
        // skips the step instead and walks on, which is `nextProgrammeStep`'s own
        // rule for a step whose node has left the graph: a step that will not run
        // must not refuse a run. The pre-flight cannot always promise otherwise
        // — `branchAuthor` falls through to a plurality of the branch when the
        // root carries no stamp, and a list's own earlier steps can move it, a
        // panel's dissent carrying the selection's stamp and an audit's
        // objections the auditor's — so the house this resolves to need not be
        // the one `programmeAuditors` keyed and priced at the sheet. Ending the
        // whole run there spent the approved list and took none of the passes
        // after it, which is the shape the sheet's own guards exist to prevent.
        const fresh = auditorFor(useStore.getState(), target.id)
        if (fresh === null) {
          if (l.programme) {
            consumeStep(l)
            continue
          }
          l.terminal = true
          continue
        }
        l.auditor = fresh
        refloorReserve(l)
        if (shouldStop(l) !== null) continue
        if (l.mode !== 'auto') {
          if (auditCandidate(useStore.getState().session, l.st)?.node.id !== target.id) continue
          usdAt = drawnUsd(l)
        }
        const at = l.moves.length + 1
        project(l, proposed, 'moving', null)
        let opId: string | null = null
        let verdicts: Audit['verdict'][] = []
        await runAudit(target.id, {
          maxClaims: 1,
          mark: false,
          quiet: true,
          onStart: (id) => {
            opId = id
            l.sessionId ??= useStore.getState().session.id
          },
          onVerdicts: (v) => {
            verdicts = v
          },
        })
        if (opId === null) {
          unstarted(l)
          consumeStep(l)
          continue
        }
        l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)
        l.auditsRun++
        // Appended, never replaced: two audits are two branches, and a run that
        // reported only the second one's tally would be understating its own coverage
        // in the one direction nothing downstream could check.
        l.auditVerdicts.push(...verdicts)
        const made = producedBy(opId)
        for (const id of made) l.st.produced.add(id)
        l.produced += made.length
        const nodes = useStore.getState().session.nodes
        tallyEndings(l, nodes, made)
        const { outcome, note, errorKind } = outcomeOf(opId, made.length)
        if (outcome === 'refused') l.refused++
        else if (outcome === 'error') {
          failedMove(l, errorKind)
        }
        l.moves.push({
          at,
          kind: 'audit',
          nodeId: target.id,
          label: target.label,
          why: auditPick.why,
          opId,
          // A pass that examined claims and broke none produced no node and is the
          // most useful thing this verb can return — so the verdict tally, not the
          // node count, is what says it ran.
          outcome: verdicts.length ? 'nodes' : outcome,
          produced: made.length,
          ...(note ? { note } : {}),
        })
        if (l.programme) l.step++
        useStore.getState().patchRun(l.id, {
          moves: l.moves.slice(),
          produced: l.produced,
          dead: l.dead,
          endings: { ...l.endings },
          auditVerdicts: l.auditVerdicts.slice(),
          open: countOpen(useStore.getState().session, l.st),
        })
        project(l, null, 'moving', null)
        continue
      }

      // The conjecture pass, when this run was given any: the fourth gate, after the
      // three that ground a claim, because it proposes rather than examines. Aimed
      // by `conjectureCandidate` — by parentage, never by the anchors a conjecture
      // names. It neither stalls nor un-stalls the run: an empty conjecture pass is
      // an answer, not an exhausted branch, and must never end a run (D281).
      const conjecturePick = !l.agendaIds && conjectureDue(l) ? conjectureCandidate(s, l.st) : null
      if (conjecturePick) {
        const target = conjecturePick.node
        const proposed: NonNullable<RunLive['now']> = {
          kind: 'conjecture',
          nodeId: target.id,
          label: target.label,
          why: conjecturePick.why,
        }
        if (l.mode !== 'auto') {
          if ((await holdForGo(l, proposed)) !== 'run') continue
          if (resolveFor(useStore.getState(), 'reason') === null) {
            l.terminal = true
            continue
          }
          refloorReserve(l)
          if (shouldStop(l) !== null) continue
          if (conjectureCandidate(useStore.getState().session, l.st)?.node.id !== target.id)
            continue
          usdAt = drawnUsd(l)
        }
        const at = l.moves.length + 1
        project(l, proposed, 'moving', null)
        // Spent before the pass, like a ladder verb: one that came back with nothing
        // was still paid for, and must not be offered the same node again.
        const tried = l.st.attempted.get(target.id) ?? new Set<DeriveOp>()
        tried.add('conjecture')
        l.st.attempted.set(target.id, tried)
        let opId: string | null = null
        const landed = await runConjecture(target.id, {
          mark: false,
          quiet: true,
          onStart: (id) => {
            opId = id
            l.sessionId ??= useStore.getState().session.id
          },
        })
        if (opId === null) {
          unstarted(l)
          continue
        }
        l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)
        l.conjecturesRun++
        for (const id of landed) l.st.produced.add(id)
        l.produced += landed.length
        const { outcome, note, errorKind } = outcomeOf(opId, landed.length)
        if (outcome === 'refused') l.refused++
        else if (outcome === 'error') {
          l.failed++
          if (endsTheRun(errorKind)) l.terminal = true
        }
        queuePriorArt(l, landed)
        l.moves.push({
          at,
          kind: 'conjecture',
          nodeId: target.id,
          label: target.label,
          why: conjecturePick.why,
          opId,
          outcome,
          produced: landed.length,
          ...(note ? { note } : {}),
        })
        useStore.getState().patchRun(l.id, {
          moves: l.moves.slice(),
          produced: l.produced,
          dead: l.dead,
          open: countOpen(useStore.getState().session, l.st),
        })
        project(l, null, 'moving', null)
        continue
      }

      // A programme's reasoning step, an agenda's, or the policy's pick. A step
      // reaching here is a run verb by elimination — the three check kinds
      // returned above.
      const move: Move | null = step
        ? isRunVerb(step.step.verb)
          ? {
              nodeId: step.step.nodeId,
              label: s.nodes[step.step.nodeId]!.label,
              verb: step.step.verb,
              why: 'programme',
            }
          : null
        : agendaPick && agendaPick.step.action !== 'verify'
          ? {
              nodeId: agendaPick.step.nodeId,
              label: s.nodes[agendaPick.step.nodeId]!.label,
              verb: agendaPick.step.action,
              why: 'agenda' as const,
            }
          : chooseMove(s, l.st)
      if (!move) {
        stop = l.programme ? 'programme' : 'frontier'
        break
      }

      // The confirm gate, on the POLICY'S pick and before the packet plan:
      // what a go approves is a pass on this node, and how many compatible
      // angles share it stays the executor's arithmetic — a packet planned
      // before a pause of minutes would be sized against a graph that no
      // longer exists.
      //
      // CAPTURED before the gate, not re-read after it, because the standing
      // go flips `l.mode` to auto while the gate is still open — and the
      // question below is not "which mode is the run in now" but "did a pause
      // just happen": answered with "On its own from here", this iteration
      // held for as long as any other.
      const gated = l.mode !== 'auto'
      if (gated) {
        const proposed: NonNullable<RunLive['now']> = {
          kind: move.verb,
          nodeId: move.nodeId,
          label: move.label,
          why: move.why,
        }
        if ((await holdForGo(l, proposed)) !== 'run') continue
        // The grounding gates' re-checks, on the reasoning route, the
        // re-floored reserve, and the policy's own pick — node AND verb,
        // because a manual pass during the pause can spend this node's next
        // rung and the approved angle with it.
        if (resolveFor(useStore.getState(), 'reason') === null) {
          l.terminal = true
          continue
        }
        refloorReserve(l)
        if (shouldStop(l) !== null) continue
        const again = agendaPick
          ? agendaPickFor(l)?.step.id === agendaPick.step.id
            ? move
            : null
          : chooseMove(useStore.getState().session, l.st)
        if (!again || again.nodeId !== move.nodeId || again.verb !== move.verb) continue
        usdAt = drawnUsd(l)
      }

      // Re-read after a gate, for the plan. When none opened this is the
      // snapshot taken at the top of the iteration, unchanged. The capacity is
      // what the run may still add, off its own count and not the graph's.
      const sNow = gated ? useStore.getState().session : s
      const nodeCapacity = nodeHeadroom(l.produced, l.budget)
      const packet =
        l.execution === 'portfolio' && !l.portfolioDisabled
          ? planPortfolio(sNow, l.st, move, {
              logicalSlots: portfolioLogicalSlots(l),
              nodeCapacity,
            })
          : null

      if (packet) {
        project(
          l,
          {
            kind: packet.verbs[0]!,
            nodeId: move.nodeId,
            label: move.label,
            why: move.why,
            packet: packet.verbs,
          },
          'moving',
          null,
        )

        // Every logical angle is spent before the shared request starts. A
        // refusal or typed failure advances the ladder exactly as the sequential
        // path does; silently retrying missing angles would be new spend.
        const tried = l.st.attempted.get(move.nodeId) ?? new Set<DeriveOp>()
        for (const verb of packet.verbs) tried.add(verb)
        l.st.attempted.set(move.nodeId, tried)

        const result = await runPortfolio(move.nodeId, packet.verbs, {
          mark: false,
          quiet: true,
          compactChildren: l.detail === 'progressive',
          maxNodes: nodeCapacity,
          onStart: () => {
            l.sessionId ??= useStore.getState().session.id
          },
        })
        if (result.opId === null) {
          unstarted(l)
          continue
        }
        l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)

        // Physical counters advance once for the one provider request, never once
        // per logical angle row — the stall streak included. See `portfolioPassTally`.
        const tally = portfolioPassTally(result)
        if (result.refused) l.refused++
        if (tally.failed) {
          failedMove(l, result.errorKind)
        }
        if (tally.stalled) l.stalls++
        else if (tally.landed) l.stalls = 0
        if (result.incomplete) l.portfolioDisabled = true

        for (let i = 0; i < result.parts.length; i++) {
          const part = result.parts[i]!
          for (const id of part.produced) l.st.produced.add(id)
          l.produced += part.produced.length
          const nodes = useStore.getState().session.nodes
          tallyEndings(l, nodes, part.produced)

          // A logical count, for the account's "N moves returned nothing new";
          // the streak that stops a run was settled above, once for the pass.
          if (part.outcome === 'nothing') l.nothing++

          if (spendsAllowance(part.outcome, part.produced.length)) {
            l.st.moves.set(move.nodeId, (l.st.moves.get(move.nodeId) ?? 0) + 1)
            const branch = branchKeyOf(move.nodeId)
            if (branch) {
              const n = (l.st.branch.get(branch) ?? 0) + 1
              l.st.branch.set(branch, n)
              l.capNote =
                n >= branchCap(l.st)
                  ? l.st.effectiveMoves !== undefined
                    ? `“${labelOf(branch)}” has taken ${n} of the ${l.st.effectiveMoves} moves your ceiling covers. The rest go elsewhere.`
                    : `“${labelOf(branch)}” has taken ${n} of ${l.budget.moves} moves. The rest go elsewhere.`
                  : l.capNote
            }
          }

          l.moves.push({
            at: l.moves.length + 1,
            kind: part.kind,
            nodeId: move.nodeId,
            label: move.label,
            why: i === 0 ? move.why : 'in-progress',
            opId: result.opId,
            outcome: part.outcome,
            produced: part.produced.length,
            ...(part.note ? { note: part.note } : {}),
          })
        }

        useStore.getState().patchRun(l.id, {
          moves: l.moves.slice(),
          produced: l.produced,
          dead: l.dead,
          endings: { ...l.endings },
          open: countOpen(useStore.getState().session, l.st),
        })
        project(l, null, 'moving', null)
        continue
      }

      const at = l.moves.length + 1
      if (agendaPick && resolveFor(useStore.getState(), 'reason') === null) {
        l.terminal = true
        continue
      }
      const intent = agendaPick ? beginQueuedMove(l, agendaPick) : null
      project(
        l,
        { kind: move.verb, nodeId: move.nodeId, label: move.label, why: move.why },
        'moving',
        null,
      )

      // Spent before the pass, not after: a pass that comes back empty was still
      // paid for, and a verb retried because it produced nothing would loop.
      const tried = l.st.attempted.get(move.nodeId) ?? new Set<DeriveOp>()
      tried.add(move.verb)
      l.st.attempted.set(move.nodeId, tried)

      let opId: string | null = null
      let detailIncomplete = false
      let produced: string[] = []
      // A run steered onto `conjecture` (`/only`, `/ladder`) dispatches it through
      // the conjecture runner: its schema, admission and registration are its own,
      // and `runOp` would land its answer as ordinary claims. Never an agenda
      // action — `AGENDA_ACTIONS` has no conjecture.
      const conjecturing = move.verb === 'conjecture'
      try {
        produced = conjecturing
          ? await runConjecture(move.nodeId, {
              mark: false,
              quiet: true,
              onStart: (id) => {
                opId = id
                l.sessionId ??= useStore.getState().session.id
              },
            })
          : await runOp(move.verb, move.nodeId, undefined, {
              mark: false,
              quiet: true,
              ...(intent ? { maxRetries: 0, investigationPurpose: agendaPick!.step.purpose } : {}),
              targetDetail: l.detail === 'progressive',
              onStart: (id) => {
                opId = id
                l.sessionId ??= useStore.getState().session.id
                patchQueuedMove(l, intent, { opId: id })
              },
              onIncomplete: () => {
                detailIncomplete = true
              },
            })
      } finally {
        settleQueuedMove(l, intent, opId)
      }
      if (opId === null) {
        unstarted(l)
        consumeStep(l)
        continue
      }
      l.reserveUsd = Math.max(l.reserveUsd, drawnUsd(l) - usdAt)

      for (const id of produced) l.st.produced.add(id)
      l.produced += produced.length
      const nodes = useStore.getState().session.nodes
      tallyEndings(l, nodes, produced)

      const settled = outcomeOf(opId, produced.length)
      const outcome = detailIncomplete ? ('incomplete' as const) : settled.outcome
      const note = detailIncomplete
        ? 'The compact target detail did not arrive, so its provisional children were not kept.'
        : settled.note
      const errorKind = settled.errorKind
      if (detailIncomplete) l.portfolioDisabled = true
      if (outcome === 'refused') {
        l.refused++
        // The verb stays SPENT. Reverting it looks like the generous reading — the
        // model declining is a fact about the model, not about the branch — but
        // the policy is deterministic, so the next selection re-issues the
        // identical node and verb, is refused again, and three verbatim retries
        // end the whole run as `declined`. Leaving it spent advances the ladder to
        // the next angle on the same node, which keeps the branch alive in the way
        // that actually helps. A refusal still never counts toward the stall
        // streak: that count is about ends with nothing left in them.
      } else if (outcome === 'error') {
        failedMove(l, errorKind)
      } else if (conjecturing) {
        // Neither stalls nor un-stalls, steered or scheduled: an empty conjecture
        // pass is an answer, not an exhausted branch (D281).
      } else if (outcome === 'nothing') {
        l.nothing++
        l.stalls++
      } else if (outcome === 'nodes') {
        l.stalls = 0
      }

      // Whatever the pass's ending, a conjecture it put on the graph is owed its
      // search — the scheduled gate's rule.
      if (conjecturing) queuePriorArt(l, produced)

      // `incomplete` on THIS path means the compact target's detail never arrived,
      // and `runOp` answers that by dropping the subtree and emptying `produced` —
      // so the third case in `spendsAllowance` is unreachable here, and the rule
      // reduces to the two clean outcomes it always was.
      if (spendsAllowance(outcome, produced.length)) {
        l.st.moves.set(move.nodeId, (l.st.moves.get(move.nodeId) ?? 0) + 1)
        const branch = branchKeyOf(move.nodeId)
        if (branch) {
          const n = (l.st.branch.get(branch) ?? 0) + 1
          l.st.branch.set(branch, n)
          // Counted against the moves this run will actually make, which under a
          // ceiling is not the number the button asked for. "4 of 24" beside a cap
          // that fired at four reads as a bug rather than as a bound.
          l.capNote =
            !l.agendaIds && n >= branchCap(l.st)
              ? l.st.effectiveMoves !== undefined
                ? `“${labelOf(branch)}” has taken ${n} of the ${l.st.effectiveMoves} moves your ceiling covers. The rest go elsewhere.`
                : `“${labelOf(branch)}” has taken ${n} of ${l.budget.moves} moves. The rest go elsewhere.`
              : l.capNote
        }
      }

      l.moves.push({
        at,
        kind: move.verb,
        nodeId: move.nodeId,
        label: move.label,
        why: move.why,
        ...(intent ? { agenda: intent } : {}),
        opId,
        outcome,
        produced: produced.length,
        ...(note ? { note } : {}),
      })
      if (l.programme) l.step++
      useStore.getState().patchRun(l.id, {
        moves: l.moves.slice(),
        produced: l.produced,
        dead: l.dead,
        endings: { ...l.endings },
        // Kept current on every move, not only at the end: a tab that dies mid-run
        // would otherwise leave the account printing the figure from before the
        // run started as the figure the run left behind.
        open: countOpen(useStore.getState().session, l.st),
      })
      project(l, null, 'moving', null)
    }

    // One synthesis scan, at the end, quiet — no toast and no inspector steal. A run
    // whose final act was to force open a descending ranking of its own candidates
    // would have composed a discovery claim out of a list captioned "a hint, not a
    // verdict". The pod states the count and leaves opening it to the person.
    // The check slots the loop passed with checks off, counted where the loop
    // ended and before the scan takes its slot — see `checksSkippedBetween`.
    const skipped =
      l.checksOffAt === null
        ? 0
        : checksSkippedBetween(l.checksOffAt, l.moves.length, l.checksRun, l.budget)
    // And for the other reason checks go off, counted the same way (D293).
    const unread = l.checksUnread
    const unreadSkipped =
      unread === null ? 0 : checksSkippedBetween(unread.at, l.moves.length, l.checksRun, l.budget)
    let scanned: number | undefined
    // Entered on the ROUTE-INDEPENDENT predicate, because the branch below that
    // ends an auto run as `unreachable` lives inside this block — and a missing
    // route is exactly when `scanWillRun` is false. Gating the door on it meant
    // that branch could not run in the one case it was written for: a scan key
    // removed mid-run closed the run silently, which is what it was meant to stop.
    if (stop !== 'protocol' && stop !== 'clock' && scanOtherwiseDue(l)) {
      await waitForIdle(l)
      const scanNow: NonNullable<RunLive['now']> = {
        kind: 'bridge',
        nodeId: '',
        label: 'the whole graph',
        why: 'whole-graph',
      }
      // Confirm mode gates the scan like every other pass — it is billed like
      // one, and a mode whose promise is "none taken alone" cannot carve out
      // the last one. Both modes then re-ask `scanWillRun` on the far side of
      // their waits — the gate's, and `waitForIdle`'s alone in auto — because
      // a wait can outlive the money and the key the scan was affordable
      // under, and the graph itself: the predicate now carries the session
      // clause, so a run that stopped as `replaced` never scans, or bills,
      // whichever exploration was just opened. The confirm arm asks once more
      // on the NEAR side of its gate, and that is not redundancy: a gate
      // opened on the far graph would project a question about the old run
      // onto a document it never worked.
      let go = l.mode === 'auto' && scanWillRun(l)
      // The same loss, in the mode that has no gate to notice it.
      //
      // `scanWillRun` folds the scan's route into one boolean, so a key removed
      // mid-run turned the closing scan off and the run ended under whatever
      // stop it was heading for — no scan, and no word about it. The confirm arm
      // below has said `unreachable` for exactly this since it was written; auto
      // said nothing, which is the silence the pre-flight refusal exists to
      // prevent, arriving late.
      //
      // Asked only where the scan was OTHERWISE going to happen: everything in
      // `scanWillRun` except the route, so a run that could not reach a scan
      // anyway is not ended over a key it never needed. This is why `scan` stays
      // out of `liveRolesFor` — the question is not "can this run still call the
      // scan model" but "was it about to", and only here is that known.
      if (l.mode === 'auto' && !go && resolveFor(useStore.getState(), 'scan') === null) {
        stop = 'unreachable'
      }
      if (l.mode !== 'auto' && !l.stopping && scanWillRun(l)) {
        if ((await holdForGo(l, scanNow)) === 'run') {
          if (resolveFor(useStore.getState(), 'scan') === null) {
            // The scan's route, re-resolved like every other gated pass's:
            // removed while the gate held, it would refuse silently inside
            // `scanForBridges`, and a run whose person just pressed Go would
            // close scan-less with nothing said. The loss takes the same
            // ending word the other roles' loss takes — the mid-run form of
            // the pre-flight refusal.
            stop = 'unreachable'
          } else {
            refloorReserve(l)
            go = scanWillRun(l)
          }
        }
      }
      if (go && !l.stopping) {
        const at = l.moves.length + 1
        project(l, scanNow, 'moving', null)
        let opId: string | null = null
        const found = await scanForBridges({
          scope: 'within',
          quiet: true,
          onStart: (id) => {
            opId = id
          },
        })
        if (opId) {
          // Through `outcomeOf` like every other move. Reading `found === 0` as
          // "proposed nothing" would report a scan that errored or was refused as
          // a scan that looked and came back empty — opposite findings, and only
          // one of them is about the graph.
          const { outcome, note } = outcomeOf(opId, found)
          if (outcome === 'nodes' || outcome === 'nothing') scanned = found
          l.moves.push({
            at,
            kind: 'bridge',
            nodeId: '',
            label: 'the whole graph',
            why: 'whole-graph',
            opId,
            outcome,
            produced: found,
            ...(note ? { note } : {}),
          })
        }
      }
    }

    // The user's hand, arriving after the loop's own terminus. Stop pressed
    // at the closing scan — or held for as long as its confirm gate holds —
    // lands after `shouldStop`'s last reading, so the record would keep the
    // budget's word over the person's. The reason is not cosmetic:
    // `guideAutoStarts` reads it, and an explicit stand-down is the one
    // ending the coach must not answer by taking the camera. A loop that
    // broke as 'stopped' arrives here already saying so.
    if (l.stopping) stop = 'stopped'

    const finishedAt = Date.now()
    const closing = {
      finishedAt,
      stop,
      moves: l.moves.slice(),
      produced: l.produced,
      dead: l.dead,
      endings: { ...l.endings },
      checkReadings: l.checkReadings.slice(),
      ...(judgeAccount(l) ? { judge: judgeAccount(l) } : {}),
      // Present only when an audit actually ran. An empty array would say "one ran
      // and examined nothing", which is a different fact and gets a different
      // sentence — the same present-or-absent rule the codec reads it back under.
      ...(l.auditsRun > 0 ? { auditVerdicts: l.auditVerdicts.slice() } : {}),
      declined: l.declined,
      open: countOpen(useStore.getState().session, l.st),
      ...(scanned !== undefined ? { scanned } : {}),
      // Present only when a slot was actually passed: checks that went off after
      // the schedule's last one skipped nothing, and a line saying so would not be
      // true. The figure is the reserve the check was held to when it went off.
      ...(skipped > 0 ? { checksSkipped: { count: skipped, usd: l.checkReserveUsd } } : {}),
      // Under the same rule, naming the model the route had when they went off —
      // which model had no search, not whichever the route names by the time the
      // account is read.
      ...(unread && unreadSkipped > 0
        ? { checksUnread: { count: unreadSkipped, model: unread.model } }
        : {}),
      // Written once, here, and never recomputed: every op after this run adds to
      // the same ledger, so an account that subtracted `spendBefore` when it was
      // read would show a run's cost growing every time the user pressed Deepen.
      // Omitted rather than zeroed when the model has no published rate.
      ...(l.priced ? { estimatedUsd: drawnUsd(l) } : {}),
    }
    // `patchRun` writes into the LIVE session. On a `replaced` ending the record
    // lives in the document the user just navigated away from, so the patch would
    // find no run with this id and silently do nothing — leaving a stored record
    // with no `finishedAt` and no `stop`, which every surface then reports as "the
    // page closed while it was going". Untrue, and the one ending that most needs
    // to be legible.
    const replacedSessionId =
      l.sessionId !== null && useStore.getState().session.id !== l.sessionId ? l.sessionId : null
    const replaced = replacedSessionId !== null
    if (replacedSessionId) {
      closeStoredRun(replacedSessionId, l.id, closing)
      // The live projection belongs to the document the user left. Putting its
      // ended card onto the newly opened graph makes its press ("See the account",
      // or "What it found") land on a tab that does not contain it — and a coach
      // would be worse, because every anchor id belongs to the old graph.
      useStore.getState().setRun(null)
    } else {
      useStore.getState().patchRun(l.id, closing)
      if (l.st.produced.size > 0 && guideAutoStarts(stop)) {
        useStore.getState().beginCoach(l.id)
      } else {
        // An ending that does not open the coach still IS the newest ending. A
        // held coach keeps its retirement but must not keep naming the run
        // before last — the bar would print the wrong stop head over this one.
        useStore.getState().retargetCoach(l.id)
      }
      project(l, null, 'ended', stop)
    }

    // The one event the retention gate actually counts — see `docs/metrics.md`.
    //
    // Fired on the run reaching a TERMINUS, not on a node arriving: a completed
    // exploration is the unit the gate counts and a node is not one. Here rather
    // than earlier in the closing sequence so it cannot fire for a run whose
    // record failed to write.
    //
    // `stop` is already a closed union, so it says *why* people stop without
    // carrying anything about what was explored. A no-op in every clone.
    track('exploration_completed', { stop })

    // A run that threaded nothing changed nothing, and a step back that announces
    // it undid something and does not is worse than no step at all — the same rule
    // every verb applies through `dropMark`. The label matches the one taken at the
    // start, which is what the last-entry check needs.
    if (l.produced === 0) useStore.getState().dropMark('Autonomous run')

    // The one toast a run raises, after the record is written. Everything during
    // the run is silent: the queue keeps three for four seconds, so per-pass
    // toasts are noise to whoever is watching and invisible to whoever left.
    //
    // The open-ends figure is the TOTAL, matching the account and the export. This
    // sentence is the only one a person who left the room ever sees, so it is the
    // last place that may quietly report a smaller number than the record does.
    const open = closing.open
    const left = open.reachable + open.pastDepth + open.folded
    const passes = modelPassesFor(l.moves)
    // The spend clause only when a ceiling was set. Someone who set one is owed
    // the figure in the sentence they will read from the other room; someone who
    // did not would be handed a dollar amount they never asked to think about, and
    // the ledger and the Run tab both have it either way.
    const money =
      (l.usdMax !== null
        ? ` About ${fmtUsd(drawnUsd(l))} of your ${fmtUsd(l.usdMax)} ceiling, estimated.`
        : '') +
      (closing.checksSkipped ? ` ${checksSkippedLine(closing.checksSkipped)}` : '') +
      (closing.checksUnread ? ` ${checksUnreadLine(closing.checksUnread)}` : '')
    const checked = l.moves.filter((m) => m.kind === 'verify').length
    if (replaced) {
      useStore
        .getState()
        .toast(
          'The run stopped when you opened another exploration. Its account stayed with that exploration.',
          'info',
        )
    } else if (l.checkTargets) {
      // A checks-only run grew nothing, so nodes and open ends are not its news:
      // how many of the named claims met a source, and what each reading was.
      const readings = l.checkReadings.map((r) => READING_LABEL[r].toLowerCase()).join(', ')
      useStore
        .getState()
        .toast(
          `Checks finished — ${checked} of ${l.checkTargets.length} named claim${l.checkTargets.length === 1 ? '' : 's'} sent to a source check${readings ? `: ${readings}` : ''}.${money} The account is in the Run tab.`,
          stop === 'unreachable' || stop === 'declined' ? 'warn' : 'info',
        )
    } else {
      // A run the model stopped answering says so, and why, in its last failed
      // pass's words — "Run finished" over four rate-limited passes read as a run
      // that had done its work.
      const why = unreachableWhy({ stop, moves: l.moves })
      useStore
        .getState()
        .toast(
          `${why ? `Run stopped — ${why}` : 'Run finished —'} ${l.moves.length} logical move${l.moves.length === 1 ? '' : 's'} across ${passes} model pass${passes === 1 ? '' : 'es'}, ${l.produced} node${l.produced === 1 ? '' : 's'}, ${left} open end${left === 1 ? '' : 's'} left.${money} The account is in the Run tab.`,
          stop === 'unreachable' || stop === 'declined' ? 'warn' : 'info',
        )
    }
  } finally {
    loop = null
  }
}

/** The first-ring ancestor of a node, for the branch share. Recomputed here rather
 *  than threaded out of the policy, because the policy's index is per-selection. */
function branchKeyOf(id: string): string | null {
  const s = useStore.getState().session
  let cur = s.nodes[id]
  if (!cur) return null
  while (cur.parentId && s.nodes[cur.parentId]?.parentId !== null) {
    const next = s.nodes[cur.parentId]
    if (!next) break
    cur = next
  }
  return cur.parentId === null ? null : cur.id
}

const labelOf = (id: string): string =>
  useStore.getState().session.nodes[id]?.label ?? 'That branch'

/**
 * Close out a run's record in an exploration that is no longer open.
 *
 * Reached only when the user opened another exploration mid-run. The outgoing
 * document was already written by the autosave's session-change checkpoint, so
 * this reads it back, patches the one record, and writes it again — the only way
 * an ending can reach a record the store can no longer see.
 *
 * PATCHING A STORED DOCUMENT IS A SAVE, so it is stamped like one. This kept the
 * `savedAt` it read back, which was defensible as "nothing the user did changed"
 * and wrong in the one place it counts: `savedAt` is also the shelf's VERSION,
 * so the pre-patch bytes and the post-patch bytes reached `putIfNewer` claiming
 * the same save — and a write still in flight from before this one could commit
 * last and leave the shelf holding a run with no ending in it. The stamp is the
 * honest reading in every direction: the index row and the sidebar's sense of
 * recency both treat a document that was just written as one that was just
 * written.
 */
function closeStoredRun(sessionId: string, runId: string, patch: Partial<Run>): void {
  const doc = loadSession(sessionId)
  if (!doc) return
  const runs = doc.session.runs.map((r) => (r.id === runId ? { ...r, ...patch } : r))
  const agendaIds = runs.find((r) => r.id === runId)?.agenda?.stepIds
  const research = doc.session.research
  // A pass whose document was replaced cannot be settled from the new live
  // graph. Keep its exact intent and flag the missing conclusion, never replay it.
  const agenda =
    research?.agenda && agendaIds
      ? {
          ...research.agenda,
          steps: research.agenda.steps.map((step) =>
            agendaIds.includes(step.id) && step.status === 'running'
              ? {
                  ...step,
                  status: 'unknown' as const,
                  attempts: step.attempts.map((attempt) =>
                    attempt.status === 'running'
                      ? { ...attempt, status: 'unknown' as const, finishedAt: Date.now() }
                      : attempt,
                  ),
                }
              : step,
          ),
        }
      : undefined
  const closed: StoredSession = {
    ...doc,
    savedAt: Math.max(Date.now(), doc.savedAt + 1),
    session: {
      ...doc.session,
      runs,
      ...(research && agenda ? { research: { ...research, agenda } } : {}),
    },
  }
  writeDoc(closed)
}

/** Re-exported for the surfaces that print a stop reason, so none of them builds
 *  its own sentence. */
export { RUN_STOP_HEAD }
